import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { euDate } from "@/lib/utils";
import {
  LayoutDashboard, CalendarDays, Users, MessageSquare, Image as ImageIcon,
  HeartHandshake, LogOut, Plus, Trash2, Pencil, X, LayoutGrid, ArrowUp, ArrowDown,
  UserRound, BarChart3, Sparkles, CalendarClock, ShoppingBag,
  Check, Ban, ListChecks, Clock, Download, KeyRound, Languages, GraduationCap,
  Instagram, Eye, Globe, Monitor, TrendingUp, CalendarPlus, CreditCard,
  Mail, Copy, ChevronDown, Baby, UtensilsCrossed, Compass, FileText, Camera, MapPin, Lock, Upload, Archive, MessageCircle, Link2, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { StarOfDavid } from "@/components/site/StarOfDavid";
import { ImageUploadField } from "@/components/site/ImageUploadField";
import { RichTextEditor } from "@/components/site/RichTextEditor";
import { resolveImg } from "@/lib/api";
import { STRINGS } from "@/lib/i18n";
import { RESOURCE_ICONS, autoResourceIcon, resourceIconComp } from "@/lib/resourceIcons";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "programs", label: "Programs", icon: LayoutGrid },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "calendar", label: "Calendar", icon: CalendarPlus },
  { id: "holidays", label: "Holidays", icon: Sparkles },
  { id: "staff", label: "Staff", icon: UserRound },
  { id: "numbers", label: "Numbers", icon: BarChart3 },
  { id: "weekly", label: "Weekly", icon: CalendarClock },
  { id: "kosher", label: "Kosher Store", icon: ShoppingBag },
  { id: "sitetext", label: "Site Text", icon: Languages },
  { id: "rsvps", label: "RSVPs", icon: Users },
  { id: "archive", label: "Past events", icon: Archive },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "students", label: "Students Club", icon: GraduationCap },
  { id: "children", label: "Children's Program", icon: Baby },
  { id: "servicepages", label: "Service Pages", icon: UtensilsCrossed },
  { id: "pagecontent", label: "Page Content", icon: FileText },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "donations", label: "Donations", icon: HeartHandshake },
  { id: "payments", label: "Payments", icon: CreditCard },
];

const emptyEvent = { title: "", slug: "", description: "", date: "", time: "", rsvp_close_at: "", location: "", location_id: "", hide_location: false, rsvp_via_whatsapp: false, no_reservation: false, students_only: false, unlisted: false, auto_approve: false, sold_out: false, students_contact_number: "", use_child_signup: false, child_signup_program: "sunday", rsvp_show_email: true, rsvp_email_required: true, rsvp_show_phone: true, rsvp_phone_required: true, rsvp_show_adults: true, rsvp_show_children: true, whatsapp_number: "", wa_button_enabled: false, wa_button_text: "", wa_button_number: "", wa_group_in_email: true, category: "", categories: [], holiday: "", image_url: "", flyer_url: "", flyer_i18n: {}, capacity: 0, payment_mode: "none", price_per_person: 0, price_adult: 0, price_child: 0, price_family: 0, suggested_amounts: [], hide_suggested_note: false, custom_questions: [], parts: [], email_i18n: {}, i18n: { he: {} } };
const emptyCategory = { title: "", description: "", body: "", image_url: "", event_image: "", flyers: [], span: "col-span-12 md:col-span-4", parent_id: "", link: "", active: true, is_service: false, price_note: "", order_whatsapp: "", whatsapp_number: "", book_donate_number: "", hide_events: false, hidden: false, i18n: { he: {} } };
const emptyStaff = { name: "", role: "", since: "", bio: "", photo_url: "", i18n: { he: {} } };
const emptyStat = { label: "", value: "", suffix: "", i18n: { he: {} } };
const emptyHoliday = { name: "", subtitle: "", date: "", end_date: "", description: "", image_url: "", rabbi_name: "", rabbi_message: "", rabbi_photo_url: "", resources: [], i18n: { he: {} } };
const RESOURCE_TYPES = [["link", "Link (URL)"], ["flyer", "Flyer pop-up (upload)"], ["text", "Text pop-up"], ["page", "Site page"]];
const RESOURCE_PAGES = [["/", "Home"], ["/about", "About"], ["/events", "Events"], ["/classes", "Classes & Prayer times"], ["/kosher", "Kosher store"], ["/kosher-establishments", "Kosher establishments"], ["/services", "Services"], ["/tourist-desk", "Tourist desk"], ["#contact", "Contact section"], ["#donate", "Donate section"]];
const emptyNote = { date: "", title: "", description: "", i18n: { he: {} } };

const TR_LANGS = [
  { code: "cs", label: "Čeština · Czech", rtl: false },
  { code: "he", label: "עברית · Hebrew", rtl: true },
  { code: "ru", label: "Русский · Russian", rtl: false },
];

function TranslationBox({ form, setForm, fields }) {
  const [loading, setLoading] = useState(null);
  const suggest = async (code) => {
    const src = {};
    fields.forEach((f) => { if (form[f.field]) src[f.field] = form[f.field]; });
    if (Object.keys(src).length === 0) { toast.error("Add the English text first"); return; }
    setLoading(code);
    try {
      const { data } = await api.post("/translate", { fields: src, targets: [code] });
      const cur = form.i18n?.[code] || {};
      setForm({ ...form, i18n: { ...(form.i18n || {}), [code]: { ...cur, ...(data[code] || {}) } } });
      toast.success("Translation suggested — review & save");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not translate"); }
    finally { setLoading(null); }
  };
  return (
    <div className="space-y-3">
      {TR_LANGS.map((L) => {
        const cur = form.i18n?.[L.code] || {};
        const set = (field, v) => setForm({ ...form, i18n: { ...(form.i18n || {}), [L.code]: { ...(form.i18n?.[L.code] || {}), [field]: v } } });
        return (
          <div key={L.code} data-testid={`trans-box-${L.code}`} dir={L.rtl ? "rtl" : "ltr"}
            className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-3">
            <div className="flex items-center justify-between" dir="ltr">
              <p className="text-sm font-serif font-semibold text-[#9E7829]">{L.label}</p>
              <button type="button" data-testid={`suggest-${L.code}`} onClick={() => suggest(L.code)} disabled={loading === L.code}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#BE923C] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#9E7829] disabled:opacity-60 transition-colors">
                <Sparkles size={13} /> {loading === L.code ? "Translating…" : "Suggest"}
              </button>
            </div>
            {fields.map((f) => {
              if (f.rich) {
                return (
                  <div key={f.field}>
                    <Label className="text-[#475569]">{f.label}</Label>
                    <div className="mt-1.5"><RichTextEditor testid={`${L.code}-field-${f.field}`} value={cur[f.field] || ""} onChange={(html) => set(f.field, html)} /></div>
                  </div>
                );
              }
              const C = f.textarea ? Textarea : Input;
              return (
                <div key={f.field}>
                  <Label className="text-[#475569]">{f.label}</Label>
                  <C data-testid={`${L.code}-field-${f.field}`} rows={f.textarea ? (f.rows || 2) : undefined}
                    value={cur[f.field] || ""} onChange={(e) => set(f.field, e.target.value)}
                    className={`mt-1.5 border-[#0F172A]/10 resize-none ${L.rtl ? "text-right" : ""}`} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

const PART_LANGS = [["cs", "CS"], ["he", "HE"], ["ru", "RU"]];
function EventPartsEditor({ parts, onChange }) {
  const update = (i, patch) => onChange(parts.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const updateI18n = (i, lg, label) => update(i, { i18n: { ...(parts[i].i18n || {}), [lg]: { label } } });
  const add = () => onChange([...parts, { id: Math.random().toString(36).slice(2, 10), label: "", date: "", time: "", price_adult: 0, price_child: 0, i18n: {} }]);
  const move = (i, d) => { const n = [...parts]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="rounded-2xl bg-white border border-[#0F172A]/10 p-4 space-y-3" data-testid="event-parts-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Event options (parts)</p>
          <p className="text-xs text-[#475569] mt-0.5">E.g. “Friday dinner” and “Shabbat lunch” on one RSVP page. Guests tick all they’ll join. Prices are optional — if set, the total is the sum of the chosen parts × guests.</p>
        </div>
        <button type="button" onClick={add} data-testid="event-part-add" className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#BE923C]/40 text-[#9E7829] px-3 py-1.5 text-xs font-semibold hover:bg-[#FBF3E2]"><Plus size={13} /> Add part</button>
      </div>
      {parts.map((p, i) => (
        <div key={p.id} className="rounded-xl border border-[#0F172A]/10 bg-[#FBFAF8] p-3 space-y-2" data-testid={`event-part-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={p.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Name (English) — e.g. Friday dinner" data-testid={`event-part-label-${i}`} className="flex-1 border-[#0F172A]/10" />
            <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-[#E8E6E1] text-[#475569]" title="Move up"><ArrowUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-[#E8E6E1] text-[#475569]" title="Move down"><ArrowDown size={14} /></button>
            <button type="button" onClick={() => onChange(parts.filter((_, idx) => idx !== i))} data-testid={`event-part-remove-${i}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div><Label className="text-[11px] text-[#475569]">Date (optional)</Label><Input type="date" value={p.date || ""} onChange={(e) => update(i, { date: e.target.value })} data-testid={`event-part-date-${i}`} className="mt-1 border-[#0F172A]/10" /></div>
            <div><Label className="text-[11px] text-[#475569]">Time (optional)</Label><Input type="time" value={p.time || ""} onChange={(e) => update(i, { time: e.target.value })} data-testid={`event-part-time-${i}`} className="mt-1 border-[#0F172A]/10" /></div>
            <div><Label className="text-[11px] text-[#475569]">Adult price (CZK)</Label><Input type="number" min={0} value={p.price_adult ?? 0} onChange={(e) => update(i, { price_adult: Number(e.target.value) || 0 })} data-testid={`event-part-price-adult-${i}`} className="mt-1 border-[#0F172A]/10" /></div>
            <div><Label className="text-[11px] text-[#475569]">Child price (CZK)</Label><Input type="number" min={0} value={p.price_child ?? 0} onChange={(e) => update(i, { price_child: Number(e.target.value) || 0 })} data-testid={`event-part-price-child-${i}`} className="mt-1 border-[#0F172A]/10" /></div>
            <div><Label className="text-[11px] text-[#475569]">Max guests (blank = unlimited)</Label><Input type="number" min={0} value={p.capacity || ""} placeholder="—" onChange={(e) => update(i, { capacity: Number(e.target.value) || 0 })} data-testid={`event-part-capacity-${i}`} className="mt-1 border-[#0F172A]/10" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {PART_LANGS.map(([lg, lbl]) => (
              <Input key={lg} value={(p.i18n?.[lg]?.label) || ""} onChange={(e) => updateI18n(i, lg, e.target.value)} placeholder={`${lbl} name`} dir={lg === "he" ? "rtl" : "ltr"} data-testid={`event-part-label-${lg}-${i}`} className="border-[#0F172A]/10 text-sm" />
            ))}
          </div>
        </div>
      ))}
      {parts.length === 0 && <p className="text-xs text-[#94a3b8]">No parts — the RSVP is for the whole event.</p>}
    </div>
  );
}

function EventQuestionEditor({ q, onUpdate, onRemove }) {
  const [tLoading, setTLoading] = useState(null);
  const isSelect = q.type === "select";
  const options = q.options || [];

  const setOption = (i, v) => { const next = [...options]; next[i] = v; onUpdate({ options: next }); };
  const addOption = () => onUpdate({ options: [...options, ""] });
  const removeOption = (i) => {
    const next = options.filter((_, idx) => idx !== i);
    const i18n = { ...(q.i18n || {}) };
    TR_LANGS.forEach((L) => {
      if (i18n[L.code]?.options) i18n[L.code] = { ...i18n[L.code], options: i18n[L.code].options.filter((_, idx) => idx !== i) };
    });
    onUpdate({ options: next, i18n });
  };

  const suggest = async (code) => {
    const src = {};
    if (q.label) src.label = q.label;
    if (isSelect) options.forEach((o, i) => { if (o) src[`opt_${i}`] = o; });
    if (Object.keys(src).length === 0) { toast.error("Add the English text first"); return; }
    setTLoading(code);
    try {
      const { data } = await api.post("/translate", { fields: src, targets: [code] });
      const tr = data[code] || {};
      const trOpts = isSelect ? options.map((o, i) => tr[`opt_${i}`] || "") : [];
      onUpdate({ i18n: { ...(q.i18n || {}), [code]: { label: tr.label || "", ...(isSelect ? { options: trOpts } : {}) } } });
      toast.success("Translation suggested — review & save");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not translate"); }
    finally { setTLoading(null); }
  };

  const setTr = (code, field, v) => onUpdate({ i18n: { ...(q.i18n || {}), [code]: { ...((q.i18n || {})[code] || {}), [field]: v } } });
  const setTrOption = (code, i, v) => {
    const cur = (q.i18n || {})[code] || {};
    const arr = [...(cur.options || [])];
    while (arr.length < options.length) arr.push("");
    arr[i] = v;
    setTr(code, "options", arr);
  };

  const showTrans = q.label || (isSelect && options.some(Boolean));

  return (
    <div data-testid={`event-question-${q.id}`} className="rounded-lg border border-[#0F172A]/10 p-3 space-y-2 bg-[#FBFAF8]">
      <div className="flex items-start gap-2">
        <Input data-testid={`event-question-label-${q.id}`} value={q.label} placeholder="e.g. T-shirt size?"
          onChange={(e) => onUpdate({ label: e.target.value })} className="flex-1 border-[#0F172A]/10" />
        <select data-testid={`event-question-type-${q.id}`} value={q.type} onChange={(e) => onUpdate({ type: e.target.value })}
          className="w-32 rounded-md border border-[#0F172A]/10 bg-white px-2 py-2 text-sm">
          <option value="text">Short text</option>
          <option value="select">Dropdown</option>
          <option value="checkbox">Yes / No</option>
        </select>
        <button type="button" onClick={onRemove} data-testid={`remove-event-question-${q.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
      </div>

      {isSelect && (
        <div className="space-y-1.5">
          <Label className="text-xs text-[#475569]">Dropdown options</Label>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input data-testid={`event-question-option-${q.id}-${i}`} value={o} placeholder={`Option ${i + 1}`}
                onChange={(e) => setOption(i, e.target.value)} className="flex-1 border-[#0F172A]/10" />
              <button type="button" onClick={() => removeOption(i)} data-testid={`remove-option-${q.id}-${i}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={addOption} data-testid={`add-option-${q.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#BE923C] hover:text-[#9E7829]">
            <Plus size={14} /> Add option
          </button>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer">
        <input type="checkbox" data-testid={`event-question-required-${q.id}`} checked={q.required} onChange={(e) => onUpdate({ required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
        Required
      </label>

      {showTrans && (
        <div className="space-y-2 pt-1">
          {TR_LANGS.map((L) => {
            const cur = (q.i18n || {})[L.code] || {};
            return (
              <div key={L.code} dir={L.rtl ? "rtl" : "ltr"} className="rounded-lg border border-[#BE923C]/25 bg-[#FBF7EE] p-2.5 space-y-2">
                <div className="flex items-center justify-between" dir="ltr">
                  <span className="text-xs font-semibold text-[#9E7829]">{L.label}</span>
                  <button type="button" onClick={() => suggest(L.code)} disabled={tLoading === L.code} data-testid={`q-suggest-${L.code}-${q.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#BE923C] text-white text-[11px] font-semibold px-2.5 py-1 hover:bg-[#9E7829] disabled:opacity-60">
                    <Sparkles size={11} /> {tLoading === L.code ? "…" : "Suggest"}
                  </button>
                </div>
                <Input data-testid={`q-${L.code}-label-${q.id}`} value={cur.label || ""} placeholder="Translated label"
                  onChange={(e) => setTr(L.code, "label", e.target.value)} className={`border-[#0F172A]/10 text-sm ${L.rtl ? "text-right" : ""}`} />
                {isSelect && options.map((o, i) => (
                  <Input key={i} data-testid={`q-${L.code}-option-${q.id}-${i}`} value={(cur.options || [])[i] || ""} placeholder={o ? `“${o}” →` : `Option ${i + 1}`}
                    onChange={(e) => setTrOption(L.code, i, e.target.value)} className={`border-[#0F172A]/10 text-sm ${L.rtl ? "text-right" : ""}`} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const emptyBlock = { title: "", html: "", position: "top", active: true, i18n: { he: {}, ru: {} } };

const HERO_PAGES = [
  { key: "classes", label: "Classes page", defaults: {
    en: { eyebrow: "Learn With Us", title: "Weekly Classes", desc: "Ongoing study, open to everyone. Join us in person — reach out on WhatsApp and we'll save you a seat." },
    he: { eyebrow: "למדו איתנו", title: "שיעורים שבועיים", desc: "לימוד מתמשך, פתוח לכולם. הצטרפו אלינו פנים אל פנים — פנו אלינו בוואטסאפ ונשמור לכם מקום." },
    ru: { eyebrow: "Учитесь с нами", title: "Еженедельные занятия", desc: "Постоянная учёба, открытая для всех. Присоединяйтесь лично — напишите в WhatsApp, и мы сохраним вам место." },
  } },
  { key: "events", label: "Events page", defaults: {
    en: { eyebrow: "Event Calendar", title: "What's happening", desc: "Join us. There's always a seat at our table." },
    he: { eyebrow: "לוח אירועים", title: "מה מתרחש", desc: "הצטרפו אלינו. תמיד יש מקום סביב השולחן שלנו." },
    ru: { eyebrow: "Календарь событий", title: "Что происходит", desc: "Присоединяйтесь. За нашим столом всегда найдётся место." },
  } },
  { key: "services", label: "Services page", defaults: {
    en: { eyebrow: "Community Services", title: "Here for your everyday needs", desc: "Beyond our programs — the services that support Jewish life in Prague, from kosher food to our synagogue." },
    he: { eyebrow: "שירותים קהילתיים", title: "כאן בשביל הצרכים היומיומיים שלכם", desc: "מעבר לתוכניות שלנו — השירותים שתומכים בחיים היהודיים בפראג, מאוכל כשר ועד בית הכנסת." },
    ru: { eyebrow: "Услуги для общины", title: "Мы рядом для ваших повседневных нужд", desc: "Помимо наших программ — услуги, поддерживающие еврейскую жизнь в Праге, от кошерной еды до синагоги." },
  } },
  { key: "kosher-establishments", label: "Kosher Establishments page", defaults: {
    en: { eyebrow: "Kosher Dining", title: "Kosher Establishments", desc: "" },
    he: { eyebrow: "אוכל כשר", title: "מקומות כשרים", desc: "" },
    ru: { eyebrow: "Кошерная еда", title: "Кошерные заведения", desc: "" },
  } },
  { key: "tourist-desk", label: "Tourist Desk page", defaults: {
    en: { eyebrow: "Tourist Desk", title: "Tourist Desk", desc: "" },
    he: { eyebrow: "דלפק תיירות", title: "דלפק תיירות", desc: "" },
    ru: { eyebrow: "Туристический стол", title: "Туристический стол", desc: "" },
  } },
];

function PageContentAdmin() {
  const [cats, setCats] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [page, setPage] = useState("services");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyBlock);
  const [editId, setEditId] = useState(null);
  const [headers, setHeaders] = useState({ services: "", events: "", classes: "" });
  const [heroTexts, setHeroTexts] = useState({});
  const [heroPage, setHeroPage] = useState("classes");
  const [heroLang, setHeroLang] = useState("en");

  const load = () => api.get("/content-blocks-all").then((r) => setBlocks(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      setHeaders({ services: "", events: "", classes: "", ...(r.data.page_headers || {}) });
      const saved = r.data.page_hero_texts || {};
      const init = {};
      for (const p of HERO_PAGES) {
        const s = saved[p.key] || {};
        init[p.key] = { en: { ...(s.en || {}) }, he: { ...(s.he || {}) }, ru: { ...(s.ru || {}) } };
      }
      setHeroTexts(init);
    }).catch(() => {});
  }, []);

  const FIXED = [
    { key: "tourist-desk", label: "Tourist Desk page" },
    { key: "kosher-establishments", label: "Kosher Establishments page" },
    { key: "services", label: "Services page" },
    { key: "events", label: "Events page" },
    { key: "classes", label: "Classes page" },
  ];
  const programOpts = cats.map((c) => ({ key: `program:${c.slug}`, label: `Program — ${c.title}` }));
  const pageOptions = [...FIXED, ...programOpts];

  const pageBlocks = blocks.filter((b) => b.page === page).sort((a, b) => (a.order || 0) - (b.order || 0));

  const openNew = () => { setForm(emptyBlock); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ title: b.title || "", html: b.html || "", position: b.position || "top", active: b.active !== false, i18n: b.i18n || { he: {}, ru: {} } }); setEditId(b.id); setOpen(true); };

  const setI18nHtml = (lang, v) => setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}), [lang]: { ...((f.i18n || {})[lang] || {}), html: v } } }));
  const setI18nTitle = (lang, v) => setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}), [lang]: { ...((f.i18n || {})[lang] || {}), title: v } } }));

  const save = async (e) => {
    e.preventDefault();
    const strip = (h) => (h || "").replace(/<[^>]+>/g, " ").trim();
    const hasContent = form.title.trim() || strip(form.html) ||
      Object.values(form.i18n || {}).some((v) => (v?.title || "").trim() || strip(v?.html));
    if (!hasContent) { toast.error("Add a heading or some text before saving."); return; }
    const payload = { page, position: form.position, title: form.title, html: form.html, i18n: form.i18n, active: true, order: editId ? undefined : pageBlocks.length };
    try {
      if (editId) await api.put(`/content-blocks/${editId}`, { ...payload, order: pageBlocks.find((b) => b.id === editId)?.order || 0 });
      else await api.post("/content-blocks", payload);
      toast.success(editId ? "Block updated" : "Block added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this text block?")) return;
    await api.delete(`/content-blocks/${id}`);
    toast.success("Block deleted"); load();
  };

  const move = async (index, dir) => {
    const list = [...pageBlocks];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setBlocks((prev) => prev.map((b) => { const i = list.findIndex((x) => x.id === b.id); return i >= 0 ? { ...b, order: i } : b; }));
    await api.put("/content-blocks-reorder", { ids: list.map((b) => b.id) });
    load();
  };

  const saveHeaders = async () => {
    try {
      await api.put("/settings", { values: { page_headers: headers } });
      toast.success("Header images saved");
    } catch { toast.error("Could not save header images"); }
  };

  const saveHeroTexts = async () => {
    const out = {};
    for (const p of HERO_PAGES) {
      const pageData = heroTexts[p.key] || {};
      const clean = {};
      for (const lg of ["en", "cs", "he", "ru"]) {
        const row = {};
        for (const k of ["eyebrow", "title", "desc"]) {
          const v = (pageData[lg]?.[k] || "").trim();
          if (v) row[k] = v;
        }
        if (Object.keys(row).length) clean[lg] = row;
      }
      if (Object.keys(clean).length) out[p.key] = clean;
    }
    try {
      await api.put("/settings", { values: { page_hero_texts: out } });
      toast.success("Hero text saved");
    } catch { toast.error("Could not save hero text"); }
  };
  const setHeroField = (k, v) => setHeroTexts((prev) => ({
    ...prev,
    [heroPage]: { ...(prev[heroPage] || {}), [heroLang]: { ...((prev[heroPage] || {})[heroLang] || {}), [k]: v } },
  }));
  const heroDefaults = (HERO_PAGES.find((p) => p.key === heroPage) || HERO_PAGES[0]).defaults;
  const heroVal = (k) => (heroTexts[heroPage]?.[heroLang]?.[k]) || "";

  const RichLangBlocks = (
    <div className="mt-3 space-y-3">
      <div dir="ltr" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
        <p className="text-sm font-serif font-semibold text-[#9E7829]">Čeština · Czech</p>
        <div>
          <Label className="text-[#475569]">Heading (optional)</Label>
          <Input data-testid="block-title-cs" value={(form.i18n?.cs?.title) || ""} onChange={(e) => setI18nTitle("cs", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
        </div>
        <Label className="text-[#475569]">Text</Label>
        <RichTextEditor testid="block-editor-cs" value={(form.i18n?.cs?.html) || ""} onChange={(v) => setI18nHtml("cs", v)} />
      </div>
      <div dir="rtl" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
        <p className="text-sm font-serif font-semibold text-[#9E7829]" dir="ltr">עברית · Hebrew</p>
        <div dir="ltr">
          <Label className="text-[#475569]">Heading (optional)</Label>
          <Input data-testid="block-title-he" value={(form.i18n?.he?.title) || ""} onChange={(e) => setI18nTitle("he", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
        </div>
        <Label className="text-[#475569]">Text</Label>
        <RichTextEditor testid="block-editor-he" value={(form.i18n?.he?.html) || ""} onChange={(v) => setI18nHtml("he", v)} />
      </div>
      <div dir="ltr" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
        <p className="text-sm font-serif font-semibold text-[#9E7829]">Русский · Russian</p>
        <div>
          <Label className="text-[#475569]">Heading (optional)</Label>
          <Input data-testid="block-title-ru" value={(form.i18n?.ru?.title) || ""} onChange={(e) => setI18nTitle("ru", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
        </div>
        <Label className="text-[#475569]">Text</Label>
        <RichTextEditor testid="block-editor-ru" value={(form.i18n?.ru?.html) || ""} onChange={(v) => setI18nHtml("ru", v)} />
      </div>
    </div>
  );

  return (
    <div data-testid="page-content-admin">
      <SectionTitle>Page Content</SectionTitle>

      {/* Header images for index pages */}
      <div className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5">
        <p className="font-semibold flex items-center gap-2"><ImageIcon size={16} className="text-[#BE923C]" /> Header background images</p>
        <p className="text-sm text-[#475569] mt-0.5 mb-4">The picture behind the title at the top of these pages. (Program, Tourist Desk & Kosher Establishments pictures are set on their own tabs.)</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {["services", "events", "classes"].map((k) => (
            <div key={k} data-testid={`page-header-${k}`}>
              <Label className="capitalize">{k} page</Label>
              <div className="mt-1.5"><ImageUploadField testid={`page-header-field-${k}`} value={headers[k]} onChange={(v) => setHeaders((h) => ({ ...h, [k]: v }))} /></div>
            </div>
          ))}
        </div>
        <button type="button" data-testid="save-page-headers" onClick={saveHeaders} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2.5">Save header images</button>
      </div>

      {/* Page hero text */}
      <div className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5" data-testid="page-hero-text-editor">
        <p className="font-semibold flex items-center gap-2"><FileText size={16} className="text-[#BE923C]" /> Page hero text</p>
        <p className="text-sm text-[#475569] mt-0.5 mb-4">The small label, big title and description at the very top of these pages. Leave a box blank to use the built-in wording.</p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div>
            <Label>Page</Label>
            <select data-testid="hero-page-select" value={heroPage} onChange={(e) => setHeroPage(e.target.value)}
              className="mt-1.5 block rounded-lg border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
              {HERO_PAGES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2 sm:mt-6">
            {["en", "cs", "he", "ru"].map((lg) => (
              <button key={lg} type="button" data-testid={`hero-lang-${lg}`} onClick={() => setHeroLang(lg)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${heroLang === lg ? "bg-[#0F172A] text-white" : "bg-[#F1F0EC] text-[#475569] hover:bg-[#E8E6E1]"}`}>
                {{ en: "English", cs: "Čeština", he: "עברית", ru: "Русский" }[lg]}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3" dir={heroLang === "he" ? "rtl" : "ltr"}>
          <div>
            <Label>Small label (eyebrow)</Label>
            <Input data-testid={`hero-eyebrow-${heroLang}`} value={heroVal("eyebrow")} placeholder={heroDefaults[heroLang].eyebrow}
              onChange={(e) => setHeroField("eyebrow", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
          </div>
          <div>
            <Label>Title</Label>
            <Input data-testid={`hero-title-${heroLang}`} value={heroVal("title")} placeholder={heroDefaults[heroLang].title}
              onChange={(e) => setHeroField("title", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
          </div>
          <div>
            <Label>Description {heroDefaults[heroLang].desc ? "" : "(optional)"}</Label>
            <Textarea data-testid={`hero-desc-${heroLang}`} rows={3} value={heroVal("desc")} placeholder={heroDefaults[heroLang].desc || "Add a short description (optional)"}
              onChange={(e) => setHeroField("desc", e.target.value)} className="mt-1.5 border-[#0F172A]/10 resize-none" />
          </div>
        </div>
        <button type="button" data-testid="save-page-hero-text" onClick={saveHeroTexts} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2.5">Save hero text</button>
      </div>

      {/* Content blocks */}
      <div className="rounded-2xl bg-white border border-[#0F172A]/5 p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
          <div className="flex-1">
            <p className="font-semibold flex items-center gap-2"><FileText size={16} className="text-[#BE923C]" /> Text blocks</p>
            <p className="text-sm text-[#475569] mt-0.5 mb-2">Add formatted text to a page and choose where it appears. Blocks show in order; use the arrows to reorder.</p>
            <Label>Pick a page</Label>
            <select data-testid="pagecontent-page-select" value={page} onChange={(e) => setPage(e.target.value)}
              className="mt-1.5 w-full sm:w-96 rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
              {pageOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <button data-testid="add-block-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
            <Plus size={16} /> Add text block
          </button>
        </div>

        <div className="space-y-3">
          {pageBlocks.map((b, i) => (
            <div key={b.id} data-testid={`admin-block-${b.id}`} className="flex items-start gap-3 rounded-2xl border border-[#0F172A]/8 p-4">
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`block-up-${b.id}`} className="p-1 rounded hover:bg-[#E8E6E1] disabled:opacity-30"><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === pageBlocks.length - 1} data-testid={`block-down-${b.id}`} className="p-1 rounded hover:bg-[#E8E6E1] disabled:opacity-30"><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <span className="inline-block rounded-full bg-[#BE923C]/12 text-[#9E7829] text-[11px] font-semibold px-2.5 py-0.5 uppercase tracking-wide">{b.position === "bottom" ? "Bottom of page" : "Top of page"}</span>
                {b.title && <p className="font-semibold mt-1.5">{b.title}</p>}
                <div className="text-sm text-[#475569] line-clamp-2 mt-1" dangerouslySetInnerHTML={{ __html: (b.html || "").replace(/<[^>]+>/g, " ").slice(0, 160) }} />
              </div>
              <button onClick={() => openEdit(b)} data-testid={`edit-block-${b.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
              <button onClick={() => remove(b.id)} data-testid={`delete-block-${b.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
          {pageBlocks.length === 0 && <p className="text-[#475569] text-sm py-4">No text blocks on this page yet.</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} text block</DialogTitle>
            <DialogDescription>Format your text and choose where it appears on the page. Translations are optional.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Where should it appear?</Label>
              <div className="mt-1.5 flex gap-2">
                {[{ v: "top", l: "Top (below the title)" }, { v: "bottom", l: "Bottom (end of page)" }].map((p) => (
                  <button type="button" key={p.v} data-testid={`block-position-${p.v}`} onClick={() => setForm({ ...form, position: p.v })}
                    className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${form.position === p.v ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#0F172A] border-[#0F172A]/12 hover:border-[#BE923C]"}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Heading (optional)</Label>
              <Input data-testid="block-title-en" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. What to expect" />
            </div>
            <div>
              <Label>Text</Label>
              <div className="mt-1.5"><RichTextEditor testid="block-editor-en" value={form.html} onChange={(v) => setForm({ ...form, html: v })} /></div>
            </div>
            <details className="rounded-xl border border-[#0F172A]/10 p-3">
              <summary className="cursor-pointer text-sm font-semibold text-[#475569]">Translations (Czech / Hebrew / Russian) — optional</summary>
              {RichLangBlocks}
            </details>
            <button type="submit" data-testid="save-block-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3">{editId ? "Save changes" : "Add block"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function KosherAdmin() {
  const emptyItem = { name: "", description: "", price: "", image_url: "", on_sale: false, i18n: { he: {}, ru: {} } };
  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState({ title: "", info: "", whatsapp_number: "", image_url: "" });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyItem);

  const load = () => api.get("/kosher-items").then((r) => setRows(r.data));
  const loadInfo = () => api.get("/settings").then((r) => setInfo({ title: "", info: "", whatsapp_number: "", image_url: "", ...(r.data.kosher || {}) }));
  useEffect(() => { load(); loadInfo(); }, []);

  const saveInfo = async (e) => { e.preventDefault(); await api.put("/settings", { values: { kosher: info } }); toast.success("Store info saved"); };
  const openNew = () => { setForm(emptyItem); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ name: it.name, description: it.description || "", price: it.price || "", image_url: it.image_url || "", on_sale: !!it.on_sale, i18n: it.i18n || { he: {}, ru: {} } }); setEditId(it.id); setOpen(true); };
  const save = async (e) => { e.preventDefault(); if (editId) await api.put(`/kosher-items/${editId}`, form); else await api.post("/kosher-items", form); setOpen(false); toast.success("Item saved"); load(); };
  const remove = async (id) => { await api.delete(`/kosher-items/${id}`); load(); };

  return (
    <div>
      <SectionTitle>Kosher Store</SectionTitle>

      <form onSubmit={saveInfo} className="space-y-3 mb-8 rounded-2xl bg-white border border-[#0F172A]/5 p-5">
        <p className="font-serif text-lg">Store information</p>
        <div><Label>Title</Label><Input data-testid="kosher-title" value={info.title} onChange={(e) => setInfo({ ...info, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="Kosher Food at Our Center" /></div>
        <div><Label>Info / description</Label><Textarea data-testid="kosher-info" rows={4} value={info.info} onChange={(e) => setInfo({ ...info, info: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
        <div><Label>WhatsApp number (with country code)</Label><Input data-testid="kosher-whatsapp" value={info.whatsapp_number} onChange={(e) => setInfo({ ...info, whatsapp_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="+420 000 000 000" /></div>
        <div><Label>Header image</Label><div className="mt-1.5"><ImageUploadField testid="kosher-image" value={info.image_url} onChange={(v) => setInfo({ ...info, image_url: v })} /></div></div>
        {TR_LANGS.map((L) => (
          <div key={L.code} dir={L.rtl ? "rtl" : "ltr"} className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-3">
            <p className="text-sm font-serif font-semibold text-[#9E7829]" dir="ltr">{L.label}</p>
            <div><Label className="text-[#475569]">Title</Label><Input data-testid={`kosher-title-${L.code}`} value={info[`title_${L.code}`] || ""} onChange={(e) => setInfo({ ...info, [`title_${L.code}`]: e.target.value })} className={`mt-1.5 border-[#0F172A]/10 ${L.rtl ? "text-right" : ""}`} /></div>
            <div><Label className="text-[#475569]">Info</Label><Textarea data-testid={`kosher-info-${L.code}`} rows={4} value={info[`info_${L.code}`] || ""} onChange={(e) => setInfo({ ...info, [`info_${L.code}`]: e.target.value })} className={`mt-1.5 border-[#0F172A]/10 resize-none ${L.rtl ? "text-right" : ""}`} /></div>
          </div>
        ))}
        <button type="submit" data-testid="save-kosher-info-btn" className="rounded-full bg-[#0F172A] text-white font-semibold px-6 py-3 hover:bg-[#1e293b] transition-colors">Save Store Info</button>
      </form>

      <div className="flex items-center justify-between mb-4">
        <p className="font-serif text-lg">Current sales / products</p>
        <button onClick={openNew} data-testid="add-kosher-item-btn" className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors"><Plus size={16} /> Add Item</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((it) => (
          <div key={it.id} data-testid={`kosher-item-${it.id}`} className="rounded-2xl bg-white border border-[#0F172A]/5 overflow-hidden">
            {it.image_url && <img src={resolveImg(it.image_url)} alt={it.name} className="w-full h-36 object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-serif text-lg leading-tight">{it.name}</h4>
                {it.on_sale && <span className="shrink-0 rounded-full bg-[#BE923C]/15 text-[#9E7829] text-[10px] font-bold uppercase px-2 py-1">On sale</span>}
              </div>
              {it.price && <p className="text-sm font-semibold text-[#BE923C] mt-1">{it.price}</p>}
              {it.description && <p className="text-sm text-[#475569] mt-1 line-clamp-2">{it.description}</p>}
              <div className="mt-3 flex gap-3">
                <button onClick={() => openEdit(it)} className="text-[#475569] hover:text-[#0F172A]" data-testid={`edit-kosher-${it.id}`}><Pencil size={16} /></button>
                <button onClick={() => remove(it.id)} className="text-[#475569] hover:text-red-600" data-testid={`delete-kosher-${it.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569]">No items yet — add your first special.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Name</Label><Input data-testid="kitem-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            <div><Label>Price</Label><Input data-testid="kitem-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. 120 CZK" /></div>
            <div><Label>Description</Label><Textarea data-testid="kitem-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
            <label className="flex items-center gap-2 text-sm text-[#475569]"><input type="checkbox" data-testid="kitem-onsale" checked={form.on_sale} onChange={(e) => setForm({ ...form, on_sale: e.target.checked })} /> Mark as "On sale"</label>
            <div><Label>Photo</Label><div className="mt-1.5"><ImageUploadField testid="kitem-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div></div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "name", label: "Name" },
              { field: "description", label: "Description", textarea: true, rows: 3 },
              { field: "price", label: "Price" },
            ]} />
            <button type="submit" data-testid="save-kitem-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Item</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMAIL_LANGS = [
  { code: "en", label: "English" },
  { code: "cs", label: "Čeština" },
  { code: "he", label: "עברית" },
  { code: "ru", label: "Русский" },
];
const EMAIL_GROUPS = [
  { key: "pending", title: "Pending email", desc: "Sent instantly when someone RSVPs. Make it clear their place is pending approval (no location is shown yet).", fields: [["pending_subject", "Subject line"], ["pending_heading", "Heading"], ["pending_greeting", "Greeting"], ["pending_note", "Body"]] },
  { key: "approval", title: "Approval email", desc: "Sent when you approve an RSVP. The event date, time & location are added automatically below your text.", fields: [["subject", "Subject line"], ["confirmed", "Heading"], ["greeting", "Greeting"], ["plans", "Body"]] },
  { key: "decline", title: "Decline email", desc: "Sent when you decline an RSVP.", fields: [["reject_subject", "Subject line"], ["reject_heading", "Heading"], ["reject_body", "Body"]] },
  { key: "shared", title: "Sign-off", desc: "Appears at the bottom of every email.", fields: [["signoff", "Sign-off"]] },
];
const EMAIL_TEXTAREAS = ["pending_greeting", "pending_note", "greeting", "plans", "reject_body", "signoff"];
const EMAIL_PLACEHOLDER_HINT = "You can use {name}, {title}, {date}, {time}, {location} and {guests} — they are filled in automatically.";

function LangTabs({ lang, setLang, prefix }) {
  return (
    <div className="flex gap-1.5">
      {EMAIL_LANGS.map((l) => (
        <button key={l.code} type="button" data-testid={`${prefix}-lang-${l.code}`} onClick={() => setLang(l.code)}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${lang === l.code ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcd9d2]"}`}>
          {l.label}
        </button>
      ))}
    </div>
  );
}

function EmailTemplateFields({ lang, getVal, setVal, placeholderFor, idPrefix, onTest, testingType, hideSubjects = false }) {
  const rtl = lang === "he";
  return (
    <div className="space-y-4">
      {EMAIL_GROUPS.map((g) => (
        <div key={g.key} className="rounded-xl border border-[#0F172A]/10 p-4 space-y-3 bg-[#FBFAF8]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">{g.title}</p>
              <p className="text-xs text-[#475569] mt-0.5">{g.desc}</p>
            </div>
            {onTest && g.key !== "shared" && (
              <button type="button" data-testid={`send-test-${idPrefix}-${g.key}`} onClick={() => onTest(g.key)} disabled={testingType === g.key}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#BE923C]/40 text-[#9E7829] px-3 py-1.5 text-xs font-semibold hover:bg-[#FBF3E2] transition-colors disabled:opacity-60">
                <Mail size={13} /> {testingType === g.key ? "Sending…" : "Send test"}
              </button>
            )}
          </div>
          {g.fields.filter(([key]) => !(hideSubjects && key.endsWith("subject"))).map(([key, label]) => {
            const isTa = EMAIL_TEXTAREAS.includes(key);
            const common = {
              "data-testid": `${idPrefix}-${key}`,
              value: getVal(key),
              placeholder: placeholderFor(key),
              onChange: (e) => setVal(key, e.target.value),
              dir: rtl ? "rtl" : "ltr",
            };
            return (
              <div key={key}>
                <Label className="text-xs text-[#475569]">{label}</Label>
                {isTa
                  ? <Textarea rows={key === "signoff" ? 2 : 3} {...common} className="mt-1.5 border-[#0F172A]/10 resize-none" />
                  : <Input {...common} className="mt-1.5 border-[#0F172A]/10" />}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const EMAIL_BRAND_DEFAULTS = { logo_url: "", logo_placement: "left", show_name: true, header_bg: "#F4EEE2", header_text: "#0F172A", accent: "#BE923C" };

function EmailBrandingCard() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [b, setB] = useState(EMAIL_BRAND_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const val = r.data.email_branding;
      if (val && typeof val === "object") setB({ ...EMAIL_BRAND_DEFAULTS, ...val });
      else setB({ ...EMAIL_BRAND_DEFAULTS, logo_url: `${BACKEND}/logo.png` });
    }).catch(() => {});
  }, [BACKEND]);

  const upd = (patch) => setB((p) => ({ ...p, ...patch }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd({ logo_url: `${BACKEND}${data.url}` });
      toast.success("Logo uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { values: { email_branding: b } });
      toast.success("Email branding saved");
    } catch (err) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const ColorRow = ({ label, k }) => (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-[#475569]">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" data-testid={`email-brand-${k}`} value={b[k]} onChange={(e) => upd({ [k]: e.target.value })}
          className="h-8 w-10 cursor-pointer rounded border border-[#0F172A]/10 bg-white p-0.5" />
        <Input value={b[k]} onChange={(e) => upd({ [k]: e.target.value })} className="w-24 h-8 border-[#0F172A]/10 text-xs font-mono" />
      </div>
    </div>
  );

  return (
    <div data-testid="email-branding-card" className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 mb-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#0F172A]">Email branding</h3>
          <p className="text-sm text-[#475569] mt-0.5">Add your logo and colors — applied to every automatic email (RSVP, sign-ups, contact).</p>
        </div>
        <button data-testid="email-branding-save" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save branding"}
        </button>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        {/* Controls */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-[#475569]">Logo</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="h-14 w-14 rounded-xl border border-[#0F172A]/10 bg-[#F9F8F6] overflow-hidden flex items-center justify-center shrink-0">
                {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-full w-full object-contain" /> : <ImageIcon size={20} className="text-[#94A3B8]" />}
              </div>
              <label className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-4 py-2 text-sm font-semibold cursor-pointer hover:border-[#BE923C] transition-colors">
                <Upload size={15} /> {uploading ? "Uploading…" : "Upload logo"}
                <input type="file" accept="image/*" data-testid="email-brand-logo-upload" onChange={uploadLogo} className="hidden" />
              </label>
              {b.logo_url && <button type="button" data-testid="email-brand-logo-remove" onClick={() => upd({ logo_url: "" })} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs text-[#475569]">Logo placement</Label>
            <div className="flex rounded-full border border-[#0F172A]/10 p-0.5">
              {["left", "center"].map((p) => (
                <button key={p} type="button" data-testid={`email-brand-placement-${p}`} onClick={() => upd({ logo_placement: p })}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors ${b.logo_placement === p ? "bg-[#0F172A] text-white" : "text-[#475569]"}`}>{p}</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="email-brand-show-name" checked={b.show_name !== false} onChange={(e) => upd({ show_name: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
            <span className="text-sm text-[#0F172A]">Show site name next to the logo</span>
          </label>

          <div className="space-y-2.5 pt-1 border-t border-[#0F172A]/5">
            <ColorRow label="Header background" k="header_bg" />
            <ColorRow label="Header text" k="header_text" />
            <ColorRow label="Accent line & highlights" k="accent" />
          </div>
        </div>

        {/* Live preview */}
        <div>
          <Label className="text-xs text-[#475569]">Live preview</Label>
          <div className="mt-1.5 rounded-xl border border-[#0F172A]/10 overflow-hidden" style={{ background: "#F9F8F6", padding: "20px" }} data-testid="email-branding-preview">
            <div style={{ maxWidth: 460, margin: "0 auto", background: "#fff", borderRadius: 14, overflow: "hidden", border: "1px solid #eee", fontFamily: "Arial, Helvetica, sans-serif" }}>
              <div style={{ background: b.header_bg, padding: "22px 26px", textAlign: b.logo_placement === "center" ? "center" : "left" }}>
                {b.logo_url && <img src={b.logo_url} alt="logo" style={{ maxHeight: 46, width: "auto", borderRadius: 8, display: "inline-block" }} />}
                {(b.show_name !== false || !b.logo_url) && <div style={{ fontSize: 18, fontWeight: "bold", color: b.header_text, marginTop: b.logo_url ? 8 : 0 }}>Chabad of Prague</div>}
              </div>
              <div style={{ background: b.accent, height: 4 }} />
              <div style={{ padding: 26 }}>
                <h1 style={{ margin: "0 0 8px", color: "#0F172A", fontSize: 22 }}>Looking forward!</h1>
                <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.6, margin: 0 }}>Dear Sarah, thank you for your RSVP. We can't wait to see you.</p>
                <div style={{ margin: "18px 0", background: "#F9F8F6", borderRadius: 12, padding: "16px 20px", borderInlineStart: `3px solid ${b.accent}` }}>
                  <div style={{ fontSize: 16, fontWeight: "bold", color: "#0F172A", marginBottom: 8 }}>Rosh Hashanah Family Celebration</div>
                  <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.9 }}>📅 2026-09-13 17:00<br />📍 Chabad Center<br />👥 Guests: 2</div>
                </div>
                <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 20 }}>Warmly,<br />The Chabad of Prague team</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmailTemplatesAdmin() {
  const { user } = useAuth();
  const [defaults, setDefaults] = useState({ en: {}, he: {}, ru: {} });
  const [tpl, setTpl] = useState({ default: {}, programs: {}, whatsapp: {} });
  const [cats, setCats] = useState([]);
  const [scope, setScope] = useState("default");
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingType, setTestingType] = useState(null);

  useEffect(() => {
    api.get("/email-templates").then((r) => {
      setDefaults(r.data.defaults || {});
      setTpl({ default: r.data.default || {}, programs: r.data.programs || {}, whatsapp: r.data.whatsapp || {} });
    }).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.email && !testEmail) setTestEmail(user.email); }, [user]); // eslint-disable-line

  const isWa = scope === "whatsapp";
  const layerFor = (s) => (s === "default" ? (tpl.default || {}) : s === "whatsapp" ? (tpl.whatsapp || {}) : ((tpl.programs || {})[s] || {}));
  // Effective email wording (default layer > built-in) — the fallback for WhatsApp text.
  const emailEffective = (key) => {
    const dl = (tpl.default || {})[lang] || {};
    if (typeof dl[key] === "string" && dl[key].trim()) return dl[key];
    return (defaults[lang]?.[key]) ?? (defaults.en?.[key]) ?? "";
  };
  const getVal = (key) => {
    const ll = (layerFor(scope)[lang]) || {};
    if (key in ll) return ll[key];
    if (isWa) return emailEffective(key);
    return (defaults[lang]?.[key]) ?? (defaults.en?.[key]) ?? "";
  };
  const placeholderFor = (key) => (isWa ? emailEffective(key) : ((defaults[lang]?.[key]) ?? (defaults.en?.[key]) ?? ""));
  const setVal = (key, value) => {
    setTpl((prev) => {
      const next = { default: { ...prev.default }, programs: { ...prev.programs }, whatsapp: { ...(prev.whatsapp || {}) } };
      if (scope === "whatsapp") {
        next.whatsapp = { ...next.whatsapp, [lang]: { ...(next.whatsapp[lang] || {}), [key]: value } };
      } else if (scope === "default") {
        next.default = { ...next.default, [lang]: { ...(next.default[lang] || {}), [key]: value } };
      } else {
        const prog = { ...(next.programs[scope] || {}) };
        prog[lang] = { ...(prog[lang] || {}), [key]: value };
        next.programs = { ...next.programs, [scope]: prog };
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const diffLayer = (layer) => {
      const out = {};
      for (const lg of ["en", "cs", "he", "ru"]) {
        const ll = layer[lg];
        if (!ll) continue;
        const o = {};
        for (const [k, v] of Object.entries(ll)) {
          const def = (defaults[lg]?.[k]) ?? (defaults.en?.[k]) ?? "";
          if (typeof v === "string" && v.trim() && v !== def) o[k] = v;
        }
        if (Object.keys(o).length) out[lg] = o;
      }
      return out;
    };
    try {
      const payloadDefault = diffLayer(tpl.default || {});
      const payloadPrograms = {};
      for (const [cat, layer] of Object.entries(tpl.programs || {})) {
        const d = diffLayer(layer || {});
        if (Object.keys(d).length) payloadPrograms[cat] = d;
      }
      const payloadWa = {};
      for (const lg of ["en", "cs", "he", "ru"]) {
        const ll = (tpl.whatsapp || {})[lg];
        if (!ll) continue;
        const o = {};
        for (const [k, v] of Object.entries(ll)) {
          const dl = (payloadDefault[lg] || {})[k];
          const emailText = (typeof dl === "string" && dl.trim()) ? dl : ((defaults[lg]?.[k]) ?? (defaults.en?.[k]) ?? "");
          if (typeof v === "string" && v.trim() && v !== emailText) o[k] = v;
        }
        if (Object.keys(o).length) payloadWa[lg] = o;
      }
      await api.put("/email-templates", { default: payloadDefault, programs: payloadPrograms, whatsapp: payloadWa });
      toast.success("Email templates saved");
    } catch (e) {
      toast.error("Could not save templates");
    }
    setSaving(false);
  };

  const sendTest = async (type) => {
    if (!testEmail.trim()) { toast.error("Enter an email address to send the test to"); return; }
    setTestingType(type);
    try {
      const r = await api.post("/email-templates/test", {
        type, lang, category: scope === "default" ? "" : scope,
        to_email: testEmail.trim(),
        draft_default: tpl.default, draft_programs: tpl.programs,
      });
      if (r.data.email_enabled) toast.success(`Test email sent to ${r.data.sent_to}`);
      else toast.warning("Email delivery isn't configured yet — nothing was sent.");
    } catch (e) {
      toast.error("Could not send the test email");
    }
    setTestingType(null);
  };

  return (
    <div data-testid="email-templates-admin">
      <SectionTitle action={
        <button data-testid="save-email-templates" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save templates"}
        </button>
      }>RSVP Emails</SectionTitle>

      <EmailBrandingCard />

      <div className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 space-y-4">
        <p className="text-sm text-[#475569]">Edit the wording of the three automatic RSVP emails. Boxes are pre-filled with the current wording — clear a box to restore the default. {EMAIL_PLACEHOLDER_HINT}</p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs text-[#475569]">Which templates</Label>
            <select data-testid="email-scope-select" value={scope} onChange={(e) => setScope(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
              <option value="default">Default — all events</option>
              <option value="whatsapp">WhatsApp wording (admin “Send on WhatsApp” button)</option>
              {cats.map((c) => <option key={c.id} value={c.title}>{c.title} (program)</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-[#475569]">Language</Label>
            <div className="mt-1.5"><LangTabs lang={lang} setLang={setLang} prefix="email-tpl" /></div>
          </div>
        </div>
        {isWa && (
          <p className="text-xs text-[#9E7829] bg-[#FBF3E2] border border-[#E8D3A2] rounded-lg px-3 py-2" data-testid="email-wa-note">
            These texts are used only for the WhatsApp message you send from an RSVP card. Boxes start with your email wording — change only what should read differently on WhatsApp (e.g. “reply to this message” instead of “reply to this email”). Clear a box to fall back to the email text. Subject lines are not used on WhatsApp.
          </p>
        )}
        {scope !== "default" && !isWa && (
          <p className="text-xs text-[#9E7829] bg-[#FBF3E2] border border-[#E8D3A2] rounded-lg px-3 py-2">
            Program templates apply to events in “{scope}” and override the default. An individual event can override this again from its own editor.
          </p>
        )}

        {!isWa && <div className="flex flex-col gap-2 rounded-xl bg-[#FBFAF8] border border-[#0F172A]/10 p-3">
          <div>
            <Label className="text-xs text-[#475569]">Send a test email to</Label>
            <Input data-testid="email-test-recipient" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com" className="mt-1.5 border-[#0F172A]/10 w-full" />
          </div>
          <p className="text-xs text-[#475569]">Uses the wording currently on screen (including unsaved edits) with sample event details. Look for the “[TEST]” prefix.</p>
        </div>}

        <EmailTemplateFields lang={lang} idPrefix="email-tpl" getVal={getVal} setVal={setVal} placeholderFor={placeholderFor} onTest={isWa ? null : sendTest} testingType={testingType} hideSubjects={isWa} />
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-[#475569]">Loading…</div>;
  }

  return (
    <div dir="ltr" className="min-h-screen bg-[#F9F8F6] flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-white border-r border-[#0F172A]/5 fixed inset-y-0 left-0">
        <div className="p-6 flex items-center gap-2 border-b border-[#0F172A]/5">
          <span className="w-10 h-10 flex items-center justify-center"><img src="/logo.png" alt="Chabad of Prague logo" className="w-full h-full object-contain" /></span>
          <span className="font-serif text-lg leading-tight">Chabad Prague<br /><span className="text-xs text-[#475569] font-sans">Admin</span></span>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-nav-${id}`}
              onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                tab === id ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-[#E8E6E1]"
              }`}
            >
              <Icon size={18} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#0F172A]/5">
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-[#475569] hover:bg-[#E8E6E1] transition-colors"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ms-64">
        <div className="md:hidden flex overflow-x-auto gap-2 p-3 bg-white border-b border-[#0F172A]/5 sticky top-0 z-20">
          {NAV.map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium ${tab === id ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569]"}`}>
              {label}
            </button>
          ))}
        </div>
        <main className="p-5 sm:p-8 max-w-6xl">
          {tab === "overview" && <Overview />}
          {tab === "programs" && <CategoriesAdmin />}
          {tab === "events" && <EventsAdmin />}
          {tab === "calendar" && <CalendarNotesAdmin />}
          {tab === "holidays" && <HolidaysAdmin />}
          {tab === "staff" && <StaffAdmin />}
          {tab === "numbers" && <StatsAdmin />}
          {tab === "weekly" && <WeeklyAdmin />}
          {tab === "kosher" && <KosherAdmin />}
          {tab === "rsvps" && <RsvpsAdmin />}
          {tab === "archive" && <PastEventsAdmin />}
          {tab === "emails" && <EmailTemplatesAdmin />}
          {tab === "students" && <><StudentsAdmin /><StudentCardsAdmin /></>}
          {tab === "children" && <ChildProgramAdmin />}
          {tab === "servicepages" && <ServicePagesAdmin />}
          {tab === "pagecontent" && <PageContentAdmin />}
          {tab === "analytics" && <AnalyticsAdmin />}
          {tab === "sitetext" && <SiteTextAdmin />}
          {tab === "messages" && <Messages />}
          {tab === "gallery" && <GalleryAdmin />}
          {tab === "donations" && <SimpleList endpoint="/donations" title="Donations" testid="donations"
            columns={[["donor_name", "Donor"], ["donor_email", "Email"], ["amount", "Amount ($)"], ["frequency", "Frequency"]]} />}
          {tab === "payments" && <PaymentsAdmin />}
        </main>
      </div>
    </div>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h1 className="font-serif text-3xl tracking-tight">{children}</h1>
      {action}
    </div>
  );
}

function Overview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [waUrl, setWaUrl] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const [waText, setWaText] = useState({ title: "", desc: "", cta: "", email_button: "", i18n: { he: {}, ru: {} } });
  const [heroImg, setHeroImg] = useState("");
  const [collageImgs, setCollageImgs] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [siteNameSaving, setSiteNameSaving] = useState(false);
  const [defaultLang, setDefaultLang] = useState("en");
  const [langSaving, setLangSaving] = useState(false);
  const [heroLayout, setHeroLayout] = useState("split");
  const [heroSaving, setHeroSaving] = useState(false);
  const [ga4, setGa4] = useState("");
  const [ga4Saving, setGa4Saving] = useState(false);
  const [social, setSocial] = useState({ facebook: "", instagram: "" });
  const [socialSaving, setSocialSaving] = useState(false);
  const [navVis, setNavVis] = useState({});
  const [navSaving, setNavSaving] = useState(false);
  const [enabledLangs, setEnabledLangs] = useState(["en", "cs", "he", "ru"]);
  const [langVisSaving, setLangVisSaving] = useState(false);
  const [bulkTarget, setBulkTarget] = useState("cs");
  const [bulkStatus, setBulkStatus] = useState(null);
  const bulkPollRef = useRef(null);
  const bulkLangName = { cs: "Czech", he: "Hebrew", ru: "Russian" }[bulkTarget] || bulkTarget;
  const pollBulk = () => {
    api.get("/admin/bulk-translate/status").then((r) => {
      setBulkStatus(r.data);
      if (!r.data.running && bulkPollRef.current) {
        clearInterval(bulkPollRef.current); bulkPollRef.current = null;
        if ((r.data.updated_fields || 0) > 0) toast.success(`Filled ${r.data.updated_fields} ${bulkLangName} translation${r.data.updated_fields === 1 ? "" : "s"}. Reload public pages to see them.`);
        else if (!r.data.error) toast.success("Everything is already translated — nothing to fill.");
        if (r.data.error) toast.error("Translation run had an issue: " + r.data.error);
      }
    }).catch(() => {});
  };
  const startBulkTranslate = async () => {
    try {
      const { data } = await api.post("/admin/bulk-translate", { target: bulkTarget });
      if (data.started === false && data.running) toast.message("A translation run is already in progress.");
      setBulkStatus({ running: true, done: 0, total: 0, updated_fields: 0 });
      if (bulkPollRef.current) clearInterval(bulkPollRef.current);
      bulkPollRef.current = setInterval(pollBulk, 2000);
      setTimeout(pollBulk, 800);
    } catch (e) { toast.error("Could not start translation"); }
  };
  useEffect(() => {
    api.get("/admin/bulk-translate/status").then((r) => {
      setBulkStatus(r.data);
      if (r.data.running) { bulkPollRef.current = setInterval(pollBulk, 2000); }
    }).catch(() => {});
    return () => { if (bulkPollRef.current) clearInterval(bulkPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/whatsapp-group").then((r) => {
      setWaUrl(r.data.url || "");
      setWaText({ title: r.data.title || "", desc: r.data.desc || "", cta: r.data.cta || "", email_button: r.data.email_button || "", i18n: r.data.i18n || { he: {}, ru: {} } });
    }).catch(() => {});
    api.get("/settings").then((r) => {
      setHeroImg(r.data.hero_image || "");
      setCollageImgs(r.data.hero_collage_images || []);
      setSiteName(r.data.site_name || "");
      setDefaultLang(r.data.default_lang || "en");
      setHeroLayout(r.data.hero_layout || "split");
      setGa4(r.data.ga4_measurement_id || "");
      setSocial({ facebook: "", instagram: "", ...(r.data.social || {}) });
      setNavVis(r.data.nav_visibility || {});
      setEnabledLangs(Array.isArray(r.data.enabled_languages) && r.data.enabled_languages.length ? r.data.enabled_languages : ["en", "cs", "he", "ru"]);
    }).catch(() => {});
  }, []);
  const saveWa = async () => {
    setWaSaving(true);
    try {
      await api.put("/whatsapp-group", { url: waUrl, title: waText.title, desc: waText.desc, cta: waText.cta, email_button: waText.email_button, i18n: waText.i18n });
      toast.success(waUrl.trim() ? "WhatsApp strip saved — it's live on the homepage" : "WhatsApp link cleared — strip hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setWaSaving(false); }
  };
  const saveSiteName = async () => {    setSiteNameSaving(true);
    try {
      await api.put("/settings", { values: { site_name: siteName.trim() } });
      toast.success(siteName.trim() ? `Site name updated to "${siteName.trim()}"` : "Site name reset to default");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSiteNameSaving(false); }
  };
  const saveDefaultLang = async (l) => {
    setDefaultLang(l);
    setLangSaving(true);
    try {
      await api.put("/settings", { values: { default_lang: l } });
      const names = { en: "English", cs: "Czech", he: "Hebrew", ru: "Russian" };
      toast.success(`Website now opens in ${names[l]} for new visitors`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLangSaving(false); }
  };
  const saveHero = async () => {
    setHeroSaving(true);
    try {
      await api.put("/settings", { values: { hero_image: heroImg, hero_layout: heroLayout, hero_collage_images: collageImgs.map((s) => (s || "").trim()).filter(Boolean) } });
      toast.success("Homepage hero saved — reload the site to see it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setHeroSaving(false); }
  };
  const saveGa4 = async () => {
    setGa4Saving(true);
    try {
      await api.put("/settings", { values: { ga4_measurement_id: ga4.trim() } });
      toast.success(ga4.trim() ? "Google Analytics connected — reload the site to start tracking" : "Google Analytics disconnected");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setGa4Saving(false); }
  };
  const saveSocial = async () => {
    setSocialSaving(true);
    try {
      await api.put("/settings", { values: { social: { facebook: social.facebook.trim(), instagram: social.instagram.trim() } } });
      toast.success("Social links saved — icons show on the site when a link is set");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSocialSaving(false); }
  };
  const toggleNav = async (id) => {
    const next = { ...navVis, [id]: navVis[id] === false ? true : false };
    setNavVis(next);
    setNavSaving(true);
    try {
      await api.put("/settings", { values: { nav_visibility: next } });
      toast.success(next[id] === false ? "Hidden from the menu" : "Shown in the menu");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setNavVis(navVis);
    } finally { setNavSaving(false); }
  };
  const toggleLangVis = async (code) => {
    if (code === "en") return; // English is the base language and always stays on.
    const on = enabledLangs.includes(code);
    const next = on ? enabledLangs.filter((c) => c !== code) : [...enabledLangs, code];
    if (!next.includes("en")) next.unshift("en");
    setEnabledLangs(next);
    setLangVisSaving(true);
    try {
      await api.put("/settings", { values: { enabled_languages: next } });
      const names = { cs: "Czech", he: "Hebrew", ru: "Russian" };
      toast.success(on ? `${names[code]} hidden from the language menu` : `${names[code]} now shows in the language menu`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setEnabledLangs(enabledLangs);
    } finally { setLangVisSaving(false); }
  };
  const cards = [
    { label: "Events", value: stats?.events, icon: CalendarDays },
    { label: "RSVPs", value: stats?.rsvps, icon: Users },
    { label: "Messages", value: stats?.contacts, icon: MessageSquare },
    { label: "Unread", value: stats?.unread_contacts, icon: MessageSquare },
    { label: "Donations", value: stats?.donations, icon: HeartHandshake },
  ];
  const HERO_LAYOUTS = [
    { id: "split", label: "Split", desc: "Text left · portrait photo right" },
    { id: "centered", label: "Centered", desc: "Centered headline · wide photo band" },
    { id: "collage", label: "Collage", desc: "Text left · 3-photo mosaic right" },
  ];
  const NAV_ITEMS = [
    { id: "home", label: "Home", where: "Header" },
    { id: "programs", label: "Programs", where: "Header · Footer" },
    { id: "events", label: "Events", where: "Header · Footer" },
    { id: "holidays", label: "Holidays", where: "Header" },
    { id: "services", label: "Services", where: "Header" },
    { id: "about", label: "About", where: "Header · Footer" },
    { id: "gallery", label: "Gallery", where: "Header · Footer" },
    { id: "contact", label: "Contact", where: "Header · Footer" },
    { id: "donate", label: "Donate button", where: "Header · Footer" },
    { id: "footer_admin", label: "Admin login", where: "Footer" },
  ];
  return (
    <div>
      <SectionTitle>Overview</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} data-testid={`stat-${label.toLowerCase()}`} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <Icon size={20} className="text-[#BE923C]" />
            <p className="mt-4 font-serif text-4xl">{value ?? "—"}</p>
            <p className="text-sm text-[#475569]">{label}</p>
          </div>
        ))}
      </div>

      <div data-testid="site-name-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Site name</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">The name shown in the top navigation bar, the footer, the browser tab, and in all email headers & the sender name. Leave blank to use the default "Chabad of Prague".</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <Input data-testid="site-name-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Chabad of Prague" className="flex-1 border-[#0F172A]/10" />
          <button onClick={saveSiteName} disabled={siteNameSaving} data-testid="save-site-name" className="rounded-full bg-[#0F172A] text-white font-semibold px-6 py-2.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60">{siteNameSaving ? "Saving…" : "Save name"}</button>
        </div>
      </div>

      <div data-testid="visual-edit-admin" className="mt-6 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1e293b] text-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.12)]">
        <div className="flex items-center gap-2">
          <Pencil size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold">Edit website visually <span className="ml-1 rounded-full bg-[#BE923C]/20 text-[#E9C77E] text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">Beta · Homepage</span></h3>
        </div>
        <p className="mt-1 text-sm text-white/70">Open your live homepage and edit text right on the page — click any highlighted text to change it, switch language, hide or reorder sections, and add new text sections. Currently available on the homepage; more pages coming.</p>
        <button data-testid="open-visual-editor" onClick={() => navigate("/?edit=1")}
          className="mt-4 rounded-full bg-[#BE923C] text-white font-semibold px-6 py-2.5 hover:bg-[#a67e31] transition-colors">
          Open visual editor →
        </button>
      </div>


      <div data-testid="default-lang-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Default website language</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">The language the public website opens in for new visitors. Visitors can still switch anytime, and returning visitors keep their own choice. (This does not change the admin dashboard.)</p>
        <div className="mt-3 flex gap-2">
          {[{ code: "en", label: "English" }, { code: "cs", label: "Čeština · Czech" }, { code: "he", label: "עברית · Hebrew" }, { code: "ru", label: "Русский · Russian" }].map((l) => (
            <button key={l.code} data-testid={`default-lang-${l.code}`} disabled={langSaving} onClick={() => saveDefaultLang(l.code)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${defaultLang === l.code ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div data-testid="lang-visibility-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Languages shown on the site</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Choose which languages appear in the public language menu. Turning one off simply hides it from visitors — your translations are kept and it comes right back when you turn it on. English is the base language and always stays on.</p>
        <div className="mt-4 flex flex-col divide-y divide-[#0F172A]/5 border border-[#0F172A]/5 rounded-xl overflow-hidden">
          {[{ code: "en", label: "English", locked: true }, { code: "cs", label: "Čeština · Czech" }, { code: "he", label: "עברית · Hebrew" }, { code: "ru", label: "Русский · Russian" }].map((l) => {
            const on = l.locked || enabledLangs.includes(l.code);
            return (
              <div key={l.code} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium text-[#0F172A]">{l.label}{l.locked && <span className="ml-2 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wide">Always on</span>}</span>
                <button
                  data-testid={`lang-vis-toggle-${l.code}`}
                  disabled={l.locked || langVisSaving}
                  onClick={() => toggleLangVis(l.code)}
                  aria-pressed={on}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-[#BE923C]" : "bg-[#CBD5E1]"} ${l.locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} disabled:opacity-60`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div data-testid="bulk-translate-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Bulk auto-translate</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Fill in every missing translation across the whole site in one go — events, programs, holidays, staff, gallery, the synagogue &amp; kosher info, form questions and more. Only empty fields are filled; anything you've already written is left untouched. Uses AI, so review important wording afterwards.</p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <Label className="text-xs text-[#475569]">Language to fill</Label>
            <div className="mt-1.5 flex gap-2">
              {[{ code: "cs", label: "Čeština" }, { code: "he", label: "עברית" }, { code: "ru", label: "Русский" }].map((l) => (
                <button key={l.code} type="button" data-testid={`bulk-target-${l.code}`} disabled={bulkStatus?.running}
                  onClick={() => setBulkTarget(l.code)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${bulkTarget === l.code ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="bulk-translate-btn" onClick={startBulkTranslate} disabled={bulkStatus?.running}
            className="rounded-full bg-[#BE923C] text-white font-semibold px-6 py-2.5 hover:bg-[#a67e31] transition-colors disabled:opacity-60">
            {bulkStatus?.running ? "Translating…" : `Fill missing ${bulkLangName} translations`}
          </button>
        </div>
        {bulkStatus?.running && (
          <div className="mt-4" data-testid="bulk-translate-progress">
            <div className="h-2 rounded-full bg-[#E8E6E1] overflow-hidden">
              <div className="h-full bg-[#BE923C] transition-all duration-500" style={{ width: `${bulkStatus.total ? Math.round((bulkStatus.done / bulkStatus.total) * 100) : 5}%` }} />
            </div>
            <p className="mt-2 text-xs text-[#94A3B8]">Working through your content… {bulkStatus.done || 0}/{bulkStatus.total || "?"}{bulkStatus.updated_fields ? ` · ${bulkStatus.updated_fields} filled so far` : ""}. You can leave this page — it keeps running.</p>
          </div>
        )}
        {!bulkStatus?.running && bulkStatus?.finished_at && (
          <p className="mt-3 text-xs text-[#475569]" data-testid="bulk-translate-result">Last run: filled {bulkStatus.updated_fields || 0} field{(bulkStatus.updated_fields || 0) === 1 ? "" : "s"}{bulkStatus.error ? ` · issue: ${bulkStatus.error}` : ""}. Reload the public site to see the changes.</p>
        )}
      </div>

      <div data-testid="nav-visibility-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Header &amp; footer menu</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Choose which links appear in the navigation bar and footer. Toggle any item off to hide it from visitors.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
          {NAV_ITEMS.map((it) => {
            const on = navVis[it.id] !== false;
            return (
              <div key={it.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#0F172A]/8 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#0F172A]">{it.label}</p>
                  <p className="text-xs text-[#94A3B8]">{it.where}</p>
                </div>
                <button
                  type="button"
                  data-testid={`nav-toggle-${it.id}`}
                  onClick={() => toggleNav(it.id)}
                  disabled={navSaving}
                  aria-pressed={on}
                  className={`relative w-12 h-7 rounded-full transition-colors disabled:opacity-60 ${on ? "bg-[#BE923C]" : "bg-[#CBD5E1]"}`}
                >
                  <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div data-testid="whatsapp-group-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-[#25D366]" />
          <h3 className="font-semibold text-[#0F172A]">Community WhatsApp group</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Paste your group invite link (e.g. https://chat.whatsapp.com/XXXX). The strip shows on the homepage only when a link is set. Leave blank to hide it.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <Input data-testid="whatsapp-group-input" value={waUrl} onChange={(e) => setWaUrl(e.target.value)} placeholder="https://chat.whatsapp.com/..." className="flex-1 border-[#0F172A]/10" />
        </div>

        <p className="mt-5 text-sm font-semibold text-[#0F172A]">Strip text</p>
        <p className="text-xs text-[#475569]">Customize the headline, description, and button. Leave any field blank to use the default wording.</p>
        <div className="mt-2 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Headline</Label>
            <Input data-testid="wa-title-input" value={waText.title} onChange={(e) => setWaText({ ...waText, title: e.target.value })} placeholder="Join our community" className="mt-1 border-[#0F172A]/10" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input data-testid="wa-desc-input" value={waText.desc} onChange={(e) => setWaText({ ...waText, desc: e.target.value })} placeholder="Get event updates on WhatsApp." className="mt-1 border-[#0F172A]/10" />
          </div>
          <div>
            <Label className="text-xs">Button label</Label>
            <Input data-testid="wa-cta-input" value={waText.cta} onChange={(e) => setWaText({ ...waText, cta: e.target.value })} placeholder="Join" className="mt-1 border-[#0F172A]/10" />
          </div>
        </div>
        <div className="mt-3 rounded-xl border border-[#25D366]/25 bg-[#F0FAF3] p-3">
          <Label className="text-xs">Approval-email button text</Label>
          <p className="text-[11px] text-[#475569] mt-0.5">Shown as a WhatsApp button in event approval emails (per-event toggle is in each event's editor).</p>
          <Input data-testid="wa-email-button-input" value={waText.email_button} onChange={(e) => setWaText({ ...waText, email_button: e.target.value })} placeholder="Connect to our WhatsApp to stay updated" className="mt-1.5 border-[#0F172A]/10 bg-white" />
        </div>
        <div className="mt-3">
          <TranslationBox form={waText} setForm={setWaText} fields={[
            { field: "title", label: "Headline" },
            { field: "desc", label: "Description" },
            { field: "cta", label: "Button label" },
            { field: "email_button", label: "Approval-email button" },
          ]} />
        </div>

        <button onClick={saveWa} disabled={waSaving} data-testid="save-whatsapp-group" className="mt-4 rounded-full bg-[#0F172A] text-white font-semibold px-6 py-2.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60">{waSaving ? "Saving…" : "Save WhatsApp strip"}</button>
      </div>

      <div data-testid="social-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Instagram size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Social media links</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Add your Facebook and Instagram page URLs. Icons appear in the footer and contact section when set. Leave blank to hide.</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Facebook page URL</Label>
            <Input data-testid="social-facebook-input" value={social.facebook} onChange={(e) => setSocial({ ...social, facebook: e.target.value })} placeholder="https://facebook.com/yourpage" className="mt-1 border-[#0F172A]/10" />
          </div>
          <div>
            <Label className="text-xs">Instagram profile URL</Label>
            <Input data-testid="social-instagram-input" value={social.instagram} onChange={(e) => setSocial({ ...social, instagram: e.target.value })} placeholder="https://instagram.com/yourprofile" className="mt-1 border-[#0F172A]/10" />
          </div>
        </div>
        <button onClick={saveSocial} disabled={socialSaving} data-testid="save-social" className="mt-3 rounded-full bg-[#0F172A] text-white font-semibold px-6 py-2.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60">{socialSaving ? "Saving…" : "Save social links"}</button>
      </div>

      <div data-testid="ga4-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Google Analytics (optional)</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">For full demographics (age, gender, interests, city). Create a free property at analytics.google.com, then paste your Measurement ID (starts with <b>G-</b>). Leave blank to use only the built-in Analytics tab.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <Input data-testid="ga4-input" value={ga4} onChange={(e) => setGa4(e.target.value)} placeholder="G-XXXXXXXXXX" className="flex-1 border-[#0F172A]/10" />
          <button onClick={saveGa4} disabled={ga4Saving} data-testid="save-ga4" className="rounded-full bg-[#0F172A] text-white font-semibold px-6 py-2.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60">{ga4Saving ? "Saving…" : "Save"}</button>
        </div>
        <a href="https://analytics.google.com" target="_blank" rel="noreferrer" data-testid="view-ga4-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#9E7829] hover:underline">Open Google Analytics dashboard →</a>
      </div>

      <div data-testid="hero-image-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <ImageIcon size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Homepage hero</h3>
        </div>
        <p className="mt-1 text-sm text-[#475569]">Choose a layout and set the main photo shown at the top of the homepage. A tall/portrait photo works best for Split & Collage. Leave the image blank to use the default.</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {HERO_LAYOUTS.map((l) => (
            <button key={l.id} type="button" data-testid={`hero-layout-${l.id}`} onClick={() => setHeroLayout(l.id)}
              className={`text-left rounded-xl border p-4 transition-colors ${heroLayout === l.id ? "border-[#BE923C] bg-[#FBF7EE]" : "border-[#0F172A]/10 hover:border-[#BE923C]/50"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded-full border-2 ${heroLayout === l.id ? "border-[#BE923C] bg-[#BE923C]" : "border-[#0F172A]/30"}`} />
                <span className="font-semibold text-[#0F172A]">{l.label}</span>
              </div>
              <p className="mt-1 text-xs text-[#475569]">{l.desc}</p>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <Label className="text-xs">Main hero image</Label>
          <div className="mt-1.5"><ImageUploadField testid="hero-image" value={heroImg} onChange={setHeroImg} /></div>
        </div>
        <div className="mt-5 rounded-xl border border-[#0F172A]/10 p-4 bg-[#FBFAF8]">
          <Label className="text-xs">Rotating collage pictures (Collage layout)</Label>
          <p className="mt-1 text-xs text-[#475569]">Add as many photos as you like. The collage keeps its 3 frames, and your photos gently rotate through them — one swaps about every 4 seconds — so each picture takes a turn, including in the large frame. Leave empty to use the main image + your gallery.</p>
          <div className="mt-3 space-y-3">
            {collageImgs.map((img, idx) => (
              <div key={idx} className="flex items-start gap-2" data-testid={`collage-img-row-${idx}`}>
                <span className="mt-3 text-xs font-semibold text-[#9E7829] w-5 shrink-0">{idx + 1}.</span>
                <div className="flex-1"><ImageUploadField testid={`collage-image-${idx}`} value={img} onChange={(v) => setCollageImgs((prev) => prev.map((x, i) => (i === idx ? v : x)))} /></div>
                <button type="button" data-testid={`remove-collage-img-${idx}`} onClick={() => setCollageImgs((prev) => prev.filter((_, i) => i !== idx))} className="mt-1 p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
            {collageImgs.length === 0 && <p className="text-xs text-[#94a3b8]">No collage photos added — the main image and gallery photos will be used.</p>}
          </div>
          <button type="button" data-testid="add-collage-img" onClick={() => setCollageImgs((prev) => [...prev, ""])} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 px-4 py-2 text-xs font-semibold hover:border-[#BE923C] transition-colors"><Plus size={14} /> Add picture</button>
        </div>
        <button onClick={saveHero} disabled={heroSaving} data-testid="save-hero-image" className="mt-3 rounded-full bg-[#0F172A] text-white font-semibold px-6 py-2.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60">{heroSaving ? "Saving…" : "Save hero"}</button>
      </div>

      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (nw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (nw !== confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed — use it next time you log in");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="change-password-admin" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-2">
        <KeyRound size={18} className="text-[#BE923C]" />
        <h3 className="font-semibold text-[#0F172A]">Change admin password</h3>
      </div>
      <p className="mt-1 text-sm text-[#475569]">Update your login password. Minimum 8 characters. You'll keep your current session; use the new password on your next login.</p>
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <Input type="password" data-testid="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="border-[#0F172A]/10" autoComplete="current-password" />
        <Input type="password" data-testid="new-password-input" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password" className="border-[#0F172A]/10" autoComplete="new-password" />
        <Input type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="border-[#0F172A]/10" autoComplete="new-password" />
      </div>
      <button onClick={save} disabled={saving || !cur || !nw || !confirm} data-testid="save-password-btn" className="mt-3 rounded-full bg-[#BE923C] text-white font-semibold px-6 py-2.5 hover:bg-[#9E7829] transition-colors disabled:opacity-60">{saving ? "Updating…" : "Update password"}</button>
    </div>
  );
}

function PaymentsAdmin() {
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripe, setStripe] = useState({ secret: "", publishable: "", mode: "test", webhook_secret: "" });
  const [paypal, setPaypal] = useState({ client_id: "", secret: "", mode: "sandbox" });
  const [currency, setCurrency] = useState({ donation: "eur", event: "czk" });
  const [curSaving, setCurSaving] = useState(false);

  const load = () => {
    api.get("/admin/payment-settings").then((r) => {
      setInfo(r.data);
      setStripe((s) => ({ ...s, mode: r.data.stripe.mode || "test" }));
      setPaypal((p) => ({ ...p, mode: r.data.paypal.mode || "sandbox" }));
      setCurrency({ donation: r.data.donation_currency || "eur", event: r.data.event_currency || "czk" });
    }).catch(() => {});
  };
  useEffect(load, []);

  const save = async (payload, label) => {
    setSaving(true);
    try {
      await api.put("/admin/payment-settings", payload);
      toast.success(`${label} saved`);
      setStripe((s) => ({ ...s, secret: "", publishable: "" }));
      setPaypal((p) => ({ ...p, client_id: "", secret: "" }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveStripe = () => save({
    stripe_secret: stripe.secret,
    stripe_publishable: stripe.publishable,
    stripe_mode: stripe.mode,
    stripe_webhook_secret: stripe.webhook_secret,
  }, "Stripe settings");

  const savePaypal = () => save({
    paypal_client_id: paypal.client_id,
    paypal_secret: paypal.secret,
    paypal_mode: paypal.mode,
  }, "PayPal settings");

  const disconnect = async (provider) => {
    setSaving(true);
    try {
      await api.delete(`/admin/payment-settings/${provider}`);
      toast.success(`${provider === "paypal" ? "PayPal" : "Stripe"} keys removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveCurrency = async () => {
    setCurSaving(true);
    try {
      await api.put("/admin/payment-settings", { donation_currency: currency.donation, event_currency: currency.event });
      toast.success("Currencies saved — new checkouts will charge in these currencies");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setCurSaving(false); }
  };

  const CUR_OPTS = [["eur", "EUR (€)"], ["czk", "CZK (Kč)"]];

  const StatusBadge = ({ ok, source }) => (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${ok ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-[#16A34A]" : "bg-[#DC2626]"}`} />
      {ok ? `Connected${source === "env" ? " (server key)" : ""}` : "Not configured"}
    </span>
  );

  return (
    <div>
      <SectionTitle>Payments</SectionTitle>
      <p className="-mt-3 mb-6 max-w-2xl text-sm text-[#475569]">
        Enter your live payment keys here to accept donations and paid event tickets. Keys are stored securely on the server and never shown in full again — to change one, just paste a new value. Leave a field blank to keep the current key.
      </p>

      {/* Currency */}
      <div data-testid="currency-settings" className="rounded-2xl bg-white border border-[#0F172A]/5 p-6 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <Globe size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Currency</h3>
        </div>
        <p className="mt-2 text-sm text-[#475569]">Choose the currency guests are charged in. This applies to BOTH Stripe (card) and PayPal, and updates the amounts shown on the site. Donations and events can use different currencies.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs">Donations currency</Label>
            <div className="mt-1.5 flex gap-2">
              {CUR_OPTS.map(([v, l]) => (
                <button key={v} type="button" data-testid={`donation-currency-${v}`} onClick={() => setCurrency((c) => ({ ...c, donation: v }))}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${currency.donation === v ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Events (paid tickets) currency</Label>
            <div className="mt-1.5 flex gap-2">
              {CUR_OPTS.map(([v, l]) => (
                <button key={v} type="button" data-testid={`event-currency-${v}`} onClick={() => setCurrency((c) => ({ ...c, event: v }))}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${currency.event === v ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={saveCurrency} disabled={curSaving} data-testid="save-currency" className="mt-4 rounded-full bg-[#BE923C] text-white font-semibold px-6 py-2.5 hover:bg-[#9E7829] transition-colors disabled:opacity-60">{curSaving ? "Saving…" : "Save currencies"}</button>
      </div>

      {/* Stripe */}
      <div data-testid="stripe-settings" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-6 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-[#635BFF]" />
            <h3 className="font-semibold text-[#0F172A]">Stripe (cards)</h3>
          </div>
          {info && <StatusBadge ok={info.stripe.secret_set} source={info.stripe.source} />}
        </div>
        <p className="mt-2 text-sm text-[#475569]">Handles card payments, monthly recurring donations, and paid event tickets in the currencies you set above. Get your keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="font-semibold text-[#635BFF] hover:underline">Stripe dashboard → Developers → API keys</a>.</p>
        {info?.stripe.secret_set && (
          <p className="mt-2 text-xs text-[#475569]">Current secret key: <span className="font-mono">{info.stripe.secret_masked}</span></p>
        )}
        <div className="mt-4 grid gap-3">
          <div>
            <Label className="text-xs">Secret key (starts with sk_)</Label>
            <Input type="password" data-testid="stripe-secret-input" value={stripe.secret} onChange={(e) => setStripe({ ...stripe, secret: e.target.value })} placeholder={info?.stripe.secret_set ? "•••• leave blank to keep current" : "sk_live_..."} className="mt-1 border-[#0F172A]/10 font-mono" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs">Publishable key (starts with pk_)</Label>
            <Input data-testid="stripe-publishable-input" value={stripe.publishable} onChange={(e) => setStripe({ ...stripe, publishable: e.target.value })} placeholder={info?.stripe.publishable ? info.stripe.publishable : "pk_live_..."} className="mt-1 border-[#0F172A]/10 font-mono" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs">Webhook signing secret (starts with whsec_)</Label>
            <Input type="password" data-testid="stripe-webhook-secret-input" value={stripe.webhook_secret} onChange={(e) => setStripe({ ...stripe, webhook_secret: e.target.value })} placeholder={info?.stripe.webhook_secret_set ? "•••• leave blank to keep current" : "whsec_..."} className="mt-1 border-[#0F172A]/10 font-mono" autoComplete="off" />
            <p className="mt-1 text-xs text-[#8C9B86]">Needed so paid RSVPs are created instantly even if the guest closes the tab. Create a webhook in Stripe → Developers → Webhooks pointing to <span className="font-mono">https://chabadczechia.cz/api/stripe/webhook</span> (event: <span className="font-mono">checkout.session.completed</span>), then paste its signing secret here.</p>
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <div className="mt-1 flex gap-2">
              {[["test", "Test"], ["live", "Live"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`stripe-mode-${v}`} onClick={() => setStripe({ ...stripe, mode: v })}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${stripe.mode === v ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={saveStripe} disabled={saving} data-testid="save-stripe" className="mt-4 rounded-full bg-[#635BFF] text-white font-semibold px-6 py-2.5 hover:bg-[#4d46d6] transition-colors disabled:opacity-60">{saving ? "Saving…" : "Save Stripe keys"}</button>
        {info?.stripe.source === "db" && (
          <button onClick={() => disconnect("stripe")} disabled={saving} data-testid="disconnect-stripe" className="mt-4 ml-3 rounded-full border border-[#0F172A]/15 text-[#475569] font-semibold px-6 py-2.5 hover:bg-[#F1F5F9] transition-colors disabled:opacity-60">Remove keys</button>
        )}
      </div>

      {/* PayPal */}
      <div data-testid="paypal-settings" className="mt-6 rounded-2xl bg-white border border-[#0F172A]/5 p-6 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-extrabold italic text-[#003087]">Pay<span className="text-[#0070E0]">Pal</span></span>
          </div>
          {info && <StatusBadge ok={info.paypal.secret_set && info.paypal.client_id_set} source={info.paypal.source} />}
        </div>
        <p className="mt-2 text-sm text-[#475569]">Adds a "Pay with PayPal" button for one-time donations and paid event tickets in the currencies you set above. Create a REST app at <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="font-semibold text-[#0070E0] hover:underline">developer.paypal.com → Apps &amp; Credentials</a> to get your Client ID and Secret.</p>
        {info?.paypal.client_id_set && (
          <p className="mt-2 text-xs text-[#475569]">Current Client ID: <span className="font-mono">{info.paypal.client_id_masked}</span></p>
        )}
        <div className="mt-4 grid gap-3">
          <div>
            <Label className="text-xs">Client ID</Label>
            <Input data-testid="paypal-clientid-input" value={paypal.client_id} onChange={(e) => setPaypal({ ...paypal, client_id: e.target.value })} placeholder={info?.paypal.client_id_set ? "•••• leave blank to keep current" : "PayPal Client ID"} className="mt-1 border-[#0F172A]/10 font-mono" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs">Secret</Label>
            <Input type="password" data-testid="paypal-secret-input" value={paypal.secret} onChange={(e) => setPaypal({ ...paypal, secret: e.target.value })} placeholder={info?.paypal.secret_set ? "•••• leave blank to keep current" : "PayPal Secret"} className="mt-1 border-[#0F172A]/10 font-mono" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <div className="mt-1 flex gap-2">
              {[["sandbox", "Sandbox (test)"], ["live", "Live"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`paypal-mode-${v}`} onClick={() => setPaypal({ ...paypal, mode: v })}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${paypal.mode === v ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={savePaypal} disabled={saving} data-testid="save-paypal" className="mt-4 rounded-full bg-[#003087] text-white font-semibold px-6 py-2.5 hover:bg-[#00246b] transition-colors disabled:opacity-60">{saving ? "Saving…" : "Save PayPal keys"}</button>
        {info?.paypal.source === "db" && (
          <button onClick={() => disconnect("paypal")} disabled={saving} data-testid="disconnect-paypal" className="mt-4 ml-3 rounded-full border border-[#0F172A]/15 text-[#475569] font-semibold px-6 py-2.5 hover:bg-[#F1F5F9] transition-colors disabled:opacity-60">Remove keys</button>
        )}
      </div>

      <p className="mt-4 text-xs text-[#94A3B8]">Note: keys are stored in your site's database. Only administrators can view or change them, and secrets are always masked. For maximum security you can instead set them as server environment variables.</p>
    </div>
  );
}

function ProgramEventImages({ cats, onSaved }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const programs = (cats || []);

  const valueFor = (c) => (c.id in drafts ? drafts[c.id] : (c.event_image || ""));

  const save = async (c) => {
    setSaving(c.id);
    try {
      await api.put(`/categories/${c.id}/event-image`, { event_image: valueFor(c) });
      toast.success(`Default event picture saved for “${c.title}”`);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save the picture");
    }
    setSaving(null);
  };

  return (
    <div data-testid="program-event-images" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 overflow-hidden">
      <button type="button" data-testid="toggle-program-images" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F9F8F6] transition-colors">
        <div>
          <p className="font-semibold flex items-center gap-2"><ImageIcon size={16} className="text-[#BE923C]" /> Automatic pictures per program</p>
          <p className="text-sm text-[#475569] mt-0.5">Set a default picture for each program. New events in that program get this picture automatically — you can still change it on any event.</p>
        </div>
        <ChevronDown size={18} className={`text-[#475569] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 grid gap-4 sm:grid-cols-2">
          {programs.map((c) => (
            <div key={c.id} data-testid={`program-image-${c.id}`} className="rounded-xl border border-[#0F172A]/5 p-3">
              <p className="font-medium text-sm mb-2">{c.title}</p>
              <ImageUploadField testid={`program-image-field-${c.id}`} value={valueFor(c)}
                onChange={(v) => setDrafts((d) => ({ ...d, [c.id]: v }))} />
              <button type="button" data-testid={`save-program-image-${c.id}`} onClick={() => save(c)} disabled={saving === c.id}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-xs font-semibold px-4 py-2 disabled:opacity-60">
                {saving === c.id ? "Saving…" : "Save picture"}
              </button>
            </div>
          ))}
          {programs.length === 0 && <p className="text-sm text-[#475569]">No programs yet.</p>}
        </div>
      )}
    </div>
  );
}


function LocationsManager({ locs, cats, reload, onProgramDefaults }) {
  const [open, setOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState({}); // id -> draft label
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try { await api.post("/locations", { label: newLabel.trim(), active: true }); setNewLabel(""); reload(); toast.success("Address added"); }
    catch (e) { toast.error("Could not add the address"); }
    setBusy(false);
  };
  const saveEdit = async (l) => {
    const label = (editing[l.id] ?? l.label).trim();
    if (!label) return;
    try { await api.put(`/locations/${l.id}`, { label, active: l.active !== false }); setEditing((p) => { const n = { ...p }; delete n[l.id]; return n; }); reload(); toast.success("Address updated — applied everywhere"); }
    catch (e) { toast.error("Could not update the address"); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.label}"? Events using it will fall back to their program default or a typed address.`)) return;
    try { await api.delete(`/locations/${l.id}`); reload(); toast.success("Address deleted"); }
    catch (e) { toast.error("Could not delete the address"); }
  };

  return (
    <div data-testid="locations-manager" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 overflow-hidden">
      <button type="button" data-testid="toggle-locations" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F9F8F6] transition-colors">
        <div>
          <p className="font-semibold flex items-center gap-2"><MapPin size={16} className="text-[#BE923C]" /> Saved addresses</p>
          <p className="text-sm text-[#475569] mt-0.5">Keep a list of addresses to pick from on each event. Editing an address here updates it on every event that uses it.</p>
        </div>
        <ChevronDown size={18} className={`text-[#475569] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-3">
          <div className="flex gap-2">
            <Input data-testid="location-new-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="e.g. Chabad House, Old Town Prague" className="border-[#0F172A]/10" />
            <button type="button" data-testid="location-add-btn" onClick={add} disabled={busy || !newLabel.trim()}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 disabled:opacity-60">
              <Plus size={15} /> Add
            </button>
          </div>
          <div className="space-y-2">
            {locs.map((l) => (
              <div key={l.id} data-testid={`location-row-${l.id}`} className="flex items-center gap-2 rounded-xl border border-[#0F172A]/5 p-2.5">
                <Input data-testid={`location-edit-${l.id}`} value={editing[l.id] ?? l.label}
                  onChange={(e) => setEditing((p) => ({ ...p, [l.id]: e.target.value }))}
                  className="flex-1 border-[#0F172A]/10" />
                <button type="button" data-testid={`location-save-${l.id}`} onClick={() => saveEdit(l)}
                  className="shrink-0 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-xs font-semibold px-3.5 py-2 transition-colors">Save</button>
                <button type="button" data-testid={`location-delete-${l.id}`} onClick={() => remove(l)}
                  className="shrink-0 text-[#D24B4B] hover:bg-[#D24B4B]/10 rounded-full p-2 transition-colors"><Trash2 size={15} /></button>
              </div>
            ))}
            {locs.length === 0 && <p className="text-sm text-[#475569]">No saved addresses yet — add one above.</p>}
          </div>

          {cats && cats.length > 0 && (
            <div className="mt-4 rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-3">
              <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><MapPin size={14} className="text-[#9E7829]" /> Default address per program</p>
              <p className="text-xs text-[#475569] mt-0.5 mb-2">New events in a program use this address unless you change it. Existing events with no address set also fall back to it.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {cats.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <span className="text-sm text-[#0F172A] w-40 truncate shrink-0">{c.title}</span>
                    <select data-testid={`program-default-location-${c.id}`} value={c.default_location_id || ""}
                      onChange={(e) => onProgramDefaults(c, e.target.value)}
                      className="flex-1 rounded-md border border-[#0F172A]/10 bg-white px-2 py-1.5 text-sm">
                      <option value="">— None —</option>
                      {locs.filter((l) => l.active !== false).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/events?admin=1").then((r) => setEvents(r.data));
  const [cats, setCats] = useState([]);
  const [locs, setLocs] = useState([]);
  const loadLocs = () => api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
  const [holidays, setHolidays] = useState([]);
  const [emailDefaults, setEmailDefaults] = useState({ en: {}, he: {}, ru: {} });
  const [emailLang, setEmailLang] = useState("en");
  const [showEmails, setShowEmails] = useState(false);
  const [eventTestEmail, setEventTestEmail] = useState("");
  const [testingEventType, setTestingEventType] = useState(null);
  const [addingLoc, setAddingLoc] = useState(false);
  const [newLocDraft, setNewLocDraft] = useState("");
  const [showPastAdmin, setShowPastAdmin] = useState(false);
  const todayKey = new Date().toISOString().slice(0, 10);
  const upcomingAdminEvents = events.filter((ev) => !ev.date || ev.date >= todayKey);
  const pastAdminEvents = events.filter((ev) => ev.date && ev.date < todayKey).sort((a, b) => b.date.localeCompare(a.date));
  const renderEventRow = (ev) => (
    <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
      <img src={resolveImg(ev.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{ev.title}</p>
        <p className="text-sm text-[#475569]">{euDate(ev.date)} {ev.time} · {ev.category}</p>
        {ev.sold_out && (
          <span data-testid={`admin-event-soldout-badge-${ev.id}`} className="mt-1 me-1 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-[11px] font-semibold">Fully booked</span>
        )}
        {ev.auto_approve && (
          <span data-testid={`admin-event-auto-badge-${ev.id}`} className="mt-1 me-1 inline-flex items-center gap-1 rounded-full bg-[#8C9B86]/15 text-[#4f5f4a] px-2 py-0.5 text-[11px] font-semibold">Instant confirm</span>
        )}
        {ev.unlisted && (
          <span data-testid={`admin-event-unlisted-badge-${ev.id}`} className="mt-1 me-1 inline-flex items-center gap-1 rounded-full bg-[#FBF3E2] text-[#9E7829] px-2 py-0.5 text-[11px] font-semibold">Hidden · link only</span>
        )}
        {ev.students_only && (
          <span data-testid={`admin-event-students-badge-${ev.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#0F172A]/8 text-[#0F172A] px-2 py-0.5 text-[11px] font-semibold">Students only</span>
        )}
      </div>
      {ev.unlisted && !ev.students_only && (
        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/events/${ev.slug || ev.id}`); toast.success("Event link copied"); }} data-testid={`copy-event-link-${ev.id}`} title="Copy event link" className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#BE923C]"><Link2 size={16} /></button>
      )}
      {ev.students_only && ev.access_key && (
        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/events/${ev.slug || ev.id}?key=${ev.access_key}`); toast.success("Student link copied"); }} data-testid={`copy-student-link-${ev.id}`} title="Copy student link" className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#BE923C]"><Lock size={16} /></button>
      )}
      <button onClick={() => duplicate(ev)} data-testid={`duplicate-event-${ev.id}`} title="Duplicate" className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Copy size={16} /></button>
      <button onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
      <button onClick={() => remove(ev.id)} data-testid={`delete-event-${ev.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
    </div>
  );
  const { user } = useAuth();
  useEffect(() => {
    load();
    loadLocs();
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/holidays").then((r) => setHolidays(r.data));
    api.get("/email-templates").then((r) => {
      // Effective global template = factory code defaults with the admin's saved
      // GLOBAL overrides (r.data.default) merged on top, so the event editor pre-fills
      // and diffs against the user's actual saved wording, not the factory text.
      const fac = r.data.defaults || {};
      const glob = r.data.default || {};
      const merged = {};
      for (const lg of ["en", "cs", "he", "ru"]) {
        const ov = Object.fromEntries(Object.entries(glob[lg] || {}).filter(([, v]) => typeof v === "string" && v.trim()));
        merged[lg] = { ...(fac[lg] || {}), ...ov };
      }
      setEmailDefaults(merged);
    }).catch(() => {});
  }, []);
  useEffect(() => { if (user?.email && !eventTestEmail) setEventTestEmail(user.email); }, [user]); // eslint-disable-line

  const copyQuestions = (srcId) => {
    const src = events.find((e) => e.id === srcId);
    if (!src) return;
    const copied = (src.custom_questions || []).map((q) => ({ ...q, id: crypto.randomUUID() }));
    setForm((prev) => ({ ...prev, custom_questions: copied }));
    toast.success(`Copied ${copied.length} question${copied.length === 1 ? "" : "s"} from “${src.title}”`);
  };
  const setEventEmail = (key, value) => setForm((prev) => ({
    ...prev,
    email_i18n: { ...(prev.email_i18n || {}), [emailLang]: { ...((prev.email_i18n || {})[emailLang] || {}), [key]: value } },
  }));
  // Pre-fill every email field with the current effective wording (the saved override,
  // else the default template) so the admin edits the text in place instead of a blank box.
  const seedEmailI18n = (existing) => {
    const out = {};
    for (const lg of ["en", "cs", "he", "ru"]) {
      const defs = emailDefaults[lg] || {};
      const row = {};
      for (const g of EMAIL_GROUPS) for (const [k] of g.fields) {
        const ov = existing?.[lg]?.[k];
        row[k] = (ov != null && String(ov) !== "") ? ov : ((defs[k]) ?? (emailDefaults.en?.[k]) ?? "");
      }
      out[lg] = row;
    }
    return out;
  };
  const sendEventTest = async (type) => {
    if (!eventTestEmail.trim()) { toast.error("Enter an email address to send the test to"); return; }
    setTestingEventType(type);
    try {
      const r = await api.post("/email-templates/test", {
        type, lang: emailLang, category: form.category || "",
        to_email: eventTestEmail.trim(), use_stored: true,
        event_email: form.email_i18n || {},
        event: { title: form.title, date: form.date, time: form.time, location: (form.location_id ? (locs.find((l) => l.id === form.location_id)?.label || "") : (form.location || "")) },
      });
      if (r.data.email_enabled) toast.success(`Test email sent to ${r.data.sent_to}`);
      else toast.warning("Email delivery isn't configured yet — nothing was sent.");
    } catch (e) {
      toast.error("Could not send the test email");
    }
    setTestingEventType(null);
  };

  const openNew = () => { setAddingLoc(false); setNewLocDraft(""); setForm({ ...emptyEvent, email_i18n: seedEmailI18n({}) }); setEditId(null); setOpen(true); };
  const openEdit = (ev) => { setAddingLoc(false); setNewLocDraft(""); const anyPrice = (Number(ev.price_adult) || Number(ev.price_per_person) || Number(ev.price_child) || Number(ev.price_family)) > 0; setForm({ ...emptyEvent, ...ev, location: ev.location_custom ?? "", location_id: ev.location_id || "", payment_mode: ev.payment_mode || (anyPrice ? "required" : "none"), price_adult: Number(ev.price_adult) || Number(ev.price_per_person) || 0, price_child: Number(ev.price_child) || 0, email_i18n: seedEmailI18n(ev.email_i18n) }); setEditId(ev.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    // Only keep event email overrides that are non-empty AND differ from the default,
    // so untouched pre-filled fields still inherit the general/default template.
    const cleanEmail = {};
    for (const lg of ["en", "cs", "he", "ru"]) {
      const rowIn = form.email_i18n?.[lg] || {};
      const o = {};
      for (const [k, v] of Object.entries(rowIn)) {
        const def = (emailDefaults[lg]?.[k]) ?? (emailDefaults.en?.[k]) ?? "";
        if (typeof v === "string" && v.trim() && v !== def) o[k] = v;
      }
      if (Object.keys(o).length) cleanEmail[lg] = o;
    }
    const payload = { ...form, email_i18n: cleanEmail, capacity: Number(form.capacity) || 0, suggested_amounts: (form.suggested_amounts || []).map(Number).filter((n) => n > 0) };
    delete payload.id; delete payload.created_at;
    try {
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(editId ? "Event updated" : "Event created");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event deleted"); load();
  };

  const duplicate = (ev) => {
    const anyPrice = (Number(ev.price_adult) || Number(ev.price_per_person) || Number(ev.price_child) || Number(ev.price_family)) > 0;
    const { id, created_at, ...rest } = ev;
    setForm({ ...emptyEvent, ...rest, location: ev.location_custom ?? "", location_id: ev.location_id || "", title: `${ev.title} (copy)`, date: "", payment_mode: ev.payment_mode || (anyPrice ? "required" : "none"), price_adult: Number(ev.price_adult) || Number(ev.price_per_person) || 0, price_child: Number(ev.price_child) || 0 });
    setEditId(null); setOpen(true);
    toast.info("Duplicated — set a new date and save");
  };

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-event-btn" onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Event
        </button>
      }>Events</SectionTitle>

      <ProgramEventImages cats={cats} onSaved={() => api.get("/categories").then((r) => setCats(r.data))} />

      <LocationsManager
        locs={locs}
        cats={cats}
        reload={loadLocs}
        onProgramDefaults={async (c, locId) => {
          try {
            await api.put(`/categories/${c.id}/default-location`, { default_location_id: locId });
            api.get("/categories").then((r) => setCats(r.data));
            toast.success(`Default address ${locId ? "set" : "cleared"} for “${c.title}”`);
          } catch (e) { toast.error("Could not save the program default"); }
        }}
      />

      <div className="space-y-3">
        {upcomingAdminEvents.map((ev) => renderEventRow(ev))}
        {upcomingAdminEvents.length === 0 && <p className="text-[#475569]">No upcoming events.</p>}
      </div>

      {pastAdminEvents.length > 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-[#0F172A]/15 p-4" data-testid="admin-past-events-box">
          <button type="button" onClick={() => setShowPastAdmin((v) => !v)} data-testid="admin-past-events-toggle"
            className="w-full flex items-center justify-between gap-3 text-left">
            <span className="text-sm text-[#475569]">
              <b className="text-[#0F172A]">{pastAdminEvents.length} past event{pastAdminEvents.length === 1 ? "" : "s"}</b> filed away — RSVPs live in the <b>Past events</b> tab. Expand here only to duplicate, edit or delete.
            </span>
            <ChevronDown size={18} className={`shrink-0 text-[#475569] transition-transform ${showPastAdmin ? "rotate-180" : ""}`} />
          </button>
          {showPastAdmin && <div className="mt-4 space-y-3 opacity-80">{pastAdminEvents.map((ev) => renderEventRow(ev))}</div>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Event</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input data-testid="event-field-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            {editId && (
              <div>
                <Label>Web address (link)</Label>
                <div className="mt-1.5 flex items-center rounded-md border border-[#0F172A]/10 bg-white overflow-hidden focus-within:border-[#BE923C]">
                  <span className="pl-3 pr-1 py-2 text-sm text-[#94A3B8] select-none whitespace-nowrap">/events/</span>
                  <input data-testid="event-field-slug" value={form.slug || ""}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="auto-generated from title"
                    className="flex-1 py-2 pr-3 text-sm outline-none bg-transparent" />
                </div>
                <p className="mt-1 text-xs text-[#475569]">This is the shareable link for the event. Leave blank to auto-create it from the title. Spaces and symbols become hyphens; must be unique (a date is added if two events share a title).</p>
              </div>
            )}
            <div>
              <Label>Primary program</Label>
              <select data-testid="event-field-category" value={form.category} onChange={(e) => {
                  const title = e.target.value;
                  const cat = cats.find((c) => c.title === title);
                  const dflt = cat ? (cat.event_image || cat.image_url || "") : "";
                  setForm((prev) => ({
                    ...prev,
                    category: title,
                    image_url: prev.image_url || (prev.flyer_url ? "" : dflt),
                    // Pre-fill this program's default location on a fresh event (still editable).
                    ...((!prev.location_id && !prev.location && cat?.default_location_id) ? { location_id: cat.default_location_id } : {}),
                  }));
                }}
                className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                <option value="">Select a program…</option>
                {cats.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
              </select>
              <p className="mt-1 text-xs text-[#475569]">Sets the auto card image. This event also appears on this program's page.</p>
            </div>
            <div>
              <Label>Also show on these program pages</Label>
              <p className="mt-0.5 mb-2 text-xs text-[#475569]">Tick any extra programs where this event should also appear (e.g. Rosh Hashanah in both Children's Programs and Holidays & Shabbat).</p>
              <div data-testid="event-field-categories" className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl border border-[#0F172A]/10 p-3 max-h-44 overflow-y-auto">
                {cats.filter((c) => c.title !== form.category).map((c) => {
                  const checked = (form.categories || []).includes(c.title);
                  return (
                    <label key={c.id} data-testid={`event-extra-cat-${c.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={checked} className="accent-[#BE923C] w-4 h-4"
                        onChange={(e) => setForm((prev) => {
                          const set = new Set(prev.categories || []);
                          if (e.target.checked) set.add(c.title); else set.delete(c.title);
                          return { ...prev, categories: Array.from(set) };
                        })} />
                      <span className="truncate">{c.title}</span>
                    </label>
                  );
                })}
                {cats.length === 0 && <p className="text-xs text-[#475569]">No programs yet.</p>}
              </div>
            </div>
            <div>
              <Label>Holiday (optional)</Label>
              <select data-testid="event-field-holiday" value={form.holiday} onChange={(e) => setForm({ ...form, holiday: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                <option value="">Not a holiday event</option>
                {holidays.map((h) => <option key={h.id} value={h.name}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Card image (events list)</Label>
              <div className="mt-1.5"><ImageUploadField testid="event-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div>
              <p className="mt-1 text-xs text-[#475569]">Shown in the events grid, cropped to a landscape card. Optional — falls back to the flyer.</p>
            </div>
            <div>
              <Label>Flyer (event page)</Label>
              <div className="mt-1.5"><ImageUploadField testid="event-flyer-image" value={form.flyer_url} onChange={(v) => setForm({ ...form, flyer_url: v })} /></div>
              <p className="mt-1 text-xs text-[#475569]">Shown full/uncropped on the event page with the RSVP form — great for portrait flyers. Optional — falls back to the card image.</p>
              <div className="mt-3 rounded-lg border border-dashed border-[#0F172A]/15 p-2.5" data-testid="event-flyer-langs">
                <p className="text-xs text-[#475569] mb-2">Language versions (optional) — visitors see the flyer matching their language; the flyer above is the fallback (English).</p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {[["cs", "Czech"], ["he", "Hebrew"], ["ru", "Russian"]].map(([lg, name]) => (
                    <div key={lg}>
                      <Label className="text-[11px] text-[#475569]">{name}</Label>
                      <div className="mt-1"><ImageUploadField testid={`event-flyer-image-${lg}`} value={(form.flyer_i18n || {})[lg] || ""} onChange={(v) => setForm({ ...form, flyer_i18n: { ...(form.flyer_i18n || {}), [lg]: v } })} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input data-testid="event-field-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Time</Label><Input data-testid="event-field-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Capacity</Label><Input data-testid="event-field-capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div className="rounded-xl border border-[#0F172A]/10 p-4">
              <Label>Close RSVPs at <span className="font-normal text-[#94A3B8]">(optional)</span></Label>
              <Input data-testid="event-field-rsvp_close_at" type="datetime-local" value={form.rsvp_close_at || ""} onChange={(e) => setForm({ ...form, rsvp_close_at: e.target.value })} className="mt-1.5 border-[#0F172A]/10 max-w-xs" />
              <p className="mt-1.5 text-xs text-[#475569]">When this time passes (Prague time), the event stays visible but its RSVP form is closed with a "Reservations are closed" notice. Leave empty to keep RSVPs open until the event date.</p>
              {form.rsvp_close_at && (
                <button type="button" data-testid="event-field-rsvp_close_at-clear" onClick={() => setForm({ ...form, rsvp_close_at: "" })} className="mt-2 text-xs font-semibold text-[#BE5B4C] hover:underline">Clear</button>
              )}
            </div>
            <div>
              <Label>Location</Label>
              <select
                data-testid="event-field-location-select"
                value={form.location_id || (form.location ? "__custom__" : "")}
                disabled={form.hide_location}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__new__") {
                    setNewLocDraft("");
                    setAddingLoc(true);
                  } else if (v === "__custom__") {
                    setAddingLoc(false);
                    setForm((p) => ({ ...p, location_id: "", location: p.location || "" }));
                  } else if (v === "") {
                    setAddingLoc(false);
                    setForm((p) => ({ ...p, location_id: "", location: "" }));
                  } else {
                    setAddingLoc(false);
                    setForm((p) => ({ ...p, location_id: v, location: "" }));
                  }
                }}
                className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="">Select an address…</option>
                {locs.filter((l) => l.active !== false).map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
                <option value="__custom__">✎ Custom address (type below)</option>
                <option value="__new__">＋ Add a new saved address…</option>
              </select>
              {addingLoc && (
                <div className="mt-2 flex gap-2 rounded-lg bg-[#FBF7EE] border border-[#BE923C]/30 p-2.5">
                  <Input data-testid="event-new-location-input" autoFocus value={newLocDraft}
                    onChange={(e) => setNewLocDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="event-new-location-save"]')?.click(); } }}
                    placeholder="New address (one line)…" className="border-[#0F172A]/10 bg-white" />
                  <button type="button" data-testid="event-new-location-save"
                    onClick={() => {
                      if (!newLocDraft.trim()) return;
                      api.post("/locations", { label: newLocDraft.trim(), active: true })
                        .then((r) => { loadLocs(); setForm((p) => ({ ...p, location_id: r.data.id, location: "" })); setAddingLoc(false); setNewLocDraft(""); toast.success("Address saved"); })
                        .catch(() => toast.error("Could not save the address"));
                    }}
                    className="shrink-0 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-xs font-semibold px-4 py-2 transition-colors">Save</button>
                  <button type="button" data-testid="event-new-location-cancel" onClick={() => { setAddingLoc(false); setNewLocDraft(""); }}
                    className="shrink-0 rounded-full text-[#475569] hover:bg-[#0F172A]/5 text-xs font-semibold px-3 py-2 transition-colors">Cancel</button>
                </div>
              )}
              {!form.location_id && !addingLoc && (
                <Input data-testid="event-field-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 border-[#0F172A]/10" disabled={form.hide_location} placeholder={form.hide_location ? "Hidden — shared upon RSVP" : "Type a one-off address…"} />
              )}
              <p className="mt-1 text-xs text-[#475569]">Pick a saved address (edits to it update everywhere), type a one-off, or add a new one. Leave blank to use this program's default.</p>
            </div>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-hide_location" checked={form.hide_location} onChange={(e) => setForm({ ...form, hide_location: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
              Hide location (show "shared upon RSVP")
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-rsvp_via_whatsapp" checked={form.rsvp_via_whatsapp} onChange={(e) => setForm({ ...form, rsvp_via_whatsapp: e.target.checked, no_reservation: e.target.checked ? false : form.no_reservation })} className="w-4 h-4 accent-[#BE923C]" />
              RSVP via WhatsApp (hide the form, show a WhatsApp button)
            </label>
            {form.rsvp_via_whatsapp && (
              <div>
                <Label>WhatsApp number (with country code, e.g. +420123456789)</Label>
                <Input data-testid="event-field-whatsapp_number" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="+420 123 456 789" />
              </div>
            )}
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-no_reservation" checked={form.no_reservation} onChange={(e) => setForm({ ...form, no_reservation: e.target.checked, rsvp_via_whatsapp: e.target.checked ? false : form.rsvp_via_whatsapp, use_child_signup: e.target.checked ? false : form.use_child_signup })} className="w-4 h-4 accent-[#BE923C]" />
              No reservation necessary (show a "welcome to join" note + WhatsApp contact button for location &amp; info)
            </label>
            {form.no_reservation && (
              <div>
                <Label>WhatsApp number for this event (optional — defaults to the site's main number)</Label>
                <Input data-testid="event-field-no_reservation-whatsapp" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="+420 123 456 789" />
              </div>
            )}
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-sold_out" checked={!!form.sold_out} onChange={(e) => setForm({ ...form, sold_out: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
              Mark as fully booked (event stays visible with a “Fully booked” badge; the RSVP form is closed)
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-auto_approve" checked={!!form.auto_approve} onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
              No approval needed — confirm RSVPs instantly (guest sees their details on screen and gets the confirmation email right away; card payments are charged immediately)
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-unlisted" checked={!!form.unlisted} onChange={(e) => setForm({ ...form, unlisted: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
              Hide from the website (unlisted — not shown in Upcoming events, the Events page or program pages; reachable only via its direct link)
            </label>
            {form.unlisted && (
              <p className="ms-7 -mt-1 text-xs text-[#9E7829]" data-testid="event-unlisted-note">
                Share this link: <code className="bg-[#FBF3E2] px-1.5 py-0.5 rounded">{window.location.origin}/events/{form.slug || "<slug>"}</code>
              </p>
            )}
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-students_only" checked={!!form.students_only} onChange={(e) => setForm({ ...form, students_only: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
              Students only (private — hide the details &amp; RSVP behind a private link)
            </label>
            {form.students_only && (
              <div className="rounded-2xl bg-[#F4F1EA] border border-[#BE923C]/30 p-4 space-y-3">
                <p className="text-xs text-[#475569]">On the public website this event shows as a locked “Students only” card. Only people who open the private link below can see the details and RSVP. Forward the link to your students (e.g. on WhatsApp).</p>
                <div>
                  <Label className="text-xs">Contact WhatsApp number for the “students only” popup (optional — defaults to the site's main number)</Label>
                  <Input data-testid="event-field-students_contact_number" value={form.students_contact_number || ""} onChange={(e) => setForm({ ...form, students_contact_number: e.target.value })} className="mt-1 border-[#0F172A]/10" placeholder="+420 123 456 789" />
                </div>
                {editId && form.access_key ? (
                  <div>
                    <Label className="text-xs">Private student link</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input data-testid="student-link-value" readOnly value={`${window.location.origin}/events/${form.slug || editId}?key=${form.access_key}`}
                        className="flex-1 rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-xs font-mono text-[#475569] truncate" />
                      <button type="button" data-testid="copy-student-link"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/events/${form.slug || editId}?key=${form.access_key}`); toast.success("Student link copied"); }}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white text-xs font-semibold px-4 py-2 transition-colors">
                        <Copy size={13} /> Copy
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[#9E7829]">Save the event first to generate the private student link.</p>
                )}
              </div>
            )}
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-use_child_signup" checked={form.use_child_signup} onChange={(e) => setForm({ ...form, use_child_signup: e.target.checked, rsvp_via_whatsapp: e.target.checked ? false : form.rsvp_via_whatsapp, no_reservation: e.target.checked ? false : form.no_reservation })} className="w-4 h-4 accent-[#BE923C]" />
              Use the Children's Program sign-up (hide the RSVP form, show a sign-up button)
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
              <input type="checkbox" data-testid="event-field-wa_group_in_email" checked={form.wa_group_in_email !== false} onChange={(e) => setForm({ ...form, wa_group_in_email: e.target.checked })} className="w-4 h-4 accent-[#25D366]" />
              Include the "join our WhatsApp group" button in this event's approval email
            </label>
            <div className="rounded-2xl bg-[#F0FAF3] border border-[#25D366]/30 p-4 space-y-3">
              <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer">
                <input type="checkbox" data-testid="event-field-wa_button_enabled" checked={form.wa_button_enabled} onChange={(e) => setForm({ ...form, wa_button_enabled: e.target.checked })} className="w-4 h-4 accent-[#25D366]" />
                Show a WhatsApp button below the RSVP form (in addition to the form)
              </label>
              {form.wa_button_enabled && (
                <div className="grid gap-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Button text (English)</Label>
                      <button type="button" data-testid="wa-button-suggest" onClick={async () => {
                        if (!form.wa_button_text.trim()) { toast.error("Enter the English text first"); return; }
                        try {
                          const { data } = await api.post("/translate", { fields: { wa_button_text: form.wa_button_text }, targets: ["cs", "he", "ru"] });
                          setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}),
                            cs: { ...((f.i18n || {}).cs || {}), wa_button_text: data.cs?.wa_button_text || "" },
                            he: { ...((f.i18n || {}).he || {}), wa_button_text: data.he?.wa_button_text || "" },
                            ru: { ...((f.i18n || {}).ru || {}), wa_button_text: data.ru?.wa_button_text || "" } } }));
                          toast.success("Translations suggested — review & save");
                        } catch { toast.error("Could not translate"); }
                      }} className="inline-flex items-center gap-1 rounded-full bg-[#BE923C] text-white text-[11px] font-semibold px-2.5 py-1 hover:bg-[#9E7829]"><Sparkles size={11} /> Suggest</button>
                    </div>
                    <Input data-testid="event-field-wa_button_text" value={form.wa_button_text} onChange={(e) => setForm({ ...form, wa_button_text: e.target.value })} className="mt-1 border-[#0F172A]/10" placeholder="e.g. RSVP directly via WhatsApp" />
                    <Input data-testid="event-field-wa_button_text-cs" value={form.i18n?.cs?.wa_button_text || ""} onChange={(e) => setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}), cs: { ...((f.i18n || {}).cs || {}), wa_button_text: e.target.value } } }))} className="mt-2 border-[#0F172A]/10" placeholder="Czech · Čeština" />
                    <Input data-testid="event-field-wa_button_text-he" dir="rtl" value={form.i18n?.he?.wa_button_text || ""} onChange={(e) => setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}), he: { ...((f.i18n || {}).he || {}), wa_button_text: e.target.value } } }))} className="mt-2 border-[#0F172A]/10 text-right" placeholder="Hebrew · עברית" />
                    <Input data-testid="event-field-wa_button_text-ru" value={form.i18n?.ru?.wa_button_text || ""} onChange={(e) => setForm((f) => ({ ...f, i18n: { ...(f.i18n || {}), ru: { ...((f.i18n || {}).ru || {}), wa_button_text: e.target.value } } }))} className="mt-2 border-[#0F172A]/10" placeholder="Russian · Русский" />
                  </div>
                  <div>
                    <Label className="text-xs">WhatsApp number (optional — defaults to the site's main number)</Label>
                    <Input data-testid="event-field-wa_button_number" value={form.wa_button_number} onChange={(e) => setForm({ ...form, wa_button_number: e.target.value })} className="mt-1 border-[#0F172A]/10" placeholder="+420 123 456 789" />
                  </div>
                </div>
              )}
            </div>
            {form.use_child_signup && (
              <div className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE] p-3">
                <Label>Which program should the sign-up button default to?</Label>
                <select data-testid="event-field-child_signup_program" value={form.child_signup_program} onChange={(e) => setForm({ ...form, child_signup_program: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                  <option value="sunday">Sunday Hebrew School</option>
                  <option value="thursday">Thursday After School</option>
                </select>
              </div>
            )}
            {!form.rsvp_via_whatsapp && !form.use_child_signup && !form.no_reservation && (
              <div className="rounded-2xl bg-white border border-[#0F172A]/10 p-4 space-y-2.5">
                <p className="text-sm font-semibold text-[#0F172A]">Standard RSVP questions</p>
                <p className="text-xs text-[#475569] -mt-1">Name is always asked. Tick which of the rest to show (e.g. hide “children” for adults-only events).</p>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="event-field-show_email" checked={form.rsvp_show_email !== false} onChange={(e) => setForm({ ...form, rsvp_show_email: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                  Ask for email
                </label>
                {form.rsvp_show_email !== false ? (
                  <label className="flex items-center gap-3 text-sm cursor-pointer ms-6">
                    <input type="checkbox" data-testid="event-field-email_required" checked={form.rsvp_email_required !== false} onChange={(e) => setForm({ ...form, rsvp_email_required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                    Email is required
                  </label>
                ) : (
                  <p className="ms-6 text-xs text-[#9E7829]" data-testid="event-email-hidden-note">Guests without an email won't receive confirmation or approval emails.</p>
                )}
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="event-field-show_phone" checked={form.rsvp_show_phone !== false} onChange={(e) => setForm({ ...form, rsvp_show_phone: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                  Ask for phone number
                </label>
                {form.rsvp_show_phone !== false && (
                  <label className="flex items-center gap-3 text-sm cursor-pointer ms-6">
                    <input type="checkbox" data-testid="event-field-phone_required" checked={form.rsvp_phone_required !== false} onChange={(e) => setForm({ ...form, rsvp_phone_required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                    Phone is required
                  </label>
                )}
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="event-field-show_adults" checked={form.rsvp_show_adults !== false} onChange={(e) => setForm({ ...form, rsvp_show_adults: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                  Ask for number of adults
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="event-field-show_children" checked={form.rsvp_show_children !== false} onChange={(e) => setForm({ ...form, rsvp_show_children: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                  Ask for number of children
                </label>
              </div>
            )}
            {!form.rsvp_via_whatsapp && !form.use_child_signup && !form.no_reservation && (
              <EventPartsEditor parts={form.parts || []} onChange={(parts) => setForm({ ...form, parts })} />
            )}
            {!form.rsvp_via_whatsapp && !form.use_child_signup && !form.no_reservation && (
              <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4 space-y-3">
                <p className="text-sm font-semibold text-[#9E7829]">Payment on this RSVP form</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[["none", "No payment", "Free RSVP"], ["required", "Required payment", "Guests must pay to register"], ["suggested", "Suggested participation", "Optional — guest can change or skip"]].map(([val, label, desc]) => (
                    <button key={val} type="button" data-testid={`payment-mode-${val}`} onClick={() => setForm({ ...form, payment_mode: val })}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${form.payment_mode === val ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                      <span className="block text-sm font-semibold">{label}</span>
                      <span className={`block text-xs ${form.payment_mode === val ? "text-white/85" : "text-[#475569]"}`}>{desc}</span>
                    </button>
                  ))}
                </div>
                {form.payment_mode !== "none" && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>{form.payment_mode === "suggested" ? "Adult (suggested)" : "Adult price"}</Label>
                        <Input data-testid="event-field-price_adult" type="number" min={0} step="1" value={form.price_adult}
                          onChange={(e) => setForm({ ...form, price_adult: Number(e.target.value) || 0, price_per_person: Number(e.target.value) || 0 })} className="mt-1.5 border-[#0F172A]/10" />
                      </div>
                      <div>
                        <Label>{form.payment_mode === "suggested" ? "Child (suggested)" : "Child price"}</Label>
                        <Input data-testid="event-field-price_child" type="number" min={0} step="1" value={form.price_child}
                          onChange={(e) => setForm({ ...form, price_child: Number(e.target.value) || 0 })} className="mt-1.5 border-[#0F172A]/10" />
                      </div>
                      <div>
                        <Label>{form.payment_mode === "suggested" ? "Family (suggested)" : "Family flat price"}</Label>
                        <Input data-testid="event-field-price_family" type="number" min={0} step="1" value={form.price_family}
                          onChange={(e) => setForm({ ...form, price_family: Number(e.target.value) || 0 })} className="mt-1.5 border-[#0F172A]/10" />
                      </div>
                    </div>
                    <p className="text-xs text-[#475569]">
                      {form.payment_mode === "suggested"
                        ? "These are the SUGGESTED contribution amounts (per adult / child, or a family flat rate). Guests see them pre-filled with quick presets, can change the amount, or RSVP without contributing. Uses the events currency from the Payments tab."
                        : "Set per-head prices (adult / child), a flat family rate, or both (guest chooses). Guests pay via Stripe or PayPal before the RSVP is confirmed. Uses the events currency from the Payments tab."}
                    </p>
                    {form.payment_mode === "suggested" && (
                      <div className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE] p-4 space-y-3" data-testid="suggested-amounts-editor">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-semibold">Suggested amount buttons</Label>
                          <button type="button" data-testid="add-suggested-amount"
                            onClick={() => setForm((prev) => ({ ...prev, suggested_amounts: [...(prev.suggested_amounts || []), 0] }))}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#BE923C]/40 bg-white px-3 py-1.5 text-xs font-semibold hover:border-[#BE923C]"><Plus size={14} /> Add amount</button>
                        </div>
                        <p className="text-xs text-[#475569]">Quick-pick contribution buttons shown on the RSVP form. Add as many as you like — each becomes its own button, and the first one is pre-selected. Leave empty to auto-generate presets from the amounts above.</p>
                        <label className="flex items-center gap-2.5 text-sm text-[#0F172A] cursor-pointer pt-1">
                          <input type="checkbox" data-testid="event-field-hide_suggested_note" checked={!!form.hide_suggested_note} onChange={(e) => setForm({ ...form, hide_suggested_note: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                          Hide the “Your contribution helps us…” line (show only “Suggested participation”)
                        </label>
                        {(form.suggested_amounts || []).length > 0 && (
                          <div className="space-y-2">
                            {(form.suggested_amounts || []).map((amt, i) => (
                              <div key={i} className="flex items-center gap-2" data-testid={`suggested-amount-row-${i}`}>
                                <Input type="number" min={0} step="1" data-testid={`suggested-amount-${i}`} value={amt}
                                  onChange={(e) => setForm((prev) => { const arr = [...(prev.suggested_amounts || [])]; arr[i] = Number(e.target.value) || 0; return { ...prev, suggested_amounts: arr }; })}
                                  className="max-w-[160px] border-[#0F172A]/10 bg-white" />
                                <button type="button" data-testid={`remove-suggested-amount-${i}`}
                                  onClick={() => setForm((prev) => ({ ...prev, suggested_amounts: (prev.suggested_amounts || []).filter((_, j) => j !== i) }))}
                                  className="p-2 rounded-lg text-[#B4573B] hover:bg-[#B4573B]/10"><Trash2 size={16} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="rounded-xl border border-[#0F172A]/10 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Custom questions for this event</Label>
                    <button type="button" data-testid="add-event-question" onClick={() => setForm((prev) => ({ ...prev, custom_questions: [...(prev.custom_questions || []), { id: crypto.randomUUID(), label: "", type: "text", required: false, options: [] }] }))}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 px-3 py-1.5 text-xs font-semibold hover:border-[#BE923C]"><Plus size={14} /> Add question</button>
                  </div>
                  <p className="text-xs text-[#475569]">These appear only on this event's RSVP form, in addition to the global questions.</p>
                  {events.filter((e) => e.id !== editId && (e.custom_questions || []).length > 0).length > 0 && (
                    <div className="flex items-center gap-2">
                      <Copy size={14} className="text-[#9E7829] shrink-0" />
                      <select data-testid="copy-questions-select" defaultValue=""
                        onChange={(e) => { if (e.target.value) { copyQuestions(e.target.value); e.target.value = ""; } }}
                        className="flex-1 rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-xs">
                        <option value="">Copy questions from another event…</option>
                        {events.filter((e) => e.id !== editId && (e.custom_questions || []).length > 0).map((e) => (
                          <option key={e.id} value={e.id}>{e.title} ({(e.custom_questions || []).length})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(form.custom_questions || []).map((q) => (
                    <EventQuestionEditor key={q.id} q={q}
                      onUpdate={(patch) => setForm((prev) => ({ ...prev, custom_questions: prev.custom_questions.map((x) => x.id === q.id ? { ...x, ...patch } : x) }))}
                      onRemove={() => setForm((prev) => ({ ...prev, custom_questions: prev.custom_questions.filter((x) => x.id !== q.id) }))}
                    />
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Description</Label>
              <div className="mt-1.5"><RichTextEditor testid="event-field-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} /></div>
            </div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "title", label: "Title" },
              { field: "location", label: "Location" },
            ]} />
            <details className="rounded-xl border border-[#0F172A]/10 p-3">
              <summary className="cursor-pointer text-sm font-semibold text-[#475569]">Description translations (Czech / Hebrew / Russian) — optional</summary>
              <div className="mt-3 space-y-3">
                <div dir="ltr" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
                  <p className="text-sm font-serif font-semibold text-[#9E7829]">Čeština · Czech</p>
                  <RichTextEditor testid="event-desc-cs" value={form.i18n?.cs?.description || ""} onChange={(v) => setForm({ ...form, i18n: { ...(form.i18n || {}), cs: { ...(form.i18n?.cs || {}), description: v } } })} />
                </div>
                <div dir="rtl" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
                  <p className="text-sm font-serif font-semibold text-[#9E7829]" dir="ltr">עברית · Hebrew</p>
                  <RichTextEditor testid="event-desc-he" value={form.i18n?.he?.description || ""} onChange={(v) => setForm({ ...form, i18n: { ...(form.i18n || {}), he: { ...(form.i18n?.he || {}), description: v } } })} />
                </div>
                <div dir="ltr" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-2">
                  <p className="text-sm font-serif font-semibold text-[#9E7829]">Русский · Russian</p>
                  <RichTextEditor testid="event-desc-ru" value={form.i18n?.ru?.description || ""} onChange={(v) => setForm({ ...form, i18n: { ...(form.i18n || {}), ru: { ...(form.i18n?.ru || {}), description: v } } })} />
                </div>
              </div>
            </details>
            <div className="rounded-2xl border border-[#0F172A]/10 p-4">
              <button type="button" data-testid="toggle-event-emails" onClick={() => setShowEmails((v) => !v)}
                className="flex items-center justify-between w-full text-left">
                <span className="text-sm font-semibold text-[#0F172A]">Custom emails for this event (optional)</span>
                <ChevronDown size={18} className={`text-[#475569] transition-transform ${showEmails ? "rotate-180" : ""}`} />
              </button>
              {showEmails && (
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-[#475569]">Leave a box blank to use the general or program template. Anything you type here overrides those for this event only. {EMAIL_PLACEHOLDER_HINT}</p>
                  <LangTabs lang={emailLang} setLang={setEmailLang} prefix="event-email" />
                  <div className="flex flex-col gap-2 rounded-lg bg-[#FBFAF8] border border-[#0F172A]/10 p-3">
                    <div>
                      <Label className="text-xs text-[#475569]">Send a test email to</Label>
                      <Input data-testid="event-email-test-recipient" type="email" value={eventTestEmail} onChange={(e) => setEventTestEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 border-[#0F172A]/10 w-full" />
                    </div>
                    <p className="text-xs text-[#475569]">Uses this event's real details + your overrides on top of the saved templates.</p>
                  </div>
                  <EmailTemplateFields lang={emailLang} idPrefix="event-email"
                    getVal={(k) => (form.email_i18n?.[emailLang]?.[k]) ?? ""}
                    setVal={setEventEmail}
                    placeholderFor={(k) => (emailDefaults[emailLang]?.[k]) ?? (emailDefaults.en?.[k]) ?? ""}
                    onTest={sendEventTest} testingType={testingEventType} />
                </div>
              )}
            </div>
            <button type="submit" data-testid="save-event-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Event</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CalendarNotesAdmin() {
  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyNote);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/calendar-notes").then((r) => setNotes(r.data));
  useEffect(() => { load(); api.get("/events?admin=1").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(emptyNote); setEditId(null); setOpen(true); };
  const openEdit = (n) => { setForm({ date: n.date, title: n.title, description: n.description || "", i18n: n.i18n || { he: {} } }); setEditId(n.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { date: form.date, title: form.title, description: form.description, i18n: form.i18n };
    try {
      if (editId) await api.put(`/calendar-notes/${editId}`, payload);
      else await api.post("/calendar-notes", payload);
      toast.success(editId ? "Calendar note updated" : "Calendar note added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this calendar note?")) return;
    await api.delete(`/calendar-notes/${id}`);
    toast.success("Calendar note deleted"); load();
  };

  const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
  const sortedEvents = [...events].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-note-btn" onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add calendar note
        </button>
      }>Calendar</SectionTitle>

      <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4 mb-6 text-sm text-[#475569]">
        Your <b>Events</b> (created in the Events tab) show on the public calendar automatically — no action needed.
        Use <b>calendar notes</b> here for text-only entries that should appear on the calendar <i>without</i> an RSVP box —
        e.g. add your Chanukah event as a normal Event for night 1, then add notes "Night 2", "Night 3", etc.
      </div>

      <h3 className="font-semibold text-[#0F172A] mb-3">Calendar notes ({notes.length})</h3>
      <div className="space-y-3">
        {notes.map((n) => (
          <div key={n.id} data-testid={`admin-note-${n.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
            <div className="w-14 h-14 rounded-xl bg-[#003063]/10 text-[#003063] flex flex-col items-center justify-center shrink-0">
              <span className="text-lg font-bold leading-none">{n.date ? new Date(n.date + "T00:00:00").getDate() : "–"}</span>
              <span className="text-[10px] uppercase">{n.date ? new Date(n.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" }) : ""}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{n.title}</p>
              <p className="text-sm text-[#475569] truncate">{fmt(n.date)}{n.description ? ` · ${n.description}` : ""}</p>
            </div>
            <button onClick={() => openEdit(n)} data-testid={`edit-note-${n.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
            <button onClick={() => remove(n.id)} data-testid={`delete-note-${n.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
        {notes.length === 0 && <p className="text-[#475569]">No calendar notes yet.</p>}
      </div>

      <h3 className="font-semibold text-[#0F172A] mt-8 mb-3">Events on the calendar (auto)</h3>
      <div className="space-y-2">
        {sortedEvents.map((ev) => (
          <div key={ev.id} className="flex items-center gap-3 rounded-xl bg-white border border-[#0F172A]/5 px-4 py-2.5 text-sm">
            <CalendarDays size={15} className="text-[#BE923C] shrink-0" />
            <span className="text-[#0F172A] font-medium truncate flex-1">{ev.title}</span>
            <span className="text-[#475569] shrink-0">{fmt(ev.date)}</span>
          </div>
        ))}
        {sortedEvents.length === 0 && <p className="text-[#475569] text-sm">No events yet — add them in the Events tab.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} calendar note</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Date</Label>
              <Input data-testid="note-field-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            <div>
              <Label>Title (e.g. "Night 2")</Label>
              <Input data-testid="note-field-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            <div>
              <Label>Details (optional)</Label>
              <Textarea data-testid="note-field-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" />
            </div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "title", label: "Title" },
              { field: "description", label: "Details", textarea: true, rows: 3 },
            ]} />
            <button type="submit" data-testid="save-note-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save note</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCategory);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyCategory); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ title: c.title, description: c.description, body: c.body || "", image_url: c.image_url, event_image: c.event_image || "", flyers: c.flyers || [], span: c.span, parent_id: c.parent_id || "", link: c.link || "", active: c.active !== false, is_service: c.is_service === true, price_note: c.price_note || "", order_whatsapp: c.order_whatsapp || "", whatsapp_number: c.whatsapp_number || "", book_donate_number: c.book_donate_number || "", slug: c.slug || "", hide_events: c.hide_events === true, hidden: c.hidden === true, i18n: c.i18n || { he: {} } }); setEditId(c.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, flyers: (form.flyers || []).filter((f) => f && f.trim()) };
    try {
      if (editId) await api.put(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Program updated" : "Program added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Program deleted"); load();
  };

  const buildFlat = (parentsArr) => {
    const flat = [];
    const seen = new Set();
    parentsArr.forEach((p) => {
      flat.push(p); seen.add(p.id);
      cats.filter((c) => c.parent_id === p.id).forEach((ch) => { flat.push(ch); seen.add(ch.id); });
    });
    cats.forEach((c) => { if (!seen.has(c.id)) flat.push(c); });
    return flat;
  };

  const persistOrder = async (flat) => {
    setCats(flat);
    await api.put("/categories/reorder", { ids: flat.map((c) => c.id) });
  };

  const moveParent = (index, dir) => {
    const parents = cats.filter((c) => !c.parent_id);
    const target = index + dir;
    if (target < 0 || target >= parents.length) return;
    [parents[index], parents[target]] = [parents[target], parents[index]];
    persistOrder(buildFlat(parents));
  };

  const moveChild = (parentId, index, dir) => {
    const parents = cats.filter((c) => !c.parent_id);
    const children = cats.filter((c) => c.parent_id === parentId);
    const target = index + dir;
    if (target < 0 || target >= children.length) return;
    [children[index], children[target]] = [children[target], children[index]];
    const flat = [];
    const seen = new Set();
    parents.forEach((p) => {
      flat.push(p); seen.add(p.id);
      const kids = p.id === parentId ? children : cats.filter((c) => c.parent_id === p.id);
      kids.forEach((k) => { flat.push(k); seen.add(k.id); });
    });
    cats.forEach((c) => { if (!seen.has(c.id)) flat.push(c); });
    persistOrder(flat);
  };

  const parents = cats.filter((c) => !c.parent_id);
  const orphans = cats.filter((c) => c.parent_id && !parents.some((p) => p.id === c.parent_id));

  const CatRow = ({ c, up, down, upDisabled, downDisabled, isChild }) => (
    <div data-testid={`admin-category-${c.id}`} className={`flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4 ${isChild ? "" : ""}`}>
      <div className="flex flex-col">
        <button onClick={up} data-testid={`cat-up-${c.id}`} disabled={upDisabled} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowUp size={15} /></button>
        <button onClick={down} data-testid={`cat-down-${c.id}`} disabled={downDisabled} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowDown size={15} /></button>
      </div>
      <img src={resolveImg(c.image_url)} alt="" className={`${isChild ? "w-12 h-12" : "w-16 h-16"} rounded-xl object-cover shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate flex items-center gap-2">
          {c.title}
          {c.is_service && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#003063]/10 text-[#003063]">Community service</span>}
          {c.hidden && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700">Hidden</span>}
          {c.parent_id && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.active !== false ? "bg-[#EEF4EC] text-[#4b7a43]" : "bg-red-50 text-red-600"}`}>{c.active !== false ? "Active" : "Inactive"}</span>}
          {c.parent_id && <span className="text-xs font-normal text-[#8C9B86]">sub-program</span>}
        </p>
        <p className="text-sm text-[#475569] line-clamp-1">{c.description}</p>
      </div>
      <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
      <button onClick={() => remove(c.id)} data-testid={`delete-category-${c.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
    </div>
  );

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-category-btn" onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Program
        </button>
      }>Programs</SectionTitle>

      <div className="space-y-4">
        {parents.map((p, pi) => {
          const kids = cats.filter((c) => c.parent_id === p.id);
          return (
            <div key={p.id} className="space-y-2">
              <CatRow c={p} isChild={false}
                up={() => moveParent(pi, -1)} down={() => moveParent(pi, 1)}
                upDisabled={pi === 0} downDisabled={pi === parents.length - 1} />
              {kids.length > 0 && (
                <div className="ml-6 pl-4 border-l-2 border-[#BE923C]/20 space-y-2">
                  {kids.map((c, ci) => (
                    <CatRow key={c.id} c={c} isChild={true}
                      up={() => moveChild(p.id, ci, -1)} down={() => moveChild(p.id, ci, 1)}
                      upDisabled={ci === 0} downDisabled={ci === kids.length - 1} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {orphans.length > 0 && (
          <div className="space-y-2">
            {orphans.map((c) => (
              <CatRow key={c.id} c={c} isChild={true}
                up={() => {}} down={() => {}} upDisabled={true} downDisabled={true} />
            ))}
          </div>
        )}
        {cats.length === 0 && <p className="text-[#475569]">No programs yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Program</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Title</Label><Input data-testid="category-field-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            <div><Label>Image</Label><div className="mt-1.5"><ImageUploadField testid="category-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div></div>
            <div>
              <Label>Flyers (shown on the program page, tap to enlarge)</Label>
              <p className="text-xs text-[#94A3B8] mt-0.5 mb-1.5">Add one or more flyers. They appear right under the sign-up section.</p>
              <div className="space-y-2">
                {(form.flyers || []).map((fl, idx) => (
                  <div key={idx} className="flex items-start gap-2" data-testid={`category-flyer-row-${idx}`}>
                    <div className="flex-1">
                      <ImageUploadField testid={`category-flyer-${idx}`} value={fl}
                        onChange={(v) => setForm({ ...form, flyers: form.flyers.map((x, i) => (i === idx ? v : x)) })} />
                    </div>
                    <button type="button" data-testid={`category-flyer-remove-${idx}`}
                      onClick={() => setForm({ ...form, flyers: form.flyers.filter((_, i) => i !== idx) })}
                      className="mt-1 p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button type="button" data-testid="category-flyer-add"
                  onClick={() => setForm({ ...form, flyers: [...(form.flyers || []), ""] })}
                  className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-4 py-2 text-sm font-semibold hover:border-[#BE923C] transition-colors">
                  <Plus size={15} /> Add flyer
                </button>
              </div>
            </div>
            <div>
              <Label>Custom link (optional)</Label>
              <Input data-testid="category-field-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. /kosher — leave blank for the standard program page" />
            </div>
            <div>
              <Label>Card width</Label>
              <select data-testid="category-field-span" value={form.span} onChange={(e) => setForm({ ...form, span: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                <option value="col-span-12 md:col-span-4">Small (1/3 width)</option>
                <option value="col-span-12 md:col-span-6">Medium (1/2 width)</option>
                <option value="col-span-12 md:col-span-8">Large (2/3 width)</option>
                <option value="col-span-12">Full width</option>
              </select>
            </div>
            <div>
              <Label>Parent program (optional)</Label>
              <select data-testid="category-field-parent" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                <option value="">None — top-level program</option>
                {cats.filter((c) => !c.parent_id && c.id !== editId).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 px-4 py-3">
              <input type="checkbox" data-testid="category-field-is-service" checked={form.is_service} onChange={(e) => setForm({ ...form, is_service: e.target.checked })} className="w-4 h-4 accent-[#003063]" />
              <span>This is a <b>community service</b>, not a program — shows in the "Community Services" section instead of the Programs grid, and is hidden from the event filters.</span>
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 px-4 py-3">
              <input type="checkbox" data-testid="category-field-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#8C9B86]" />
              <span>Active — shows a green "Active" badge on the program page (uncheck for a red "Inactive" badge)</span>
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 px-4 py-3">
              <input type="checkbox" data-testid="category-field-hidden" checked={form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} className="w-4 h-4 accent-[#003063]" />
              <span>Hide from the website — the program/sub-program stays saved here but is removed from the Programs grid, sub-program lists and the Services section. Uncheck any time to show it again.</span>
            </label>
            <label className="flex items-center gap-3 text-sm text-[#0F172A] cursor-pointer rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 px-4 py-3">
              <input type="checkbox" data-testid="category-field-hide-events" checked={form.hide_events} onChange={(e) => setForm({ ...form, hide_events: e.target.checked })} className="w-4 h-4 accent-[#003063]" />
              <span>Hide the <b>"Upcoming events"</b> section on this page — useful for one-off services with no scheduled events (e.g. Mezuzah Affixing).</span>
            </label>
            <div><Label>Short description (shown on card)</Label><Textarea data-testid="category-field-description" rows={2} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
            <div><Label>Full text (shown on the program page)</Label><div className="mt-1.5"><RichTextEditor testid="category-field-body" value={form.body} onChange={(v) => setForm({ ...form, body: v })} placeholder="Tell the story of this program…" /></div></div>
            {form.is_service && (
              <div className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-[#0F172A]">Order / pricing (community service)</p>
                <div>
                  <Label className="text-xs">Price note (badge on the service page)</Label>
                  <Input data-testid="category-field-price-note" value={form.price_note} onChange={(e) => setForm({ ...form, price_note: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. €49 per mezuzah · free installation" />
                </div>
                <div>
                  <Label className="text-xs">"Order via WhatsApp" message (leave blank to hide the button)</Label>
                  <Input data-testid="category-field-order-whatsapp" value={form.order_whatsapp} onChange={(e) => setForm({ ...form, order_whatsapp: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. Hi! I'd like to order a mezuzah." />
                  <p className="text-xs text-[#94A3B8] mt-1">Shows a gold "Order via WhatsApp" button that opens the site's main WhatsApp number with this pre-filled message.</p>
                </div>
                <div>
                  <Label className="text-xs">WhatsApp number for this page (optional)</Label>
                  <Input data-testid="category-field-whatsapp-number" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. +420 724 300 120" />
                  <p className="text-xs text-[#94A3B8] mt-1">Overrides the site's main WhatsApp number for this page only. Leave blank to use the default number.</p>
                </div>
                {form.slug === "children-s-jewish-books-library" && (
                  <div>
                    <Label className="text-xs">"Donate a book" WhatsApp number (library only)</Label>
                    <Input data-testid="category-field-book-donate-number" value={form.book_donate_number} onChange={(e) => setForm({ ...form, book_donate_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" placeholder="e.g. +420 724 300 120" />
                    <p className="text-xs text-[#94A3B8] mt-1">Number for the "Donate a book, grow the library" box. Leave blank to use this page's WhatsApp number, then the site default.</p>
                  </div>
                )}
              </div>
            )}
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "title", label: "Title" },
              { field: "description", label: "Short description", textarea: true, rows: 2 },
              { field: "body", label: "Full text", rich: true },
              ...(form.is_service ? [
                { field: "price_note", label: "Price note" },
                { field: "order_whatsapp", label: "WhatsApp order message" },
              ] : []),
            ]} />
            <button type="submit" data-testid="save-category-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Program</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HolidaysAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyHoliday);
  const [editId, setEditId] = useState(null);
  const [resLoad, setResLoad] = useState(null);

  const load = () => api.get("/holidays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyHoliday); setEditId(null); setOpen(true); };
  const openEdit = (h) => {
    setForm({ name: h.name, subtitle: h.subtitle || "", date: h.date, end_date: h.end_date || "", description: h.description || "",
      image_url: h.image_url || "", rabbi_name: h.rabbi_name || "", rabbi_message: h.rabbi_message || "",
      rabbi_photo_url: h.rabbi_photo_url || "", resources: h.resources || [], i18n: h.i18n || { he: {} } });
    setEditId(h.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, resources: form.resources.filter((r) => r.label) };
    try {
      if (editId) await api.put(`/holidays/${editId}`, payload);
      else await api.post("/holidays", payload);
      toast.success(editId ? "Holiday updated" : "Holiday added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    await api.delete(`/holidays/${id}`); toast.success("Deleted"); load();
  };

  const setResource = (i, key, val) => {
    const next = [...form.resources]; next[i] = { ...next[i], [key]: val }; setForm({ ...form, resources: next });
  };
  const addResource = () => setForm({ ...form, resources: [...form.resources, { label: "", url: "", description: "", icon: "", type: "link", flyer_url: "", flyer_i18n: {}, body: "", page: "" }] });
  const removeResource = (i) => setForm({ ...form, resources: form.resources.filter((_, idx) => idx !== i) });
  const uploadFlyer = async (i, file, lg = "") => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/document-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (lg) setResource(i, "flyer_i18n", { ...(form.resources[i].flyer_i18n || {}), [lg]: data.url });
      else setResource(i, "flyer_url", data.url);
      toast.success(lg ? `${lg.toUpperCase()} flyer uploaded` : "Flyer uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
  };
  const removeLangFlyer = (i, lg) => {
    const next = { ...(form.resources[i].flyer_i18n || {}) }; delete next[lg]; setResource(i, "flyer_i18n", next);
  };

  const setResTrans = (code, i, key, val) => {
    const langObj = form.i18n?.[code] || {};
    const arr = [...(langObj.resources || [])];
    arr[i] = { ...(arr[i] || {}), [key]: val };
    setForm({ ...form, i18n: { ...(form.i18n || {}), [code]: { ...langObj, resources: arr } } });
  };
  const suggestResTrans = async (code) => {
    const src = {};
    form.resources.forEach((r, i) => {
      if (r.label) src[`r${i}_label`] = r.label;
      if (r.description) src[`r${i}_description`] = r.description;
      if (r.type === "text" && r.body) src[`r${i}_body`] = r.body;
    });
    if (Object.keys(src).length === 0) { toast.error("Add resources with English text first"); return; }
    setResLoad(code);
    try {
      const { data } = await api.post("/translate", { fields: src, targets: [code] });
      const langObj = form.i18n?.[code] || {};
      const arr = form.resources.map((r, i) => ({
        label: data[code]?.[`r${i}_label`] ?? (langObj.resources?.[i]?.label || ""),
        description: data[code]?.[`r${i}_description`] ?? (langObj.resources?.[i]?.description || ""),
        body: data[code]?.[`r${i}_body`] ?? (langObj.resources?.[i]?.body || ""),
      }));
      setForm({ ...form, i18n: { ...(form.i18n || {}), [code]: { ...langObj, resources: arr } } });
      toast.success("Resource translations suggested — review & save");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not translate"); }
    finally { setResLoad(null); }
  };

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-holiday-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Holiday
        </button>
      }>Holidays</SectionTitle>

      <div className="space-y-3">
        {rows.map((h) => (
          <div key={h.id} data-testid={`admin-holiday-${h.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
            {h.image_url ? <img src={resolveImg(h.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
              : <span className="w-16 h-16 rounded-xl bg-[#BE923C]/15 text-[#BE923C] flex items-center justify-center shrink-0"><Sparkles size={20} /></span>}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{h.name}</p>
              <p className="text-sm text-[#475569]">{euDate(h.date)}{h.subtitle ? ` · ${h.subtitle}` : ""} · {h.resources?.length || 0} resources</p>
            </div>
            <button onClick={() => openEdit(h)} data-testid={`edit-holiday-${h.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
            <button onClick={() => remove(h.id)} data-testid={`delete-holiday-${h.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569]">No holidays yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Holiday</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="holiday-field-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Subtitle</Label><Input data-testid="holiday-field-subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input data-testid="holiday-field-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>End date (optional)</Label><Input data-testid="holiday-field-end_date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div><Label>Hero image</Label><div className="mt-1.5"><ImageUploadField testid="holiday-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div></div>
            <div><Label>About the holiday</Label><div className="mt-1.5"><RichTextEditor testid="holiday-field-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} /></div></div>

            <div className="rounded-2xl border border-[#0F172A]/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-serif">Resources</Label>
                <button type="button" data-testid="add-resource-btn" onClick={addResource} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#BE923C]"><Plus size={15} /> Add resource</button>
              </div>

              <details data-testid="resource-link-reference" className="rounded-xl border border-[#BE923C]/30 bg-[#BE923C]/[0.06] px-4 py-3 text-sm text-[#334155]">
                <summary className="cursor-pointer font-semibold text-[#9E7829] select-none">Link reference — how to point a button to a page or section</summary>
                <div className="mt-3 space-y-4">
                  <p>In the <b>Link</b> box type one of the following:</p>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1.5">Sections on the homepage — type the <code className="bg-white px-1 rounded">#</code> anchor</p>
                    <ul className="space-y-1">
                      <li>Contact form → <code className="bg-white px-1.5 py-0.5 rounded">#contact</code></li>
                      <li>Donate → <code className="bg-white px-1.5 py-0.5 rounded">#donate</code></li>
                      <li>Programs → <code className="bg-white px-1.5 py-0.5 rounded">#programs</code></li>
                      <li>Upcoming events (home) → <code className="bg-white px-1.5 py-0.5 rounded">#events</code></li>
                      <li>Photo gallery → <code className="bg-white px-1.5 py-0.5 rounded">#gallery</code></li>
                      <li>Community services → <code className="bg-white px-1.5 py-0.5 rounded">#community-services</code></li>
                      <li>Event calendar → <code className="bg-white px-1.5 py-0.5 rounded">#calendar-section</code></li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1.5">Whole pages — type the path</p>
                    <ul className="space-y-1">
                      <li>About → <code className="bg-white px-1.5 py-0.5 rounded">/about</code></li>
                      <li>All events → <code className="bg-white px-1.5 py-0.5 rounded">/events</code></li>
                      <li>Synagogue page → <code className="bg-white px-1.5 py-0.5 rounded">/classes</code></li>
                      <li>Kosher → <code className="bg-white px-1.5 py-0.5 rounded">/kosher</code></li>
                      <li>Holidays overview → <code className="bg-white px-1.5 py-0.5 rounded">/holidays</code></li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1.5">A specific event page</p>
                    <p className="mb-1.5">Open that event on the website (Events → click the event). Copy everything in the browser address bar from <code className="bg-white px-1 rounded">/events/</code> onward and paste it here — e.g. <code className="bg-white px-1.5 py-0.5 rounded">/events/8d05...b3c6</code>.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1.5">A section on another page — path + <code className="bg-white px-1 rounded">#id</code></p>
                    <ul className="space-y-1">
                      <li>Shul weekday / Shabbat prayer times → <code className="bg-white px-1.5 py-0.5 rounded">/classes#shul-prayer-times</code></li>
                      <li>Holiday prayer-times flyer → <code className="bg-white px-1.5 py-0.5 rounded">/classes#shul-holiday-flyer</code></li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1.5">External / other</p>
                    <ul className="space-y-1">
                      <li>Any website → <code className="bg-white px-1.5 py-0.5 rounded">https://example.com</code></li>
                      <li>Email → <code className="bg-white px-1.5 py-0.5 rounded">mailto:info@chabadprague.cz</code></li>
                      <li>Phone → <code className="bg-white px-1.5 py-0.5 rounded">tel:+420123456789</code></li>
                    </ul>
                  </div>
                  <p className="text-xs text-[#475569] border-t border-[#BE923C]/20 pt-3">Rule of thumb: starts with <code className="bg-white px-1 rounded">#</code> → a homepage section · starts with <code className="bg-white px-1 rounded">/</code> → an internal page/section · anything else → an external website. Left blank → the contact section.</p>
                </div>
              </details>
              {form.resources.length === 0 && <p className="text-sm text-[#475569]">No resources yet. E.g. "Purchase your Menorah", "Order Matza".</p>}
              {form.resources.map((r, i) => {
                const RPrev = resourceIconComp(r);
                const autoKey = autoResourceIcon(r.url, r.label);
                const rtype = r.type || "link";
                return (
                <div key={i} data-testid={`resource-row-${i}`} className="rounded-xl border border-[#0F172A]/10 bg-[#F9F8F6] p-3 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2 items-center">
                    <Input data-testid={`resource-label-${i}`} placeholder="Label (e.g. Purchase your Menorah)" value={r.label} onChange={(e) => setResource(i, "label", e.target.value)} className="border-[#0F172A]/10 bg-white" />
                    <select data-testid={`resource-type-${i}`} value={rtype} onChange={(e) => setResource(i, "type", e.target.value)}
                      className="rounded-md border border-[#0F172A]/10 bg-white px-2.5 py-2 text-sm">
                      {RESOURCE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button type="button" data-testid={`remove-resource-${i}`} onClick={() => removeResource(i)} className="p-2.5 rounded-lg hover:bg-red-50 text-red-500"><X size={16} /></button>
                  </div>

                  {rtype === "link" && (
                    <Input data-testid={`resource-url-${i}`} placeholder="Link (URL or #donate / #contact)" value={r.url} onChange={(e) => setResource(i, "url", e.target.value)} className="border-[#0F172A]/10 bg-white" />
                  )}
                  {rtype === "page" && (
                    <select data-testid={`resource-page-${i}`} value={r.url} onChange={(e) => setResource(i, "url", e.target.value)}
                      className="w-full rounded-md border border-[#0F172A]/10 bg-white px-2.5 py-2 text-sm">
                      <option value="">Choose a page…</option>
                      {RESOURCE_PAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  )}
                  {rtype === "flyer" && (
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 cursor-pointer hover:bg-[#1e293b]">
                        <Plus size={14} /> {r.flyer_url ? "Replace flyer" : "Upload flyer (JPG / PNG / PDF)"}
                        <input type="file" data-testid={`resource-flyer-${i}`} accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" className="hidden" onChange={(e) => uploadFlyer(i, e.target.files?.[0])} />
                      </label>
                      {r.flyer_url && <a href={resolveImg(r.flyer_url)} target="_blank" rel="noreferrer" className="text-sm text-[#BE923C] underline">Preview uploaded flyer</a>}
                    </div>
                  )}
                  {rtype === "flyer" && (
                    <div className="rounded-lg border border-dashed border-[#0F172A]/15 p-2.5" data-testid={`resource-flyer-langs-${i}`}>
                      <p className="text-xs text-[#475569] mb-2">Language versions (optional). Visitors see the flyer matching their language; the main flyer above is the fallback (English).</p>
                      <div className="flex flex-wrap gap-2">
                        {[["cs", "Czech"], ["he", "Hebrew"], ["ru", "Russian"]].map(([lg, name]) => {
                          const has = !!(r.flyer_i18n || {})[lg];
                          return (
                            <span key={lg} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${has ? "border-[#8C9B86] bg-[#8C9B86]/10 text-[#0F172A]" : "border-[#0F172A]/15 text-[#475569]"}`}>
                              <label className="cursor-pointer inline-flex items-center gap-1">
                                <Upload size={12} /> {name}{has ? " ✓" : ""}
                                <input type="file" data-testid={`resource-flyer-${lg}-${i}`} accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" className="hidden" onChange={(e) => uploadFlyer(i, e.target.files?.[0], lg)} />
                              </label>
                              {has && <a href={resolveImg(r.flyer_i18n[lg])} target="_blank" rel="noreferrer" className="underline text-[#BE923C]">view</a>}
                              {has && <button type="button" onClick={() => removeLangFlyer(i, lg)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {rtype === "text" && (
                    <div>
                      <p className="text-xs text-[#94A3B8] mb-1">Pop-up text (English). Add translations below.</p>
                      <RichTextEditor testid={`resource-body-${i}`} value={r.body || ""} placeholder="Write the text that opens in the pop-up…" onChange={(html) => setResource(i, "body", html)} />
                    </div>
                  )}

                  <Input data-testid={`resource-desc-${i}`} placeholder="Short description (optional)" value={r.description} onChange={(e) => setResource(i, "description", e.target.value)} className="border-[#0F172A]/10 bg-white" />
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-lg bg-[#BE923C]/10 flex items-center justify-center text-[#BE923C] shrink-0"><RPrev size={17} /></span>
                    <span className="text-xs text-[#475569] shrink-0">Icon</span>
                    <select data-testid={`resource-icon-${i}`} value={r.icon || ""} onChange={(e) => setResource(i, "icon", e.target.value)}
                      className="flex-1 rounded-md border border-[#0F172A]/10 bg-white px-2.5 py-2 text-sm">
                      <option value="">Auto — {RESOURCE_ICONS[autoKey]?.label || "Gift"} (from link/label)</option>
                      {Object.entries(RESOURCE_ICONS).map(([key, def]) => (
                        <option key={key} value={key}>{def.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                );
              })}

              {form.resources.some((r) => r.label) && TR_LANGS.map((L) => {
                const rArr = form.i18n?.[L.code]?.resources || [];
                return (
                  <div key={L.code} data-testid={`resource-trans-${L.code}`} dir={L.rtl ? "rtl" : "ltr"}
                    className="rounded-xl border border-[#BE923C]/30 bg-[#FBF7EE] p-3 space-y-2">
                    <div className="flex items-center justify-between" dir="ltr">
                      <p className="text-xs font-serif font-semibold text-[#9E7829]">Resources · {L.label}</p>
                      <button type="button" data-testid={`suggest-resources-${L.code}`} onClick={() => suggestResTrans(L.code)} disabled={resLoad === L.code}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#BE923C] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#9E7829] disabled:opacity-60 transition-colors">
                        <Sparkles size={13} /> {resLoad === L.code ? "Translating…" : "Suggest"}
                      </button>
                    </div>
                    {form.resources.map((r, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input data-testid={`${L.code}-resource-label-${i}`} placeholder={r.label || "Label"} value={rArr[i]?.label || ""} onChange={(e) => setResTrans(L.code, i, "label", e.target.value)} className={`border-[#0F172A]/10 ${L.rtl ? "text-right" : ""}`} />
                        <Input data-testid={`${L.code}-resource-desc-${i}`} placeholder={r.description || "Description"} value={rArr[i]?.description || ""} onChange={(e) => setResTrans(L.code, i, "description", e.target.value)} className={`border-[#0F172A]/10 ${L.rtl ? "text-right" : ""}`} />
                        {r.type === "text" && (
                          <textarea data-testid={`${L.code}-resource-body-${i}`} rows={3} placeholder="Pop-up text" value={rArr[i]?.body || ""} onChange={(e) => setResTrans(L.code, i, "body", e.target.value)} dir={L.rtl ? "rtl" : "ltr"} className={`sm:col-span-2 rounded-md border border-[#0F172A]/10 bg-white p-2 text-sm ${L.rtl ? "text-right" : ""}`} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-[#0F172A]/10 p-4 space-y-3">
              <Label className="text-base font-serif">A word from the Rabbi</Label>
              <div><Label>Rabbi name</Label><Input data-testid="holiday-field-rabbi_name" value={form.rabbi_name} onChange={(e) => setForm({ ...form, rabbi_name: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Rabbi photo</Label><div className="mt-1.5"><ImageUploadField testid="holiday-rabbi" value={form.rabbi_photo_url} onChange={(v) => setForm({ ...form, rabbi_photo_url: v })} /></div></div>
              <div><Label>Message</Label><Textarea data-testid="holiday-field-rabbi_message" rows={4} value={form.rabbi_message} onChange={(e) => setForm({ ...form, rabbi_message: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
            </div>

            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "name", label: "Name" },
              { field: "subtitle", label: "Subtitle" },
              { field: "description", label: "About", rich: true },
              { field: "rabbi_message", label: "Rabbi message", textarea: true, rows: 4 },
            ]} />

            <button type="submit" data-testid="save-holiday-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Holiday</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WeeklyAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", time: "", audience: "", description: "", whatsapp_number: "", wa_button_enabled: true, wa_button_text: "", image_url: "", i18n: { he: {} } });
  const [editId, setEditId] = useState(null);
  const [shul, setShul] = useState({ title: "", text: "", image_url: "", holiday_flyer: "", title_he: "", text_he: "", prayers_weekday: [], prayers_shabbat: [] });

  const load = () => api.get("/weekly-classes").then((r) => setRows(r.data));
  const loadSettings = () => api.get("/settings").then((r) => setShul({ title: "", text: "", image_url: "", holiday_flyer: "", title_he: "", text_he: "", prayers_weekday: [], prayers_shabbat: [], ...(r.data.synagogue || {}) }));
  useEffect(() => { load(); loadSettings(); }, []);

  const setPrayer = (key, idx, field, val) => setShul((s) => { const arr = [...(s[key] || [])]; arr[idx] = { ...arr[idx], [field]: val }; return { ...s, [key]: arr }; });
  const addPrayer = (key) => setShul((s) => ({ ...s, [key]: [...(s[key] || []), { label: "", time: "" }] }));
  const removePrayer = (key, idx) => setShul((s) => ({ ...s, [key]: (s[key] || []).filter((_, i) => i !== idx) }));

  const openNew = () => { setForm({ title: "", time: "", audience: "", description: "", whatsapp_number: "", wa_button_enabled: true, wa_button_text: "", image_url: "", i18n: { he: {} } }); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ title: c.title, time: c.time, audience: c.audience, description: c.description || "", whatsapp_number: c.whatsapp_number || "", wa_button_enabled: c.wa_button_enabled !== false, wa_button_text: c.wa_button_text || "", image_url: c.image_url || "", i18n: c.i18n || { he: {} } }); setEditId(c.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/weekly-classes/${editId}`, form);
      else await api.post("/weekly-classes", form);
      toast.success(editId ? "Class updated" : "Class added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    await api.delete(`/weekly-classes/${id}`); toast.success("Deleted"); load();
  };

  const saveShul = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", { values: { synagogue: shul } });
      toast.success("Synagogue info saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10">
      <div>
        <SectionTitle action={
          <button data-testid="add-weekly-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
            <Plus size={16} /> Add Class
          </button>
        }>Weekly Classes</SectionTitle>
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.id} data-testid={`admin-weekly-${c.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
              <div className="flex-1">
                <p className="font-semibold">{c.title} {c.audience && <span className="text-xs font-normal text-[#8C9B86]">· {c.audience}</span>}</p>
                <p className="text-sm text-[#475569]">{c.time}</p>
              </div>
              <button onClick={() => openEdit(c)} data-testid={`edit-weekly-${c.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
              <button onClick={() => remove(c.id)} data-testid={`delete-weekly-${c.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
          {rows.length === 0 && <p className="text-[#475569]">No weekly classes yet.</p>}
        </div>
      </div>

      <div>
        <h2 className="font-serif text-2xl tracking-tight mb-4">Synagogue</h2>
        <form onSubmit={saveShul} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 space-y-3 max-w-2xl">
          <div><Label>Title</Label><Input data-testid="shul-title" value={shul.title} onChange={(e) => setShul({ ...shul, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
          <div><Label>Description</Label><Textarea data-testid="shul-text" rows={4} value={shul.text} onChange={(e) => setShul({ ...shul, text: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
          <div><Label>Photo</Label><div className="mt-1.5"><ImageUploadField testid="shul-image" value={shul.image_url} onChange={(v) => setShul({ ...shul, image_url: v })} /></div></div>
          <div className="rounded-2xl border border-[#BE923C]/30 bg-[#BE923C]/[0.06] p-4">
            <Label>Upcoming holiday prayer times — flyer</Label>
            <p className="mt-1 text-xs text-[#475569]">Upload the flyer with holiday prayer times. It appears in its own section on the synagogue page (guests can tap to enlarge). Leave empty to hide the section.</p>
            <div className="mt-2"><ImageUploadField testid="shul-holiday-flyer" value={shul.holiday_flyer} onChange={(v) => setShul({ ...shul, holiday_flyer: v })} /></div>
          </div>

          {[{ key: "prayers_weekday", title: "Weekday prayer times" }, { key: "prayers_shabbat", title: "Shabbat & holiday prayer times" }].map((grp) => (
            <div key={grp.key} data-testid={`prayers-${grp.key}`} className="rounded-2xl border border-[#003063]/15 bg-[#003063]/[0.04] p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[#003063]">{grp.title}</p>
                <button type="button" data-testid={`add-${grp.key}`} onClick={() => addPrayer(grp.key)} className="inline-flex items-center gap-1.5 rounded-full border border-[#003063]/25 px-3 py-1.5 text-xs font-semibold text-[#003063] hover:bg-[#003063]/10 transition-colors"><Plus size={13} /> Add time</button>
              </div>
              <div className="mt-3 space-y-3">
                {(shul[grp.key] || []).map((p, idx) => (
                  <div key={idx} className="rounded-xl bg-white border border-[#0F172A]/8 p-3">
                    <div className="flex items-center gap-2">
                      <Input data-testid={`${grp.key}-label-${idx}`} placeholder="Prayer name (English) — e.g. Shacharit" value={p.label || ""} onChange={(e) => setPrayer(grp.key, idx, "label", e.target.value)} className="flex-1 border-[#0F172A]/10" />
                      <Input data-testid={`${grp.key}-time-${idx}`} placeholder="7:00 AM" value={p.time || ""} onChange={(e) => setPrayer(grp.key, idx, "time", e.target.value)} className="w-36 border-[#0F172A]/10" />
                      <button type="button" data-testid={`remove-${grp.key}-${idx}`} onClick={() => removePrayer(grp.key, idx)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input data-testid={`${grp.key}-label_cs-${idx}`} placeholder="Název modlitby (česky)" value={p.label_cs || ""} onChange={(e) => setPrayer(grp.key, idx, "label_cs", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                      <Input data-testid={`${grp.key}-time_cs-${idx}`} placeholder="Čas (česky, nepovinné)" value={p.time_cs || ""} onChange={(e) => setPrayer(grp.key, idx, "time_cs", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                      <Input dir="rtl" data-testid={`${grp.key}-label_he-${idx}`} placeholder="שם התפילה (עברית)" value={p.label_he || ""} onChange={(e) => setPrayer(grp.key, idx, "label_he", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                      <Input dir="rtl" data-testid={`${grp.key}-time_he-${idx}`} placeholder="שעה (עברית, לא חובה)" value={p.time_he || ""} onChange={(e) => setPrayer(grp.key, idx, "time_he", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                      <Input data-testid={`${grp.key}-label_ru-${idx}`} placeholder="Название (Русский)" value={p.label_ru || ""} onChange={(e) => setPrayer(grp.key, idx, "label_ru", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                      <Input data-testid={`${grp.key}-time_ru-${idx}`} placeholder="Время (Русский, необяз.)" value={p.time_ru || ""} onChange={(e) => setPrayer(grp.key, idx, "time_ru", e.target.value)} className="border-[#0F172A]/10 text-sm" />
                    </div>
                  </div>
                ))}
                {(shul[grp.key] || []).length === 0 && <p className="text-xs text-[#94A3B8]">No times added yet.</p>}
              </div>
              <p className="mt-2 text-xs text-[#94A3B8]">Add the prayer name in English, then optionally its Czech/Hebrew/Russian name. Leave a translation blank to fall back to the English name. Time is usually the same in all languages — only fill the translated time if it needs different wording (e.g. "before sunset").</p>
            </div>
          ))}

          {TR_LANGS.map((L) => (
            <div key={L.code} className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-4 space-y-3" dir={L.rtl ? "rtl" : "ltr"} data-testid={`shul-trans-${L.code}`}>
              <p className="text-sm font-serif font-semibold text-[#9E7829]" dir="ltr">{L.label}</p>
              <div><Label className="text-[#475569]">Title</Label><Input data-testid={`shul-title-${L.code}`} value={shul[`title_${L.code}`] || ""} onChange={(e) => setShul({ ...shul, [`title_${L.code}`]: e.target.value })} className={`mt-1.5 border-[#0F172A]/10 ${L.rtl ? "text-right" : ""}`} /></div>
              <div><Label className="text-[#475569]">Description</Label><Textarea data-testid={`shul-text-${L.code}`} rows={4} value={shul[`text_${L.code}`] || ""} onChange={(e) => setShul({ ...shul, [`text_${L.code}`]: e.target.value })} className={`mt-1.5 border-[#0F172A]/10 resize-none ${L.rtl ? "text-right" : ""}`} /></div>
            </div>
          ))}
          <button type="submit" data-testid="save-shul-btn" className="rounded-full bg-[#0F172A] text-white font-semibold px-6 py-3 hover:bg-[#1e293b] transition-colors">Save Synagogue Info</button>
        </form>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Class</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Title</Label><Input data-testid="weekly-field-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Time</Label><Input data-testid="weekly-field-time" placeholder="Sundays at 8:15 PM" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Audience</Label><Input data-testid="weekly-field-audience" placeholder="Women / Men" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div className="rounded-2xl border border-[#0F172A]/10 bg-[#F9F8F6] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#0F172A]">WhatsApp button</Label>
                  <p className="text-xs text-[#475569] mt-0.5">Show a "Join on WhatsApp" button on this class card.</p>
                </div>
                <button
                  type="button"
                  data-testid="weekly-field-wa_button_enabled"
                  onClick={() => setForm({ ...form, wa_button_enabled: !form.wa_button_enabled })}
                  aria-pressed={form.wa_button_enabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${form.wa_button_enabled ? "bg-[#BE923C]" : "bg-[#CBD5E1]"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.wa_button_enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {form.wa_button_enabled && (
                <div className="mt-4 space-y-3">
                  <div><Label>Button text (English)</Label><Input data-testid="weekly-field-wa_button_text" placeholder="Join on WhatsApp" value={form.wa_button_text} onChange={(e) => setForm({ ...form, wa_button_text: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /><p className="text-xs text-[#94A3B8] mt-1">Leave blank to use the site's default button text.</p></div>
                  <div><Label>WhatsApp number (to join)</Label><Input data-testid="weekly-field-whatsapp" placeholder="+420 123 456 789" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
                </div>
              )}
            </div>
            <div><Label>Photo</Label><div className="mt-1.5"><ImageUploadField testid="weekly-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div></div>
            <div><Label>Description</Label><Textarea data-testid="weekly-field-description" rows={4} placeholder="What is this class about?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "title", label: "Title" },
              { field: "time", label: "Time" },
              { field: "audience", label: "Audience" },
              { field: "description", label: "Description", textarea: true, rows: 4 },
              ...(form.wa_button_enabled ? [{ field: "wa_button_text", label: "Button text" }] : []),
            ]} />
            <button type="submit" data-testid="save-weekly-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Class</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RebbeAdmin() {
  const [image, setImage] = useState("");
  const [quote, setQuote] = useState({ en: "", he: "", ru: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/rebbe").then((r) => {
      setImage(r.data.image_url || "");
      setQuote({ en: "", he: "", ru: "", ...(r.data.quote || {}) });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/rebbe", { image_url: image, quote });
      toast.success("Rebbe section saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    setSaving(false);
  };

  const langs = [{ k: "en", label: "English", rtl: false }, { k: "cs", label: "Čeština · Czech", rtl: false }, { k: "he", label: "עברית · Hebrew", rtl: true }, { k: "ru", label: "Русский · Russian", rtl: false }];

  return (
    <div data-testid="rebbe-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 mb-6">
      <p className="font-semibold flex items-center gap-2"><ImageIcon size={16} className="text-[#BE923C]" /> Photo of the Rebbe (About page)</p>
      <p className="text-sm text-[#475569] mt-0.5 mb-4">Shown in the “Our Story” section on the About page, beside the story text, with the quote below it. Leave the photo empty to hide it.</p>
      <div className="grid md:grid-cols-[220px_1fr] gap-5">
        <div>
          <Label>Portrait</Label>
          <div className="mt-1.5"><ImageUploadField testid="rebbe-image-field" value={image} onChange={setImage} /></div>
        </div>
        <div className="space-y-3">
          <Label>Quote / teaching (attribution “— The Lubavitcher Rebbe” is added automatically)</Label>
          {langs.map((l) => (
            <div key={l.k}>
              <span className="text-xs font-semibold text-[#9E7829]">{l.label}</span>
              <Textarea dir={l.rtl ? "rtl" : "ltr"} data-testid={`rebbe-quote-${l.k}`} rows={2} value={quote[l.k] || ""}
                onChange={(e) => setQuote((q) => ({ ...q, [l.k]: e.target.value }))}
                className="mt-1 border-[#0F172A]/10 resize-none" />
            </div>
          ))}
        </div>
      </div>
      <button type="button" data-testid="save-rebbe" onClick={save} disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-60">
        {saving ? "Saving…" : "Save Rebbe section"}
      </button>
    </div>
  );
}


function StaffAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyStaff);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/staff").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyStaff); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, role: s.role, since: s.since, bio: s.bio, photo_url: s.photo_url, i18n: s.i18n || { he: {} } }); setEditId(s.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/staff/${editId}`, form);
      else await api.post("/staff", form);
      toast.success(editId ? "Staff updated" : "Staff added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    await api.delete(`/staff/${id}`); toast.success("Removed"); load();
  };

  const move = async (index, dir) => {
    const next = [...rows]; const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
    await api.put("/staff/reorder", { ids: next.map((s) => s.id) });
  };

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-staff-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Staff
        </button>
      }>Staff</SectionTitle>

      <RebbeAdmin />

      <div className="space-y-3">
        {rows.map((s, i) => (
          <div key={s.id} data-testid={`admin-staff-${s.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`staff-up-${s.id}`} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} data-testid={`staff-down-${s.id}`} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowDown size={15} /></button>
            </div>
            {s.photo_url ? <img src={resolveImg(s.photo_url)} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
              : <span className="w-14 h-14 rounded-full bg-[#BE923C]/15 text-[#BE923C] flex items-center justify-center font-serif text-lg shrink-0">{s.name?.[0]}</span>}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{s.name}</p>
              <p className="text-sm text-[#475569]">{s.role}{s.since ? ` · ${s.since}` : ""}</p>
            </div>
            <button onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
            <button onClick={() => remove(s.id)} data-testid={`delete-staff-${s.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569]">No staff yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Staff</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input data-testid="staff-field-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label><Input data-testid="staff-field-role" required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Since (optional)</Label><Input data-testid="staff-field-since" value={form.since} onChange={(e) => setForm({ ...form, since: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div><Label>Photo</Label><div className="mt-1.5"><ImageUploadField testid="staff-photo" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} /></div></div>
            <div><Label>Bio</Label><Textarea data-testid="staff-field-bio" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" /></div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "role", label: "Role" },
              { field: "bio", label: "Bio", textarea: true, rows: 4 },
            ]} />
            <button type="submit" data-testid="save-staff-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Staff</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatsAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyStat);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/stats-board").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyStat); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ label: s.label, value: s.value, suffix: s.suffix, i18n: s.i18n || { he: {} } }); setEditId(s.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/stats-board/${editId}`, form);
      else await api.post("/stats-board", form);
      toast.success(editId ? "Number updated" : "Number added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this number?")) return;
    await api.delete(`/stats-board/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <SectionTitle action={
        <button data-testid="add-stat-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Number
        </button>
      }>Numbers Board</SectionTitle>

      <div className="grid sm:grid-cols-2 gap-4">
        {rows.map((s) => (
          <div key={s.id} data-testid={`admin-stat-${s.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-[#0F172A]/5 p-5">
            <div className="flex-1">
              <p className="font-serif text-3xl text-[#BE923C]">{s.value}{s.suffix}</p>
              <p className="text-sm text-[#475569]">{s.label}</p>
            </div>
            <button onClick={() => openEdit(s)} data-testid={`edit-stat-${s.id}`} className="p-2 rounded-lg hover:bg-[#E8E6E1] text-[#475569]"><Pencil size={16} /></button>
            <button onClick={() => remove(s.id)} data-testid={`delete-stat-${s.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569]">No numbers yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Number</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Value</Label><Input data-testid="stat-field-value" required placeholder="250" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
              <div><Label>Suffix (optional)</Label><Input data-testid="stat-field-suffix" placeholder="+" value={form.suffix} onChange={(e) => setForm({ ...form, suffix: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            </div>
            <div><Label>Label</Label><Input data-testid="stat-field-label" required placeholder="Community events every year" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1.5 border-[#0F172A]/10" /></div>
            <TranslationBox form={form} setForm={setForm} fields={[
              { field: "label", label: "Label" },
            ]} />
            <button type="submit" data-testid="save-stat-btn" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3 hover:bg-[#1e293b] transition-colors">Save Number</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RsvpFieldsAdmin() {
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(true);
  const [note, setNote] = useState({ en: "", he: "", ru: "" });

  useEffect(() => { api.get("/rsvp-fields").then((r) => { setFields(r.data.custom_fields || []); setPhoneRequired(r.data.phone_required !== false); setNote({ en: "", he: "", ru: "", ...(r.data.suggested_note || {}) }); }).catch(() => {}); }, []);

  const addField = () => setFields((f) => [...f, { id: crypto.randomUUID(), label: "", type: "text", required: false, options: [] }]);
  const removeField = (id) => setFields((f) => f.filter((x) => x.id !== id));
  const update = (id, patch) => setFields((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const save = async () => {
    const cleaned = fields.filter((f) => f.label.trim());
    setSaving(true);
    try {
      await api.put("/rsvp-fields", { custom_fields: cleaned, phone_required: phoneRequired, suggested_note: note });
      setFields(cleaned);
      toast.success("Saved — applies to every RSVP form");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="rsvp-fields-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 overflow-hidden">
      <button data-testid="toggle-rsvp-fields" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F9F8F6] transition-colors">
        <span className="flex items-center gap-2 font-semibold text-[#0F172A]"><ListChecks size={18} className="text-[#BE923C]" /> Custom RSVP questions</span>
        <span className="text-sm text-[#475569]">{fields.length} question{fields.length === 1 ? "" : "s"} · applies to all events</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-[#0F172A]/5 pt-4">
          <label className="flex items-center justify-between gap-3 rounded-xl border border-[#0F172A]/10 px-4 py-3">
            <span className="text-sm font-semibold text-[#0F172A]">Require phone number on every RSVP</span>
            <button type="button" data-testid="toggle-phone-required" onClick={() => setPhoneRequired((p) => !p)} aria-pressed={phoneRequired}
              className={`relative w-12 h-7 rounded-full transition-colors ${phoneRequired ? "bg-[#BE923C]" : "bg-[#CBD5E1]"}`}>
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${phoneRequired ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </label>

          <div className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE] p-4 space-y-2">
            <Label className="text-sm font-semibold text-[#9E7829]">Suggested-participation note</Label>
            <p className="text-xs text-[#475569]">Shown above the amount on events set to “Suggested participation”. Leave blank to use the default wording.</p>
            {[["en", "English"], ["cs", "Čeština"], ["he", "עברית"], ["ru", "Русский"]].map(([code, label]) => (
              <div key={code}>
                <Label className="text-xs text-[#475569]">{label}</Label>
                <Input data-testid={`suggested-note-${code}`} value={note[code] || ""} dir={code === "he" ? "rtl" : "ltr"}
                  placeholder={{ en: "Your contribution helps us host more events like this.", cs: "Váš příspěvek nám pomáhá pořádat více akcí, jako je tato.", he: "התרומה שלך עוזרת לנו לקיים עוד אירועים כמו זה.", ru: "Ваш взнос помогает нам проводить больше подобных мероприятий." }[code]}
                  onChange={(e) => setNote((n) => ({ ...n, [code]: e.target.value }))} className="mt-1 border-[#0F172A]/10" />
              </div>
            ))}
          </div>
          {fields.length === 0 && <p className="text-sm text-[#475569]">No extra questions yet. Add one below — it will appear on every event's RSVP form. For questions specific to a single event, use the "Custom questions" section inside that event.</p>}
          {fields.map((f) => (
            <div key={f.id} data-testid={`rsvp-field-row-${f.id}`} className="rounded-xl border border-[#0F172A]/10 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Label className="text-xs">Question</Label>
                  <Input data-testid={`rsvp-field-label-${f.id}`} value={f.label} placeholder="e.g. Any dietary requirements?"
                    onChange={(e) => update(f.id, { label: e.target.value })} className="mt-1 border-[#0F172A]/10" />
                </div>
                <div className="w-40">
                  <Label className="text-xs">Type</Label>
                  <select data-testid={`rsvp-field-type-${f.id}`} value={f.type} onChange={(e) => update(f.id, { type: e.target.value })}
                    className="mt-1 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                    <option value="text">Short text</option>
                    <option value="select">Dropdown</option>
                    <option value="checkbox">Yes / No</option>
                  </select>
                </div>
                <button onClick={() => removeField(f.id)} data-testid={`remove-rsvp-field-${f.id}`} className="mt-6 p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
              {f.type === "select" && (
                <div>
                  <Label className="text-xs">Options (comma-separated)</Label>
                  <Input data-testid={`rsvp-field-options-${f.id}`} value={(f.options || []).join(", ")} placeholder="Adult, Child, Vegetarian"
                    onChange={(e) => update(f.id, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })}
                    className="mt-1 border-[#0F172A]/10" />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer">
                <input type="checkbox" data-testid={`rsvp-field-required-${f.id}`} checked={f.required} onChange={(e) => update(f.id, { required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                Required
              </label>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-1">
            <button onClick={addField} data-testid="add-rsvp-field" className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-4 py-2 text-sm font-semibold hover:border-[#BE923C]"><Plus size={15} /> Add question</button>
            <button onClick={save} disabled={saving} data-testid="save-rsvp-fields" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1e293b] disabled:opacity-60">{saving ? "Saving…" : "Save questions"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const REMINDER_DEFAULT = {
  subject: "Reminder: {title} · {date}",
  html: "<p>Dear {name},</p><p>A friendly reminder that <b>{title}</b> takes place on <b>{date}</b> at <b>{time}</b>, {location}.</p><p>We look forward to seeing you!</p>",
};
function ReminderDialog({ eventId, eventTitle, open, onOpenChange }) {
  const [subject, setSubject] = useState(REMINDER_DEFAULT.subject);
  const [html, setHtml] = useState(REMINDER_DEFAULT.html);
  const [statuses, setStatuses] = useState(["approved"]);
  const [step, setStep] = useState("compose");
  const [preview, setPreview] = useState(null);
  const [phones, setPhones] = useState({});
  const [sentIds, setSentIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const toggleStatus = (k) => setStatuses((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/reminder/preview`, { subject, html, statuses });
      setPreview(r.data); setPhones(Object.fromEntries(r.data.recipients.map((x) => [x.id, x.wa_phone]))); return r.data;
    } catch (e) { toast.error("Could not load recipients"); } finally { setBusy(false); }
  };
  const goWhatsApp = async () => { if (await loadPreview()) setStep("whatsapp"); };
  const sendEmail = async () => {
    const p = preview || await loadPreview();
    if (!p) return;
    if (!window.confirm(`Send this email to ${p.with_email} guest${p.with_email === 1 ? "" : "s"} now?`)) return;
    setBusy(true);
    try { const r = await api.post(`/events/${eventId}/reminder/email`, { subject, html, statuses }); setResult(r.data); toast.success(`Sent to ${r.data.sent} guest${r.data.sent === 1 ? "" : "s"}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); } finally { setBusy(false); }
  };
  const waRecipients = (preview?.recipients || []).filter((x) => x.phone);
  const openWa = (x) => {
    const digits = (phones[x.id] || "").replace(/\D/g, "");
    if (!digits) return;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(x.text)}`, "_blank", "noopener");
    setSentIds((s) => (s.includes(x.id) ? s : [...s, x.id]));
  };
  const nextUnsent = waRecipients.find((x) => !sentIds.includes(x.id));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="ltr" className="font-sans max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Send a reminder · {eventTitle}</DialogTitle>
          <DialogDescription>One message to every guest. Placeholders: {"{name} {title} {date} {time} {location} {guests}"} are filled in per guest.</DialogDescription>
        </DialogHeader>
        {step === "compose" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-[#475569]">Send to:</span>
              {["approved", "pending"].map((k) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer capitalize"><input type="checkbox" checked={statuses.includes(k)} onChange={() => toggleStatus(k)} className="accent-[#BE923C]" data-testid={`reminder-status-${k}`} /> {k}</label>
              ))}
            </div>
            <div><Label htmlFor="reminder-subject">Email subject</Label><Input id="reminder-subject" data-testid="reminder-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Message</Label><div className="mt-1.5"><RichTextEditor value={html} onChange={setHtml} testid="reminder-editor" /></div></div>
            {result && <p className="text-sm text-[#4f5f4a] bg-[#8C9B86]/15 rounded-lg px-3 py-2" data-testid="reminder-result">Email sent to {result.sent} guest{result.sent === 1 ? "" : "s"}{result.skipped_no_email ? ` · ${result.skipped_no_email} had no email — use WhatsApp for them` : ""}.</p>}
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="button" disabled={busy || statuses.length === 0} onClick={sendEmail} data-testid="reminder-send-email" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-60"><Mail size={15} /> Send by email</button>
              <button type="button" disabled={busy || statuses.length === 0} onClick={goWhatsApp} data-testid="reminder-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 hover:border-[#BE923C] text-sm font-semibold px-5 py-2.5 disabled:opacity-60"><MessageCircle size={15} /> Send by WhatsApp…</button>
            </div>
          </div>
        )}
        {step === "whatsapp" && preview && (
          <div className="space-y-3" data-testid="reminder-wa-list">
            <div className="flex items-center justify-between gap-3 text-sm">
              <button type="button" onClick={() => setStep("compose")} className="text-[#475569] underline underline-offset-2">← Edit message</button>
              <span className="text-[#475569]">{sentIds.length} / {waRecipients.length} opened</span>
              {nextUnsent && <button type="button" onClick={() => openWa(nextUnsent)} data-testid="reminder-wa-next" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-4 py-2"><MessageCircle size={14} /> Open next: {nextUnsent.name}</button>}
            </div>
            <p className="text-xs text-[#475569]">WhatsApp doesn’t allow bulk sending from a normal account, so each tap opens one pre-filled chat — press Send in WhatsApp, come back, tap the next. Numbers are editable.</p>
            <div className="divide-y divide-[#0F172A]/5 rounded-xl border border-[#0F172A]/10 bg-white">
              {waRecipients.map((x) => (
                <div key={x.id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`reminder-wa-row-${x.id}`}>
                  <div className="flex-1 min-w-[140px]"><p className="text-sm font-semibold text-[#0F172A]">{x.name}</p><p className="text-xs text-[#475569] capitalize">{x.status} · {x.guests} guests</p></div>
                  <Input value={phones[x.id] || ""} onChange={(e) => setPhones({ ...phones, [x.id]: e.target.value })} data-testid={`reminder-wa-phone-${x.id}`} className="w-44 h-9 text-sm" />
                  <button type="button" onClick={() => openWa(x)} data-testid={`reminder-wa-open-${x.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold ${sentIds.includes(x.id) ? "bg-[#8C9B86]/20 text-[#4f5f4a]" : "bg-[#0F172A] text-white hover:bg-[#1e293b]"}`}>
                    {sentIds.includes(x.id) ? <><Check size={13} /> Opened</> : <><MessageCircle size={13} /> Open</>}
                  </button>
                </div>
              ))}
              {waRecipients.length === 0 && <p className="p-4 text-sm text-[#475569]">No guests with a phone number.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppSend({ rsvp, emphasize = false, endpoint = null }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [kind, setKind] = useState("");
  if (!rsvp.phone) return null;
  const KIND_LABEL = { approval: "Approval", decline: "Decline", pending: "Pending" };

  const load = async (k) => {
    setLoading(true);
    try {
      const r = await api.get(endpoint || `/rsvps/${rsvp.id}/whatsapp${k ? `?kind=${k}` : ""}`);
      setPhone(r.data.phone); setText(r.data.text); setKind(r.data.kind); setOpen(true);
    } catch (e) { toast.error("Could not build the WhatsApp message"); } finally { setLoading(false); }
  };
  const digits = phone.replace(/\D/g, "");
  const waUrl = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;

  return (
    <>
      <button type="button" onClick={() => load("")} disabled={loading} data-testid={`wa-send-${rsvp.id}`}
        title={emphasize ? "No email — send the message on WhatsApp" : "Send this message on WhatsApp"}
        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${emphasize ? "bg-[#0F172A] text-white hover:bg-[#1e293b]" : "border border-[#0F172A]/15 text-[#0F172A] hover:border-[#BE923C] hover:text-[#BE923C]"}`}>
        <MessageCircle size={15} /> WhatsApp
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="ltr" className="font-sans max-w-lg" data-testid={`wa-dialog-${rsvp.id}`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Send on WhatsApp</DialogTitle>
            <DialogDescription>Review the number and message, then tap “Open WhatsApp” — it opens a chat with everything pre-filled; you just press Send.</DialogDescription>
          </DialogHeader>
          {!endpoint && <div className="flex flex-wrap gap-2">
            {["approval", "decline", "pending"].map((k) => (
              <button key={k} type="button" onClick={() => load(k)} data-testid={`wa-kind-${k}-${rsvp.id}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${kind === k ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>{KIND_LABEL[k]} message</button>
            ))}
          </div>}
          <div>
            <Label htmlFor={`wa-phone-${rsvp.id}`}>Phone (international, digits only)</Label>
            <Input id={`wa-phone-${rsvp.id}`} data-testid={`wa-phone-${rsvp.id}`} value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="420123456789" />
            <p className="mt-1 text-xs text-[#475569]">Original: {rsvp.phone}</p>
          </div>
          <div>
            <Label htmlFor={`wa-text-${rsvp.id}`}>Message</Label>
            <Textarea id={`wa-text-${rsvp.id}`} data-testid={`wa-text-${rsvp.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={12} className="mt-1.5 text-sm" dir="auto" />
          </div>
          <a href={digits ? waUrl : undefined} target="_blank" rel="noopener noreferrer" data-testid={`wa-open-${rsvp.id}`}
            onClick={() => { if (digits) setOpen(false); }}
            className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-colors ${digits ? "bg-[#0F172A] hover:bg-[#1e293b]" : "bg-[#94a3b8] pointer-events-none"}`}>
            <MessageCircle size={16} /> Open WhatsApp
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
}


const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-[#FBF7EE] text-[#9E7829] border-[#BE923C]/30", icon: Clock },
  approved: { label: "Approved", cls: "bg-[#EEF4EC] text-[#4b7a43] border-[#8C9B86]/40", icon: Check },
  rejected: { label: "Declined", cls: "bg-red-50 text-red-600 border-red-200", icon: Ban },
};

function partsFor(event, ids) {
  const parts = (event && event.parts) || [];
  return (ids || []).map((id) => (parts.find((p) => p.id === id) || {}).label || "").filter(Boolean).join(", ");
}

function downloadRsvpCsv(rows, filename) {
  const questionKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.answers || {}))));
  const base = ["Name", "Email", "Phone", "Event", "Options", "Guests", "Adults", "Children", "Ticket", "Paid", "Amount", "Status", "Submitted"];
  const headers = [...base, ...questionKeys];
  const esc = (v) => {
    const s = v === true ? "Yes" : v === false ? "No" : String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(",")];
  rows.forEach((r) => {
    const a = r.answers || {};
    lines.push([
      r.name, r.email, r.phone || "", r.event_title, r.parts_label || (r.parts || []).join(", "), r.guests, r.adults ?? "", r.children ?? "",
      r.ticket_type || "", r.paid ? "Paid" : "Free", r.paid ? r.amount_czk : "",
      r.status || "pending", (r.created_at || "").slice(0, 10), ...questionKeys.map((k) => a[k]),
    ].map(esc).join(","));
  });
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function PastEventRow({ ev }) {
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("approved");
  const shown = statusFilter === "all" ? ev.rsvps : ev.rsvps.filter((r) => (r.status || "pending") === statusFilter);
  const slug = (s) => (s || "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase().slice(0, 40);
  const exportCsv = () => {
    if (shown.length === 0) { toast.error("Nothing to export for this filter"); return; }
    downloadRsvpCsv(shown.map((r) => ({ ...r, parts_label: partsFor(ev, r.parts) })), `rsvps-${slug(ev.title) || "event"}-${ev.date || "undated"}-${statusFilter}.csv`);
    toast.success(`Exported ${shown.length} RSVP${shown.length === 1 ? "" : "s"}`);
  };
  const pretty = ev.date ? new Date(ev.date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "No date";
  return (
    <div className="rounded-2xl bg-white border border-[#0F172A]/5 overflow-hidden" data-testid={`archive-event-${ev.event_id || "deleted"}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`archive-toggle-${ev.event_id || "deleted"}`}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-[#F9F8F6] transition-colors">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-[#0F172A] truncate">{ev.title}</p>
            {ev.deleted && <span className="rounded-full bg-[#E8E6E1] text-[#475569] px-2.5 py-0.5 text-xs font-semibold">Event deleted</span>}
          </div>
          <p className="mt-1 text-sm text-[#475569]">{pretty}{ev.category ? ` · ${ev.category}` : ""}</p>
        </div>
        <div className="flex items-center gap-5 shrink-0 text-sm">
          <div className="hidden sm:flex items-center gap-4 text-[#475569]">
            <span data-testid={`archive-approved-${ev.event_id}`}><b className="text-[#0F172A]">{ev.approved}</b> approved</span>
            <span><b className="text-[#0F172A]">{ev.approved_guests}</b> guests</span>
            {ev.pending > 0 && <span className="text-[#9E7829]"><b>{ev.pending}</b> pending</span>}
            {ev.paid_total > 0 && <span><b className="text-[#0F172A]">{Math.round(ev.paid_total)}</b> {(ev.currency || "czk").toUpperCase()}</span>}
          </div>
          <span className="sm:hidden text-[#475569]"><b className="text-[#0F172A]">{ev.total}</b> RSVPs</span>
          <ChevronDown size={18} className={`text-[#475569] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-[#0F172A]/5 p-5 bg-[#F9F8F6]/60">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {["approved", "pending", "rejected", "all"].map((k) => (
              <button key={k} type="button" onClick={() => setStatusFilter(k)} data-testid={`archive-filter-${k}-${ev.event_id}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === k ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
                {k} <span className="opacity-70">({k === "all" ? ev.total : ev[k]})</span>
              </button>
            ))}
            <button type="button" onClick={exportCsv} data-testid={`archive-export-${ev.event_id}`}
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-1.5 text-xs font-semibold transition-colors">
              <Download size={13} /> Export {shown.length} (CSV)
            </button>
          </div>
          {shown.length === 0 ? <p className="text-sm text-[#475569] py-4 text-center">No {statusFilter === "all" ? "" : statusFilter} RSVPs.</p> : (
            <div className="overflow-x-auto rounded-xl border border-[#0F172A]/5 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#F4EEE2] text-[#475569] text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-4 py-2.5 font-semibold">Name</th><th className="text-left px-4 py-2.5 font-semibold">Contact</th><th className="text-left px-4 py-2.5 font-semibold">Guests</th><th className="text-left px-4 py-2.5 font-semibold">Status</th><th className="text-left px-4 py-2.5 font-semibold">Payment</th><th className="text-left px-4 py-2.5 font-semibold">Submitted</th></tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const st = r.status || "pending";
                    const badge = STATUS_BADGE[st] || STATUS_BADGE.pending;
                    const answers = Object.entries(r.answers || {}).filter(([, v]) => v !== "" && v != null);
                    return (
                      <tr key={r.id} className="border-t border-[#0F172A]/5 align-top" data-testid={`archive-rsvp-${r.id}`}>
                        <td className="px-4 py-2.5 font-medium text-[#0F172A]">{r.name}
                          {partsFor(ev, r.parts) && <div className="mt-0.5 text-xs text-[#9E7829]">🗓 {partsFor(ev, r.parts)}</div>}
                          {answers.length > 0 && <div className="mt-1 text-xs text-[#475569] font-normal space-y-0.5">{answers.map(([k, v]) => <div key={k}><span className="font-semibold">{k}:</span> {v === true ? "Yes" : v === false ? "No" : String(v)}</div>)}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-[#475569]">{r.email}{r.phone ? <><br />{r.phone}</> : null}</td>
                        <td className="px-4 py-2.5 text-[#475569]">{r.guests}{(r.adults != null || r.children != null) ? ` (${r.adults ?? 0}A / ${r.children ?? 0}C)` : ""}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>{badge.label}</span></td>
                        <td className="px-4 py-2.5 text-[#475569]">{r.paid ? `${r.amount_czk} ${(r.currency || "czk").toUpperCase()}` : "—"}</td>
                        <td className="px-4 py-2.5 text-[#475569] whitespace-nowrap">{euDate(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PastEventsAdmin() {
  const [events, setEvents] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/rsvps/archive").then((r) => setEvents(r.data.events)).catch(() => setEvents([])); }, []);
  const list = (events || []).filter((e) => !q || e.title.toLowerCase().includes(q.toLowerCase()) || (e.date || "").includes(q));
  const totals = (events || []).reduce((a, e) => ({ events: a.events + 1, rsvps: a.rsvps + e.total, guests: a.guests + e.approved_guests }), { events: 0, rsvps: 0, guests: 0 });
  return (
    <div>
      <SectionTitle>Past events</SectionTitle>
      <p className="text-sm text-[#475569] -mt-4 mb-6">Events move here automatically the day after they take place, together with all their RSVPs. Nothing is ever deleted.</p>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="archive-totals">
        {[["Past events", totals.events], ["RSVPs kept", totals.rsvps], ["Approved guests", totals.guests]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#0F172A]/5 p-4"><p className="text-xs uppercase tracking-wide text-[#475569]">{l}</p><p className="mt-1 font-serif text-2xl text-[#0F172A]">{v}</p></div>
        ))}
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by event name or date (YYYY-MM)…" data-testid="archive-search"
        className="w-full mb-4 rounded-full border border-[#0F172A]/15 bg-white px-5 py-2.5 text-sm focus:outline-none focus:border-[#BE923C]" />
      <div className="space-y-3" data-testid="archive-list">
        {events === null && <p className="text-[#475569] py-8 text-center">Loading…</p>}
        {list.map((ev) => <PastEventRow key={ev.event_id || ev.title} ev={ev} />)}
        {events !== null && list.length === 0 && <p className="text-[#475569] py-8 text-center">No past events{q ? " match your search" : " yet"}.</p>}
      </div>
    </div>
  );
}

function RsvpsAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [eventFilter, setEventFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const [evMap, setEvMap] = useState({});
  const [reminderOpen, setReminderOpen] = useState(false);
  const load = () => api.get("/rsvps").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/events?admin=1").then((r) => setEvMap(Object.fromEntries(r.data.map((e) => [e.id, e])))).catch(() => {}); }, []);
  const partLabels = (r) => partsFor(evMap[r.event_id], r.parts);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.put(`/rsvps/${id}/${action}`);
      toast.success(action === "approve" ? "RSVP approved — confirmation email sent" : "RSVP declined — guest notified");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const counts = {
    pending: rows.filter((r) => (r.status || "pending") === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
    all: rows.length,
  };
  // Unique events present in the RSVP list, for the per-event dropdown.
  const eventOptions = Array.from(new Map(rows.map((r) => [r.event_id, r.event_title || "Untitled event"])).entries())
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title));
  const byEvent = eventFilter === "all" ? rows : rows.filter((r) => r.event_id === eventFilter);
  const filtered = filter === "all" ? byEvent : byEvent.filter((r) => (r.status || "pending") === filter);
  const eventTitleFor = (id) => (eventOptions.find((e) => e.id === id) || {}).title || "";

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export for this filter"); return; }
    const slug = (s) => (s || "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase().slice(0, 40);
    const eventPart = eventFilter === "all" ? "all-events" : (slug(eventTitleFor(eventFilter)) || "event");
    downloadRsvpCsv(filtered.map((r) => ({ ...r, parts_label: partLabels(r) })), `rsvps-${eventPart}-${filter}-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${filtered.length} RSVP${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      <SectionTitle>RSVPs</SectionTitle>
      <RsvpFieldsAdmin />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button key={k} data-testid={`rsvp-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === k ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
            {k} <span className="opacity-70">({counts[k] ?? 0})</span>
          </button>
        ))}
        <select data-testid="rsvp-event-filter" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
          className="rounded-full border border-[#0F172A]/15 bg-white px-4 py-2 text-sm font-medium text-[#0F172A] focus:outline-none focus:border-[#BE923C]">
          <option value="all">All events ({rows.length})</option>
          {eventOptions.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.title} ({rows.filter((r) => r.event_id === ev.id).length})</option>
          ))}
        </select>
        <button data-testid="export-rsvps-btn" onClick={exportCsv}
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-2 text-sm font-semibold transition-colors">
          <Download size={15} /> Export {filtered.length} (CSV)
        </button>
      </div>

      {eventFilter !== "all" && (
        <div className="mb-4 flex items-center gap-3" data-testid="rsvp-reminder-bar">
          <button type="button" onClick={() => setReminderOpen(true)} data-testid="rsvp-send-reminder" className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#a87f33] text-white text-sm font-semibold px-5 py-2.5"><Send size={15} /> Send reminder to all guests</button>
          <span className="text-xs text-[#475569]">Email blast or guided WhatsApp, to everyone who RSVP’d for this event.</span>
          <ReminderDialog key={eventFilter} eventId={eventFilter} eventTitle={eventTitleFor(eventFilter)} open={reminderOpen} onOpenChange={setReminderOpen} />
        </div>
      )}
      {eventFilter !== "all" && (evMap[eventFilter]?.parts || []).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="rsvp-part-counts">
          {evMap[eventFilter].parts.map((p) => {
            const rs = byEvent.filter((r) => (r.parts || []).includes(p.id) && r.status === "approved");
            const g = rs.reduce((a, r) => a + (r.guests || 0), 0);
            const held = byEvent.filter((r) => (r.parts || []).includes(p.id) && r.status !== "rejected").reduce((a, r) => a + (r.guests || 0), 0);
            const cap = Number(p.capacity) || 0;
            return <span key={p.id} className={`rounded-full bg-white border px-3 py-1.5 text-xs text-[#0F172A] ${cap && held >= cap ? "border-red-300" : "border-[#BE923C]/30"}`}><b>{p.label}</b> · {rs.length} approved · {g} guests{cap ? ` · ${held}/${cap} taken${held >= cap ? " · FULL" : ""}` : ""}</span>;
          })}
        </div>
      )}
      <div className="space-y-3" data-testid="rsvps-list">
        {filtered.map((r) => {
          const status = r.status || "pending";
          const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
          const BadgeIcon = badge.icon;
          const answers = r.answers || {};
          return (
            <div key={r.id} data-testid={`admin-rsvp-${r.id}`} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#0F172A]">{r.name}</p>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.cls}`}><BadgeIcon size={12} /> {badge.label}</span>
                    {r.paid && <span className="rounded-full bg-[#BE923C]/10 text-[#9E7829] border border-[#BE923C]/30 px-2.5 py-0.5 text-xs font-semibold">Paid {r.amount_czk}{r.currency ? ` ${r.currency.toUpperCase()}` : ""}</span>}
                  </div>
                  <p className="mt-1 text-sm text-[#475569]">{r.email || <span className="text-[#9E7829] font-medium">No email given</span>}{r.phone ? ` · ${r.phone}` : ""}</p>
                  {partLabels(r) && <p className="mt-0.5 text-sm text-[#9E7829] font-medium" data-testid={`rsvp-parts-${r.id}`}>🗓 {partLabels(r)}</p>}
                  <p className="mt-0.5 text-sm text-[#475569]">{r.event_title} · {r.guests} guest{r.guests === 1 ? "" : "s"}{(r.adults != null || r.children != null) ? ` (${r.adults ?? 0} ${r.adults === 1 ? "adult" : "adults"}, ${r.children ?? 0} ${r.children === 1 ? "child" : "children"})` : ""}{r.ticket_type ? ` · ${r.ticket_type}` : ""}</p>
                </div>
                {status === "pending" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <WhatsAppSend rsvp={r} emphasize={!r.email} />
                    <button data-testid={`approve-rsvp-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "approve")}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#8C9B86] hover:bg-[#788a72] text-white text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"><Check size={15} /> Approve</button>
                    <button data-testid={`reject-rsvp-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "reject")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"><Ban size={15} /> Decline</button>
                  </div>
                )}
                {status !== "pending" && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <WhatsAppSend rsvp={r} emphasize={!r.email} />
                    <button data-testid={`reset-rsvp-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, status === "approved" ? "reject" : "approve")}
                      className="text-sm text-[#475569] hover:text-[#0F172A] underline underline-offset-2">
                      {status === "approved" ? "Decline instead" : "Approve instead"}
                    </button>
                  </div>
                )}
              </div>
              {Object.keys(answers).length > 0 && (
                <div className="mt-3 rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 p-3 text-sm text-[#475569] space-y-1">
                  {Object.entries(answers).map(([k, v]) => (
                    <div key={k}><span className="font-semibold text-[#0F172A]">{k}:</span> {v === true ? "Yes" : v === false ? "No" : String(v || "—")}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-[#475569] py-8 text-center">No {filter === "all" ? "" : filter} RSVPs.</p>}
      </div>
    </div>
  );
}

function AnalyticsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics?days=${days}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const fmtDay = (d) => { const x = new Date(d + "T00:00:00"); return x.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };

  const BarList = ({ title, icon: Icon, items, testid }) => (
    <div data-testid={testid} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-2 mb-4"><Icon size={16} className="text-[#BE923C]" /><h3 className="font-semibold text-[#0F172A]">{title}</h3></div>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-[#94A3B8]">No data yet.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => {
            const max = items[0].count || 1;
            const pct = Math.max(6, Math.round((it.count / max) * 100));
            return (
              <div key={it.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-[#0F172A] truncate pr-2">{it.name}</span>
                  <span className="text-[#475569] font-semibold shrink-0">{it.count}</span>
                </div>
                <div className="h-2 rounded-full bg-[#E8E6E1] overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#BE923C] to-[#C4964C]" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="analytics-admin">
      <SectionTitle>Website analytics</SectionTitle>

      <div className="flex items-center gap-2 mb-5">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`analytics-range-${d}`} onClick={() => setDays(d)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${days === d ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
            Last {d} days
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#475569] py-8">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div data-testid="analytics-total-views" className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
              <Eye size={20} className="text-[#BE923C]" />
              <p className="mt-4 font-serif text-4xl">{data?.total_views ?? 0}</p>
              <p className="text-sm text-[#475569]">Page views</p>
            </div>
            <div data-testid="analytics-unique-visitors" className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
              <Users size={20} className="text-[#BE923C]" />
              <p className="mt-4 font-serif text-4xl">{data?.unique_visitors ?? 0}</p>
              <p className="text-sm text-[#475569]">Unique visitors</p>
            </div>
            <div className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
              <Globe size={20} className="text-[#BE923C]" />
              <p className="mt-4 font-serif text-4xl">{data?.countries?.length ?? 0}</p>
              <p className="text-sm text-[#475569]">Countries</p>
            </div>
            <div className="rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
              <TrendingUp size={20} className="text-[#BE923C]" />
              <p className="mt-4 font-serif text-4xl">{data?.total_views && data?.unique_visitors ? (data.total_views / data.unique_visitors).toFixed(1) : "0"}</p>
              <p className="text-sm text-[#475569]">Views / visitor</p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-white border border-[#0F172A]/5 p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-[#BE923C]" /><h3 className="font-semibold text-[#0F172A]">Traffic over time</h3></div>
            <div style={{ width: "100%", height: 260 }} data-testid="analytics-chart">
              <ResponsiveContainer>
                <AreaChart data={data?.series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#BE923C" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#BE923C" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#003063" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#003063" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94A3B8" }} minTickGap={24} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <Tooltip labelFormatter={fmtDay} contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 13 }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#BE923C" strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#003063" strokeWidth={2} fill="url(#gVis)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <BarList title="Top pages" icon={Eye} items={data?.top_pages} testid="analytics-pages" />
            <BarList title="Countries" icon={Globe} items={data?.countries} testid="analytics-countries" />
            <BarList title="Devices" icon={Monitor} items={data?.devices} testid="analytics-devices" />
            <BarList title="Browsers" icon={Monitor} items={data?.browsers} testid="analytics-browsers" />
            <BarList title="Traffic sources" icon={TrendingUp} items={data?.referrers} testid="analytics-referrers" />
          </div>

          <p className="mt-4 text-xs text-[#94A3B8]">Age, gender & interest demographics require Google Analytics — add your Measurement ID in Overview to unlock them in Google's dashboard.</p>
        </>
      )}
    </div>
  );
}


const STUDENT_TEXT_FIELDS = [
  { key: "signup_title", i18n: "student.signup_title", label: "Section eyebrow" },
  { key: "signup_desc", i18n: "student.signup_desc", label: "Section description", multiline: true },
  { key: "signup_btn", i18n: "student.signup_btn", label: "Button label" },
  { key: "form_title", i18n: "student.form_title", label: "Form title" },
  { key: "form_intro", i18n: "student.form_intro", label: "Form intro", multiline: true },
  { key: "success", i18n: "student.success", label: "Success message", multiline: true },
  { key: "name_label", i18n: "student.name", label: "Field: Name" },
  { key: "whatsapp_label", i18n: "student.whatsapp", label: "Field: WhatsApp" },
  { key: "faculty_label", i18n: "student.faculty", label: "Field: Faculty" },
  { key: "year_label", i18n: "student.year", label: "Field: Year" },
  { key: "duration_label", i18n: "student.duration", label: "Field: Duration" },
  { key: "notes_label", i18n: "student.notes", label: "Field: Notes" },
];

const STUDENT_Q_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "select", label: "Single choice (dropdown)" },
  { value: "multiselect", label: "Multiple choice (checkboxes)" },
  { value: "checkbox", label: "Yes / No" },
];

function StudentFormAdmin() {
  const [texts, setTexts] = useState({});
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/student-form").then((r) => {
      const saved = r.data.texts || {};
      // Pre-fill every box with its effective wording (saved override, else the default copy)
      // so admins can edit a word in place — no more "placeholder vanishes on first keystroke".
      const merged = {};
      STUDENT_TEXT_FIELDS.forEach((f) => {
        merged[f.key] = saved[f.key] !== undefined ? saved[f.key] : (STRINGS.en[f.i18n] || "");
      });
      setTexts(merged);
      setQuestions(r.data.questions || []);
    }).catch(() => {});
  }, []);

  const setText = (k, v) => setTexts((t) => ({ ...t, [k]: v }));
  const addQ = () => setQuestions((q) => [...q, { id: crypto.randomUUID(), label: "", type: "text", required: false, options: [] }]);
  const removeQ = (id) => setQuestions((q) => q.filter((x) => x.id !== id));
  const updateQ = (id, patch) => setQuestions((q) => q.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const save = async () => {
    // Validate so no question silently disappears and choice questions always have options
    for (const q of questions) {
      if (!(q.label || "").trim()) {
        toast.error("Please give every question a title, or remove empty ones before saving.");
        return;
      }
      if ((q.type === "select" || q.type === "multiselect") && (!q.options || q.options.length === 0)) {
        toast.error(`Add at least one option for "${(q.label || "").trim()}" (comma-separated).`);
        return;
      }
    }
    // Only store text that DIFFERS from the default so untouched fields keep the trilingual defaults.
    const cleanedTexts = {};
    STUDENT_TEXT_FIELDS.forEach((f) => {
      const v = String(texts[f.key] ?? "").trim();
      const def = String(STRINGS.en[f.i18n] || "").trim();
      if (v && v !== def) cleanedTexts[f.key] = v;
    });
    const cleanedQs = questions.map((q) => ({ ...q, label: (q.label || "").trim() }));
    setSaving(true);
    try {
      await api.put("/student-form", { texts: cleanedTexts, questions: cleanedQs });
      setQuestions(cleanedQs);
      toast.success("Student form saved — changes are live on the Students Club page");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const hasOptions = (t) => t === "select" || t === "multiselect";

  return (
    <div data-testid="student-form-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 overflow-hidden">
      <button data-testid="toggle-student-form" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F9F8F6] transition-colors">
        <span className="flex items-center gap-2 font-semibold text-[#0F172A]"><ListChecks size={18} className="text-[#BE923C]" /> Edit the sign-up form</span>
        <span className="text-sm text-[#475569]">{questions.length} extra question{questions.length === 1 ? "" : "s"} · texts & questions</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[#0F172A]/5 pt-4 space-y-6">
          <div>
            <p className="text-sm font-semibold text-[#0F172A] mb-1">Form text</p>
            <p className="text-xs text-[#94A3B8] mb-3">Edit any wording below — your changes show in all languages. Clear a box to restore the default.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STUDENT_TEXT_FIELDS.map((f) => (
                <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                  <Label className="text-xs">{f.label}</Label>
                  {f.multiline ? (
                    <Textarea data-testid={`student-text-${f.key}`} rows={2} value={texts[f.key] || ""}
                      placeholder={STRINGS.en[f.i18n] || ""} onChange={(e) => setText(f.key, e.target.value)} className="mt-1 border-[#0F172A]/10" />
                  ) : (
                    <Input data-testid={`student-text-${f.key}`} value={texts[f.key] || ""}
                      placeholder={STRINGS.en[f.i18n] || ""} onChange={(e) => setText(f.key, e.target.value)} className="mt-1 border-[#0F172A]/10" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-[#0F172A] mb-3">Extra questions</p>
            <div className="space-y-3">
              {questions.length === 0 && <p className="text-sm text-[#475569]">No extra questions yet. Add one below.</p>}
              {questions.map((q) => (
                <div key={q.id} data-testid={`student-q-row-${q.id}`} className="rounded-xl border border-[#0F172A]/10 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">Question</Label>
                      <Input data-testid={`student-q-label-${q.id}`} value={q.label} placeholder="e.g. What are you most looking forward to?"
                        onChange={(e) => updateQ(q.id, { label: e.target.value })} className="mt-1 border-[#0F172A]/10" />
                    </div>
                    <div className="w-52">
                      <Label className="text-xs">Type</Label>
                      <select data-testid={`student-q-type-${q.id}`} value={q.type} onChange={(e) => updateQ(q.id, { type: e.target.value })}
                        className="mt-1 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                        {STUDENT_Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <button onClick={() => removeQ(q.id)} data-testid={`remove-student-q-${q.id}`} className="mt-6 p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                  </div>
                  {hasOptions(q.type) && (
                    <div>
                      <Label className="text-xs">Options (comma-separated)</Label>
                      <Input data-testid={`student-q-options-${q.id}`} value={(q.options || []).join(", ")}
                        placeholder="Shabbat meals, Dinners through the week, Outings & nights out, A listening ear"
                        onChange={(e) => updateQ(q.id, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })}
                        className="mt-1 border-[#0F172A]/10" />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer">
                    <input type="checkbox" data-testid={`student-q-required-${q.id}`} checked={q.required} onChange={(e) => updateQ(q.id, { required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" />
                    Required
                  </label>
                </div>
              ))}
            </div>
            <button onClick={addQ} data-testid="add-student-q" className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-4 py-2 text-sm font-semibold hover:border-[#BE923C]"><Plus size={15} /> Add question</button>
          </div>

          <button onClick={save} disabled={saving} data-testid="save-student-form" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1e293b] disabled:opacity-60">{saving ? "Saving…" : "Save form"}</button>
        </div>
      )}
    </div>
  );
}

function StudentCardsAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/student-cards").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/student-card-settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const act = async (id, action) => {
    setBusy(id);
    try { await api.put(`/student-cards/${id}/${action}`); toast.success(action === "approve" ? "Card approved — sent to the student" : "Card revoked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(null); }
  };
  const del = async (id) => { if (!window.confirm("Delete this request?")) return; await api.delete(`/student-cards/${id}`); load(); };
  const saveSettings = async () => { try { const r = await api.put("/student-card-settings", settings); setSettings(r.data); toast.success("Saved"); } catch (e) { toast.error("Save failed"); } };
  const counts = rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const list = rows.filter((r) => (filter === "all" || r.status === filter) && (!q || `${r.name} ${r.phone} ${r.email} ${r.university} ${r.faculty} ${r.code}`.toLowerCase().includes(q.toLowerCase())));
  const exportCsv = () => {
    const esc = (v) => { const t = String(v ?? ""); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const lines = [["Code", "Name", "Phone", "Email", "University", "Faculty", "Year", "Status", "Requested", "Card link"].join(",")];
    list.forEach((r) => lines.push([r.code, r.name, r.phone, r.email, r.university, r.faculty, r.year, r.status, euDate(r.created_at), `${window.location.origin}/card/${r.code}`].map(esc).join(",")));
    const url = URL.createObjectURL(new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `student-cards-${filter}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const badge = { pending: "bg-[#FBF3E2] text-[#9E7829]", approved: "bg-[#8C9B86]/15 text-[#4f5f4a]", revoked: "bg-red-50 text-red-600" };
  return (
    <div className="mt-14" data-testid="student-cards-admin">
      <SectionTitle>Student discount cards</SectionTitle>
      <p className="text-sm text-[#475569] -mt-4 mb-5">Requests from <code className="bg-[#E8E6E1] px-1.5 py-0.5 rounded">/students/card</code>. Approving emails the student their digital card (with photo + QR); revoking makes the card page show “Not valid”.</p>
      {settings && (
        <div className="rounded-2xl bg-white border border-[#0F172A]/5 p-4 mb-6 space-y-3" data-testid="student-card-settings">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#0F172A]">Discount text on the card</p>
            <label className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer"><input type="checkbox" checked={settings.active !== false} onChange={(e) => setSettings({ ...settings, active: e.target.checked })} className="accent-[#BE923C]" data-testid="student-card-active" /> Sign-up open</label>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[["en", "English"], ["cs", "Czech"], ["he", "Hebrew"], ["ru", "Russian"]].map(([lg, n]) => (
              <Input key={lg} dir={lg === "he" ? "rtl" : "ltr"} data-testid={`student-card-text-${lg}`} value={settings.discount_text?.[lg] || ""} placeholder={`${n} text`} onChange={(e) => setSettings({ ...settings, discount_text: { ...settings.discount_text, [lg]: e.target.value } })} className="border-[#0F172A]/10 text-sm" />
            ))}
          </div>
          <button type="button" onClick={saveSettings} data-testid="student-card-settings-save" className="rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2 hover:bg-[#1e293b]">Save text</button>
          <div className="pt-3 border-t border-[#0F172A]/5" data-testid="student-card-poster">
            <p className="text-sm font-semibold text-[#0F172A]">Printable A5 poster (QR → sign-up page)</p>
            <p className="text-xs text-[#475569] mt-0.5 mb-2">Print-ready PDF for the shop and campus notice boards. Uses the discount text above — save it first.</p>
            <div className="flex flex-wrap gap-2">
              {[["en", "English"], ["cs", "Czech"], ["he", "Hebrew"], ["ru", "Russian"]].map(([lg, n]) => (
                <a key={lg} href={`${API}/student-card-poster?lang=${lg}`} target="_blank" rel="noreferrer" data-testid={`student-card-poster-${lg}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-3.5 py-1.5 text-xs font-semibold"><Download size={13} /> {n} PDF</a>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["pending", "approved", "revoked", "all"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`student-card-filter-${k}`} className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === k ? "bg-[#0F172A] text-white" : "bg-white text-[#475569] hover:bg-[#E8E6E1]"}`}>{k} <span className="opacity-70">({k === "all" ? rows.length : counts[k] || 0})</span></button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="student-card-search" className="ms-auto rounded-full border border-[#0F172A]/15 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#BE923C]" />
        <button onClick={exportCsv} data-testid="student-card-export" className="inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-2 text-sm font-semibold"><Download size={15} /> Export {list.length} (CSV)</button>
      </div>
      <div className="space-y-3" data-testid="student-cards-list">
        {list.map((r) => (
          <div key={r.id} data-testid={`student-card-row-${r.id}`} className="rounded-2xl bg-white border border-[#0F172A]/5 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            {r.photo_url ? <img src={resolveImg(r.photo_url)} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" /> : <div className="w-14 h-14 rounded-xl bg-[#E8E6E1] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-[#0F172A]">{r.name}</p><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${badge[r.status] || ""}`}>{r.status}</span></div>
              <p className="mt-0.5 text-sm text-[#475569]">{[r.university, r.faculty, r.year].filter(Boolean).join(" · ")}</p>
              <p className="text-sm text-[#475569]">{r.email || <span className="text-[#9E7829]">No email</span>} · {r.phone} · {euDate(r.created_at)}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <WhatsAppSend rsvp={{ id: r.id, phone: r.phone, email: r.email }} emphasize={!r.email} endpoint={`/student-cards/${r.id}/whatsapp`} />
              <a href={`/card/${r.code}`} target="_blank" rel="noreferrer" data-testid={`student-card-open-${r.id}`} className="rounded-full border border-[#0F172A]/15 px-3.5 py-2 text-sm font-semibold hover:border-[#BE923C]">Card</a>
              {r.status !== "approved" && <button disabled={busy === r.id} onClick={() => act(r.id, "approve")} data-testid={`student-card-approve-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#8C9B86] hover:bg-[#788a72] text-white text-sm font-semibold px-4 py-2 disabled:opacity-60"><Check size={15} /> Approve</button>}
              {r.status === "approved" && <button disabled={busy === r.id} onClick={() => act(r.id, "revoke")} data-testid={`student-card-revoke-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 disabled:opacity-60"><Ban size={15} /> Revoke</button>}
              <button onClick={() => del(r.id)} data-testid={`student-card-delete-${r.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-[#475569] py-6 text-center">No {filter === "all" ? "" : filter} requests.</p>}
      </div>
    </div>
  );
}

function StudentsAdmin() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/student-signups").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this sign-up? This cannot be undone.")) return;
    setBusy(id);
    try {
      await api.delete(`/student-signups/${id}`);
      toast.success("Sign-up deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const exportCsv = () => {
    if (rows.length === 0) { toast.error("No sign-ups to export"); return; }
    const qKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.answers || {}))));
    const headers = ["Name", "WhatsApp", "Faculty / Uni", "Year", "Duration in Prague", "Notes", ...qKeys, "Submitted"];
    const esc = (v) => {
      const s = Array.isArray(v) ? v.join("; ") : v === true ? "Yes" : v === false ? "No" : String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(",")];
    rows.forEach((r) => {
      const a = r.answers || {};
      lines.push([r.name, r.whatsapp, r.faculty, r.year, r.duration_prague, r.notes, ...qKeys.map((k) => a[k]), (r.created_at || "").slice(0, 10)].map(esc).join(","));
    });
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `students-club-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} sign-up${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      <SectionTitle>Students Club sign-ups</SectionTitle>
      <StudentFormAdmin />
      <div className="flex items-center gap-2 mb-4">
        <p className="text-sm text-[#475569]">{rows.length} total</p>
        <button data-testid="export-students-btn" onClick={exportCsv}
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-2 text-sm font-semibold transition-colors">
          <Download size={15} /> Export (CSV)
        </button>
      </div>

      <div className="space-y-3" data-testid="students-list">
        {rows.map((r) => (
          <div key={r.id} data-testid={`admin-student-${r.id}`} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="font-semibold text-[#0F172A]">{r.name}</p>
                <p className="mt-1 text-sm text-[#475569]">
                  <a href={`https://wa.me/${(r.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-[#9E7829] hover:underline">{r.whatsapp}</a>
                </p>
                <p className="mt-0.5 text-sm text-[#475569]">
                  {[r.faculty, r.year, r.duration_prague].filter(Boolean).join(" · ") || "—"}
                </p>
                {r.notes && <p className="mt-2 text-sm text-[#475569] italic">"{r.notes}"</p>}
                {Object.keys(r.answers || {}).length > 0 && (
                  <div className="mt-2 rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 p-3 text-sm text-[#475569] space-y-1">
                    {Object.entries(r.answers).map(([k, v]) => (
                      <div key={k}><span className="font-semibold text-[#0F172A]">{k}:</span> {Array.isArray(v) ? v.join(", ") : v === true ? "Yes" : v === false ? "No" : String(v || "—")}</div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-[#94A3B8]">{euDate(r.created_at)}</p>
              </div>
              <button data-testid={`delete-student-${r.id}`} disabled={busy === r.id} onClick={() => del(r.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60">
                <Trash2 size={15} /> Delete
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569] py-8 text-center">No sign-ups yet.</p>}
      </div>
    </div>
  );
}

const SERVICE_LANGS = [["en", "English"], ["cs", "Čeština"], ["he", "עברית"], ["ru", "Русский"]];

function ServicesOrderAdmin() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/settings"), api.get("/services-order")]).then(([c, s, o]) => {
      const svc = (c.data || []).filter((x) => x.is_service && x.active !== false)
        .map((x) => ({ key: x.id, title: x.title, image: x.image_url }));
      const shul = s.data.synagogue;
      const list = [...svc];
      if (shul) list.push({ key: "synagogue", title: (shul.title || "Our Community Shul") + " (Synagogue)", image: shul.image_url });
      const order = o.data.order || [];
      const idx = (k) => { const i = order.indexOf(k); return i === -1 ? 1e6 : i; };
      list.sort((a, b) => idx(a.key) - idx(b.key));
      setItems(list);
    }).catch(() => {});
  }, []);

  const move = async (i, dir) => {
    const next = [...items];
    const t = i + dir;
    if (t < 0 || t >= next.length) return;
    [next[i], next[t]] = [next[t], next[i]];
    setItems(next);
    setSaving(true);
    try { await api.put("/services-order", { order: next.map((x) => x.key) }); toast.success("Services order saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="services-order-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <LayoutGrid size={18} className="text-[#BE923C]" />
        <h3 className="font-semibold text-[#0F172A]">Services slider order</h3>
      </div>
      <p className="text-sm text-[#475569] mb-4">Drag order with the arrows — this is the exact order services appear in the homepage slider and on the /services page (the Synagogue card is included).</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.key} data-testid={`service-order-${it.key}`} className="flex items-center gap-3 rounded-2xl border border-[#0F172A]/8 bg-[#F9F8F6] p-3">
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0 || saving} data-testid={`service-order-up-${it.key}`} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1 || saving} data-testid={`service-order-down-${it.key}`} className="p-1 text-[#475569] disabled:opacity-30 hover:text-[#0F172A]"><ArrowDown size={15} /></button>
            </div>
            {it.image ? <img src={resolveImg(it.image)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-[#0F172A]/5 shrink-0" />}
            <span className="text-sm font-semibold text-[#0F172A]">{i + 1}. {it.title}</span>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#475569]">No community services yet — add one in Programs (tick “community service”).</p>}
      </div>
    </div>
  );
}



function IntroEditor({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {SERVICE_LANGS.map(([code, label]) => (
        <div key={code}>
          <Label className="text-xs">{label}</Label>
          <Textarea data-testid={`${testid}-${code}`} rows={4} value={value[code] || ""} dir={code === "he" ? "rtl" : "ltr"}
            onChange={(e) => onChange({ ...value, [code]: e.target.value })} className="mt-1 border-[#0F172A]/10" />
        </div>
      ))}
    </div>
  );
}

function ServicePagesAdmin() {
  const [intro, setIntro] = useState({ en: "", he: "", ru: "" });
  const [restaurants, setRestaurants] = useState([]);
  const [kPhone, setKPhone] = useState("");
  const [kEmail, setKEmail] = useState("");
  const [savingK, setSavingK] = useState(false);
  const [tIntro, setTIntro] = useState({ en: "", he: "", ru: "" });
  const [tUrl, setTUrl] = useState("");
  const [tButton, setTButton] = useState({ en: "", he: "", ru: "" });
  const [savingT, setSavingT] = useState(false);
  const [kHeader, setKHeader] = useState("");
  const [tHeader, setTHeader] = useState("");

  useEffect(() => {
    api.get("/kosher-establishments").then((r) => {
      setIntro({ en: "", he: "", ru: "", ...(r.data.intro || {}) });
      setRestaurants((r.data.restaurants || []).map((x) => ({ ...x, uid: x.id || Math.random().toString(36).slice(2) })));
      setKPhone(r.data.phone || "");
      setKEmail(r.data.email || "");
      setKHeader(r.data.header_image || "");
    }).catch(() => {});
    api.get("/tourist-desk").then((r) => {
      setTIntro({ en: "", he: "", ru: "", ...(r.data.intro || {}) });
      setTUrl(r.data.url || "");
      setTButton({ en: "", he: "", ru: "", ...(r.data.button || {}) });
      setTHeader(r.data.header_image || "");
    }).catch(() => {});
  }, []);

  const setR = (uid, patch) => setRestaurants((arr) => arr.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const addR = () => setRestaurants((arr) => [...arr, { uid: Math.random().toString(36).slice(2), id: "", name: "", tag: "", description: "", address: "", hours: "", image_url: "" }]);
  const removeR = (uid) => setRestaurants((arr) => arr.filter((r) => r.uid !== uid));

  const saveKosher = async () => {
    setSavingK(true);
    try {
      const payload = restaurants
        .filter((r) => (r.name || "").trim())
        .map(({ uid, ...rest }) => ({ ...rest, id: rest.id || (rest.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || uid }));
      await api.put("/kosher-establishments", { intro, restaurants: payload, phone: kPhone.trim(), email: kEmail.trim(), header_image: kHeader });
      toast.success("Kosher Establishments page saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSavingK(false); }
  };

  const saveTourist = async () => {
    setSavingT(true);
    try {
      await api.put("/tourist-desk", { intro: tIntro, url: tUrl.trim(), button: tButton, header_image: tHeader });
      toast.success("Tourist Desk page saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSavingT(false); }
  };

  return (
    <div>
      <SectionTitle>Service Pages</SectionTitle>

      <ServicesOrderAdmin />

      {/* Kosher Establishments */}
      <div data-testid="kosher-estab-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5 sm:p-6 space-y-5">
        <div className="flex items-center gap-2">
          <UtensilsCrossed size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Kosher Establishments</h3>
        </div>
        <p className="text-sm text-[#475569] -mt-2">Shows at <code className="bg-[#F1EEE8] px-1.5 py-0.5 rounded">/kosher-establishments</code>. Add this as a community service (Programs → tick “community service”, link = /kosher-establishments) so it appears in the Services slider.</p>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Header background image</p>
          <ImageUploadField testid="kosher-estab-header" value={kHeader} onChange={setKHeader} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Intro paragraph</p>
          <IntroEditor value={intro} onChange={setIntro} testid="kosher-intro" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Reservations phone (shown at top)</Label>
            <Input data-testid="kosher-phone" value={kPhone} onChange={(e) => setKPhone(e.target.value)} placeholder="+420 …" className="mt-1 border-[#0F172A]/10" />
          </div>
          <div>
            <Label className="text-xs">Group bookings email (shown at top)</Label>
            <Input data-testid="kosher-email" value={kEmail} onChange={(e) => setKEmail(e.target.value)} placeholder="groups@example.com" className="mt-1 border-[#0F172A]/10" />
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-2">Restaurants</p>
          <div className="space-y-3">
            {restaurants.map((r, i) => (
              <div key={r.uid} data-testid={`kosher-rest-${i}`} className="rounded-2xl border border-[#0F172A]/10 bg-[#F9F8F6] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-[#0F172A]">Restaurant {i + 1}</span>
                  <button type="button" data-testid={`kosher-rest-remove-${i}`} onClick={() => removeR(r.uid)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                </div>
                <div className="grid sm:grid-cols-[160px_1fr] gap-4">
                  <div><Label className="text-xs">Photo</Label><div className="mt-1.5"><ImageUploadField testid={`kosher-rest-image-${i}`} value={r.image_url} onChange={(v) => setR(r.uid, { image_url: v })} /></div></div>
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label className="text-xs">Name</Label><Input data-testid={`kosher-rest-name-${i}`} value={r.name} onChange={(e) => setR(r.uid, { name: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" /></div>
                      <div><Label className="text-xs">Tag (e.g. Meat / Dairy / Pizza)</Label><Input data-testid={`kosher-rest-tag-${i}`} value={r.tag} onChange={(e) => setR(r.uid, { tag: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" /></div>
                    </div>
                    <div><Label className="text-xs">Description</Label><Textarea rows={2} data-testid={`kosher-rest-desc-${i}`} value={r.description} onChange={(e) => setR(r.uid, { description: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" /></div>
                    <div><Label className="text-xs">Address</Label><Input data-testid={`kosher-rest-address-${i}`} value={r.address} onChange={(e) => setR(r.uid, { address: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" /></div>
                    <div><Label className="text-xs">Opening hours</Label><Textarea rows={2} data-testid={`kosher-rest-hours-${i}`} value={r.hours} onChange={(e) => setR(r.uid, { hours: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" placeholder="e.g. Sun–Thu 12:00–22:00, Fri 12:00–15:00" /></div>
                    <div><Label className="text-xs">Map link (Google Maps URL)</Label><Input data-testid={`kosher-rest-map-${i}`} value={r.map_url || ""} onChange={(e) => setR(r.uid, { map_url: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" placeholder="https://maps.google.com/…" /></div>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" data-testid="kosher-rest-add" onClick={addR} className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-4 py-2 text-sm font-semibold hover:border-[#BE923C] transition-colors"><Plus size={15} /> Add restaurant</button>
          </div>
        </div>
        <button onClick={saveKosher} disabled={savingK} data-testid="save-kosher-estab" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1e293b] disabled:opacity-60">{savingK ? "Saving…" : "Save Kosher Establishments"}</button>
      </div>

      {/* Tourist Desk */}
      <div data-testid="tourist-desk-admin" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5 sm:p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Compass size={18} className="text-[#BE923C]" />
          <h3 className="font-semibold text-[#0F172A]">Tourist Desk</h3>
        </div>
        <p className="text-sm text-[#475569] -mt-2">Shows at <code className="bg-[#F1EEE8] px-1.5 py-0.5 rounded">/tourist-desk</code>. Add this as a community service (link = /tourist-desk) to show it in the Services slider.</p>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Header background image</p>
          <ImageUploadField testid="tourist-desk-header" value={tHeader} onChange={setTHeader} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Intro paragraph</p>
          <IntroEditor value={tIntro} onChange={setTIntro} testid="tourist-intro" />
        </div>
        <div>
          <Label className="text-xs">Tourist website URL</Label>
          <Input data-testid="tourist-url" value={tUrl} onChange={(e) => setTUrl(e.target.value)} placeholder="https://…" className="mt-1 border-[#0F172A]/10" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Button label</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SERVICE_LANGS.map(([code, label]) => (
              <div key={code}>
                <Label className="text-xs">{label}</Label>
                <Input data-testid={`tourist-button-${code}`} value={tButton[code] || ""} dir={code === "he" ? "rtl" : "ltr"} onChange={(e) => setTButton({ ...tButton, [code]: e.target.value })} className="mt-1 border-[#0F172A]/10" />
              </div>
            ))}
          </div>
        </div>
        <button onClick={saveTourist} disabled={savingT} data-testid="save-tourist-desk" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1e293b] disabled:opacity-60">{savingT ? "Saving…" : "Save Tourist Desk"}</button>
      </div>
    </div>
  );
}


const CHILD_TEXT_FIELDS = [
  { key: "signup_title", label: "Section title", i18n: "child.signup_title" },
  { key: "signup_desc", label: "Section description", i18n: "child.signup_desc", multiline: true },
  { key: "signup_btn", label: "Button label", i18n: "child.signup_btn" },
  { key: "form_title", label: "Form title", i18n: "child.form_title" },
  { key: "form_intro", label: "Form intro", i18n: "child.form_intro", multiline: true },
];

const CHILD_PROG_LABEL = { sunday: "Sunday Hebrew School", thursday: "Thursday After School", both: "Both programs" };
const CHILD_JEWISH_LABEL = { birth: "Jewish from birth", conversion: "Jewish by conversion", na: "Not Jewish / N/A" };

function ChildProgramAdmin() {
  const [cfg, setCfg] = useState({ payment_mode: "none", price_sunday: 0, price_thursday: 0, price_both: 0, currency: "czk", age_groups: [], russian_group_enabled: true, russian_group_name: "", school_year_start: "10-01" });
  const [note, setNote] = useState({ en: "", he: "", ru: "" });
  const [thursdayEnabled, setThursdayEnabled] = useState(true);
  const [thursdayNote, setThursdayNote] = useState({ en: "", he: "", ru: "" });
  const [sibling, setSibling] = useState({ enabled: true, type: "percent", value: 20 });
  const [jewishEnabled, setJewishEnabled] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [qLang, setQLang] = useState("en");
  const [texts, setTexts] = useState({});
  const [textLang, setTextLang] = useState("en");
  const [savingCfg, setSavingCfg] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const loadRows = () => api.get("/child-registrations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/child-program").then((r) => {
      setCfg((c) => ({ ...c, ...r.data, payment_mode: r.data.payment_mode || (r.data.payment_enabled ? "required" : "none") }));
      setNote({ en: "", he: "", ru: "", ...(r.data.suggested_note || {}) });
      setThursdayEnabled(r.data.thursday_enabled !== false);
      setThursdayNote({ en: "", he: "", ru: "", ...(r.data.thursday_note || {}) });
      setSibling({
        enabled: r.data.sibling_enabled !== false,
        type: r.data.sibling_type || "percent",
        value: r.data.sibling_value ?? 20,
      });
      setJewishEnabled(r.data.jewish_questions_enabled !== false);
      setQuestions(Array.isArray(r.data.questions) ? r.data.questions : []);
      const saved = r.data.texts || {};
      const merged = {};
      CHILD_TEXT_FIELDS.forEach((f) => {
        const s = saved[f.key];
        const obj = { en: "", he: "", ru: "" };
        if (s && typeof s === "object") { obj.en = s.en || ""; obj.he = s.he || ""; obj.ru = s.ru || ""; }
        else if (typeof s === "string") { obj.en = s; }
        ["en", "cs", "he", "ru"].forEach((lg) => { if (!obj[lg]) obj[lg] = STRINGS[lg]?.[f.i18n] || STRINGS.en[f.i18n] || ""; });
        merged[f.key] = obj;
      });
      setTexts(merged);
    }).catch(() => {});
    loadRows();
  }, []);

  const setText = (k, lg, v) => setTexts((t) => ({ ...t, [k]: { ...(t[k] || {}), [lg]: v } }));
  const curLabel = (cfg.currency || "czk").toUpperCase();

  const genQId = () => "q_" + Math.random().toString(36).slice(2, 9);
  const addQuestion = () => setQuestions((qs) => [...qs, { id: genQId(), scope: "child", type: "multiselect", required: false, allow_other: false, label: { en: "", he: "", ru: "" }, options: [] }]);
  const updateQuestion = (id, patch) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  const removeQuestion = (id) => setQuestions((qs) => qs.filter((q) => q.id !== id));
  const setQLabel = (id, lg, v) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, label: { ...(q.label || {}), [lg]: v } } : q)));
  const addOption = (id) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, options: [...(q.options || []), { id: genQId(), label: { en: "", he: "", ru: "" } }] } : q)));
  const removeOption = (id, oid) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, options: (q.options || []).filter((o) => o.id !== oid) } : q)));
  const setOptLabel = (id, oid, lg, v) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, options: (q.options || []).map((o) => (o.id === oid ? { ...o, label: { ...(o.label || {}), [lg]: v } } : o)) } : q)));

  const saveCfg = async () => {
    const cleaned = {};
    CHILD_TEXT_FIELDS.forEach((f) => {
      const obj = texts[f.key] || {};
      const out = {};
      ["en", "cs", "he", "ru"].forEach((lg) => {
        const v = String(obj[lg] ?? "").trim();
        const def = String(STRINGS[lg]?.[f.i18n] || STRINGS.en[f.i18n] || "").trim();
        if (v && v !== def) out[lg] = v;
      });
      if (Object.keys(out).length) cleaned[f.key] = out;
    });
    setSavingCfg(true);
    try {
      await api.put("/child-program", {
        payment_mode: cfg.payment_mode,
        price_sunday: Number(cfg.price_sunday) || 0,
        price_thursday: Number(cfg.price_thursday) || 0,
        price_both: Number(cfg.price_both) || 0,
        photo_sunday: cfg.photo_sunday || "",
        photo_thursday: cfg.photo_thursday || "",
        photo_both: cfg.photo_both || "",
        suggested_note: note,
        texts: cleaned,
        thursday_enabled: thursdayEnabled,
        thursday_note: thursdayNote,
        sibling_enabled: sibling.enabled,
        sibling_type: sibling.type,
        sibling_value: Number(sibling.value) || 0,
        jewish_questions_enabled: jewishEnabled,
        installments_enabled: !!cfg.installments_enabled,
        installments_count: Math.max(2, Number(cfg.installments_count) || 3),
        age_groups: (cfg.age_groups || []).map((g) => ({ id: g.id || ("g_" + Math.random().toString(36).slice(2, 7)), name: (g.name || "").trim() || "Group", min_age: Number(g.min_age) || 0, max_age: Number(g.max_age) || 0 })),
        russian_group_enabled: cfg.russian_group_enabled !== false,
        russian_group_name: (cfg.russian_group_name || "").trim(),
        school_year_start: cfg.school_year_start || "10-01",
        questions: (questions || []).filter((q) => (q.label?.en || q.label?.he || q.label?.ru || "").trim()).map((q) => ({
          ...q,
          options: ["dropdown", "multiselect"].includes(q.type) ? (q.options || []).filter((o) => (o.label?.en || o.label?.he || o.label?.ru || "").trim()) : [],
        })),
      });
      toast.success("Children's Program settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSavingCfg(false); }
  };

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.put(`/child-registrations/${id}/${action}`);
      toast.success(action === "approve" ? "Registration approved — parent notified" : "Registration declined — parent notified");
      await loadRows();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this registration? This cannot be undone.")) return;
    setBusy(id);
    try { await api.delete(`/child-registrations/${id}`); toast.success("Registration deleted"); await loadRows(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(null); }
  };

  const cancelPlan = async (id) => {
    if (!window.confirm("Stop future installment charges for this registration? Payments already made stay as they are.")) return;
    setBusy(id);
    try { await api.put(`/child-registrations/${id}/cancel-plan`); toast.success("Installment plan cancelled — no further charges"); await loadRows(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(null); }
  };

  const counts = {
    pending: rows.filter((r) => (r.status || "pending") === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
    all: rows.length,
  };
  const filtered = filter === "all" ? rows : rows.filter((r) => (r.status || "pending") === filter);

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export for this filter"); return; }
    const headers = ["Parent", "Email", "Phone", "Second phone", "Address", "Photo consent", "Child", "Program", "Date of birth", "Gender", "School/Grade", "Class group", "Allergies", "Paid", "Amount", "Status", "Submitted"];
    const esc = (v) => { const s = v === true ? "Yes" : v === false ? "No" : String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.map(esc).join(",")];
    filtered.forEach((r) => {
      (r.children || []).forEach((c) => {
        lines.push([
          r.parent_name, r.parent_email, r.parent_phone || "", r.parent_phone2 || "", r.parent_address || "", r.photo_consent ? "Yes" : "No",
          c.name || "", CHILD_PROG_LABEL[c.program] || c.program || "", c.dob || "", c.gender || "", c.grade || "", c.age_group || "", c.allergies || "",
          r.paid ? "Paid" : "Free", r.paid ? r.amount : "", r.status || "pending", (r.created_at || "").slice(0, 10),
        ].map(esc).join(","));
      });
    });
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `children-program-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} registration${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      <SectionTitle>Children's Program</SectionTitle>

      {/* Settings */}
      <div data-testid="child-program-settings" className="rounded-2xl bg-white border border-[#0F172A]/5 mb-6 p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-[#0F172A]">Registration & payment</p>
            <p className="text-sm text-[#475569] mt-0.5">Prices are per child, per program. Choose how payment works below.</p>
          </div>
          <div>
            <Label className="text-xs">Payment mode</Label>
            <select data-testid="child-payment-mode" value={cfg.payment_mode}
              onChange={(e) => setCfg((c) => ({ ...c, payment_mode: e.target.value }))}
              className="mt-1 w-full sm:w-64 rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
              <option value="none">No payment — free registration</option>
              <option value="required">Required — parents must pay to register</option>
              <option value="suggested">Suggested participation — parents can change or skip</option>
            </select>
          </div>
        </div>
        {cfg.payment_mode !== "none" && (
          <div className="grid sm:grid-cols-3 gap-4">
            {[["price_sunday", "Sunday Hebrew School"], ["price_thursday", "Thursday After School"], ["price_both", "Both programs"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs">{cfg.payment_mode === "suggested" ? `${lbl} (suggested)` : lbl} ({curLabel})</Label>
                <Input type="number" min={0} step="1" data-testid={`child-${k}`} value={cfg[k]}
                  onChange={(e) => setCfg((c) => ({ ...c, [k]: e.target.value }))} className="mt-1 border-[#0F172A]/10" />
              </div>
            ))}
          </div>
        )}
        {cfg.payment_mode === "suggested" && (
          <div data-testid="child-suggested-note-editor">
            <p className="text-sm font-semibold text-[#0F172A] mb-1">Suggested participation note</p>
            <p className="text-xs text-[#94A3B8] mb-3">Shown above the contribution box on the form. Leave a language blank to use the default.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[["en", "English"], ["cs", "Čeština"], ["he", "עברית"], ["ru", "Русский"]].map(([code, label]) => (
                <div key={code}>
                  <Label className="text-xs">{label}</Label>
                  <Textarea data-testid={`child-suggested-note-${code}`} rows={3} value={note[code] || ""} dir={code === "he" ? "rtl" : "ltr"}
                    placeholder={STRINGS.en["child.suggested_note_default"]}
                    onChange={(e) => setNote((n) => ({ ...n, [code]: e.target.value }))} className="mt-1 border-[#0F172A]/10" />
                </div>
              ))}
            </div>
          </div>
        )}
        {cfg.payment_mode === "required" && (
          <div data-testid="child-installments-editor" className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-4">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" data-testid="child-installments-enabled" checked={!!cfg.installments_enabled}
                onChange={(e) => setCfg((c) => ({ ...c, installments_enabled: e.target.checked }))} className="w-4 h-4 accent-[#BE923C]" />
              <span className="text-sm font-semibold text-[#0F172A]">Allow paying in monthly installments</span>
            </label>
            <p className="text-xs text-[#94A3B8] mt-1">When on, parents can split the fee into equal monthly card payments. The 1st is charged at registration; the rest are charged automatically each month. Uses your Stripe account.</p>
            {cfg.installments_enabled && (
              <div className="mt-3 flex items-center gap-2">
                <Label className="text-xs">Number of monthly payments</Label>
                <Input type="number" min={2} max={12} data-testid="child-installments-count" value={cfg.installments_count ?? 3}
                  onChange={(e) => setCfg((c) => ({ ...c, installments_count: Math.min(12, Math.max(2, Number(e.target.value) || 3)) }))} className="max-w-[90px] border-[#0F172A]/10 bg-white" />
              </div>
            )}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Program photos</p>
          <p className="text-xs text-[#94A3B8] mb-3">These images show on the registration form's program picker. Leave blank to keep the default photo.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[["photo_sunday", "Sunday Hebrew School"], ["photo_thursday", "Thursday After School"], ["photo_both", "Both programs"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs">{lbl}</Label>
                <div className="mt-1.5">
                  <ImageUploadField testid={`child-${k}`} value={cfg[k]} onChange={(v) => setCfg((c) => ({ ...c, [k]: v }))} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div data-testid="child-groups-editor" className="rounded-xl border border-[#003063]/20 bg-[#003063]/[0.04] p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">Class groups (auto-allocation)</p>
            <p className="text-xs text-[#94A3B8] mt-1">Children are placed into a group automatically from their date of birth (age as of the school-year start). Russian-speaking children who opt in go to the Russian group regardless of age.</p>
          </div>
          <div>
            <Label className="text-xs">School year starts on</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input type="number" min={1} max={12} data-testid="child-school-year-month"
                value={parseInt((cfg.school_year_start || "10-01").split("-")[0], 10) || 10}
                onChange={(e) => { const m = Math.min(12, Math.max(1, Number(e.target.value) || 10)); const d = (cfg.school_year_start || "10-01").split("-")[1] || "01"; setCfg((c) => ({ ...c, school_year_start: `${String(m).padStart(2, "0")}-${d}` })); }}
                className="w-20 border-[#0F172A]/10 bg-white" />
              <span className="text-xs text-[#475569]">/</span>
              <Input type="number" min={1} max={31} data-testid="child-school-year-day"
                value={parseInt((cfg.school_year_start || "10-01").split("-")[1], 10) || 1}
                onChange={(e) => { const d = Math.min(31, Math.max(1, Number(e.target.value) || 1)); const m = (cfg.school_year_start || "10-01").split("-")[0] || "10"; setCfg((c) => ({ ...c, school_year_start: `${m}-${String(d).padStart(2, "0")}` })); }}
                className="w-20 border-[#0F172A]/10 bg-white" />
              <span className="text-xs text-[#94A3B8]">(month / day)</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Age groups</Label>
            {(cfg.age_groups || []).map((g, gi) => (
              <div key={g.id || gi} data-testid={`child-age-group-${gi}`} className="flex items-center gap-2">
                <Input data-testid={`child-age-group-name-${gi}`} placeholder="Group name" value={g.name || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, age_groups: c.age_groups.map((x, xi) => (xi === gi ? { ...x, name: e.target.value } : x)) }))}
                  className="flex-1 border-[#0F172A]/10 bg-white" />
                <Input type="number" min={0} max={99} data-testid={`child-age-group-min-${gi}`} placeholder="Min" value={g.min_age ?? ""}
                  onChange={(e) => setCfg((c) => ({ ...c, age_groups: c.age_groups.map((x, xi) => (xi === gi ? { ...x, min_age: Number(e.target.value) } : x)) }))}
                  className="w-20 border-[#0F172A]/10 bg-white" />
                <span className="text-xs text-[#475569]">to</span>
                <Input type="number" min={0} max={99} data-testid={`child-age-group-max-${gi}`} placeholder="Max" value={g.max_age ?? ""}
                  onChange={(e) => setCfg((c) => ({ ...c, age_groups: c.age_groups.map((x, xi) => (xi === gi ? { ...x, max_age: Number(e.target.value) } : x)) }))}
                  className="w-20 border-[#0F172A]/10 bg-white" />
                <button type="button" data-testid={`child-age-group-remove-${gi}`} onClick={() => setCfg((c) => ({ ...c, age_groups: c.age_groups.filter((_, xi) => xi !== gi) }))} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
              </div>
            ))}
            <button type="button" data-testid="child-age-group-add" onClick={() => setCfg((c) => ({ ...c, age_groups: [...(c.age_groups || []), { id: "g_" + Math.random().toString(36).slice(2, 7), name: "", min_age: 0, max_age: 0 }] }))}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#003063]/25 px-3 py-1.5 text-xs font-semibold text-[#003063] hover:bg-[#003063]/10 transition-colors"><Plus size={13} /> Add group</button>
          </div>
          <div className="rounded-lg border border-[#BE923C]/25 bg-white p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="child-russian-group-enabled" checked={cfg.russian_group_enabled !== false}
                onChange={(e) => setCfg((c) => ({ ...c, russian_group_enabled: e.target.checked }))} className="w-4 h-4 accent-[#BE923C]" />
              <span className="text-sm font-semibold text-[#0F172A]">Russian-speaking group</span>
            </label>
            <p className="text-xs text-[#94A3B8] mt-1">When a child speaks Russian (from the "languages" question), the form asks if they'd like the Russian-speaking group. If yes, they're placed here regardless of age.</p>
            {cfg.russian_group_enabled !== false && (
              <div className="mt-2">
                <Label className="text-xs">Group name</Label>
                <Input data-testid="child-russian-group-name" placeholder="Group 4 · Russian speakers" value={cfg.russian_group_name || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, russian_group_name: e.target.value }))} className="mt-1 border-[#0F172A]/10 bg-white" />
              </div>
            )}
          </div>
        </div>
        <div data-testid="child-thursday-editor" className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="child-thursday-enabled" checked={thursdayEnabled}
              onChange={(e) => setThursdayEnabled(e.target.checked)} className="w-4 h-4 accent-[#BE923C]" />
            <span className="text-sm font-semibold text-[#0F172A]">Show "Thursday After-School on request" callout</span>
          </label>
          <p className="text-xs text-[#94A3B8] mt-1">A gold card with a WhatsApp button shown on the Children's Program page and inside the sign-up form. Uses the site's main WhatsApp number. Leave a language blank to use the default.</p>
          {thursdayEnabled && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[["en", "English"], ["cs", "Čeština"], ["he", "עברית"], ["ru", "Русский"]].map(([code, label]) => (
                <div key={code}>
                  <Label className="text-xs">{label}</Label>
                  <Textarea data-testid={`child-thursday-note-${code}`} rows={3} value={thursdayNote[code] || ""} dir={code === "he" ? "rtl" : "ltr"}
                    placeholder={STRINGS[code]?.["child.thursday_note"] || STRINGS.en["child.thursday_note"]}
                    onChange={(e) => setThursdayNote((n) => ({ ...n, [code]: e.target.value }))} className="mt-1 border-[#0F172A]/10" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div data-testid="child-sibling-editor" className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="child-sibling-enabled" checked={sibling.enabled}
              onChange={(e) => setSibling((s) => ({ ...s, enabled: e.target.checked }))} className="w-4 h-4 accent-[#BE923C]" />
            <span className="text-sm font-semibold text-[#0F172A]">Sibling discount (Sunday School)</span>
          </label>
          <p className="text-xs text-[#94A3B8] mt-1">Applies automatically when a family registers more than one child for Sunday School. The first child pays the full Sunday price; each additional sibling is discounted. Only takes effect when a Sunday price and "payment required" are set.</p>
          {sibling.enabled && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Discount type</Label>
                <select data-testid="child-sibling-type" value={sibling.type}
                  onChange={(e) => setSibling((s) => ({ ...s, type: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                  <option value="percent">Percentage off each additional child</option>
                  <option value="fixed_price">Fixed price for each additional child</option>
                  <option value="flat_off">Flat amount off each additional child</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">
                  {sibling.type === "percent" ? "Percent off (e.g. 20 = 20%)"
                    : sibling.type === "fixed_price" ? "Flat price per sibling"
                    : "Amount off per sibling"}
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input data-testid="child-sibling-value" type="number" min={0} step="1" value={sibling.value}
                    onChange={(e) => setSibling((s) => ({ ...s, value: e.target.value }))} className="border-[#0F172A]/10" />
                  <span className="text-sm text-[#475569] shrink-0">{sibling.type === "percent" ? "%" : (cfg.currency || "czk").toUpperCase()}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div data-testid="child-jewish-editor" className="rounded-xl border border-[#BE923C]/25 bg-[#FBF7EE]/60 p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="child-jewish-enabled" checked={jewishEnabled}
              onChange={(e) => setJewishEnabled(e.target.checked)} className="w-4 h-4 accent-[#BE923C]" />
            <span className="text-sm font-semibold text-[#0F172A]">Show Jewish-status & conversion-document questions</span>
          </label>
          <p className="text-xs text-[#94A3B8] mt-1">When on, the sign-up form asks each parent whether they are Jewish from birth / by conversion / not Jewish, with a required document upload when "by conversion" is chosen. If the mother selects "by conversion", each child is also asked and must upload their document if they choose "by conversion".</p>
        </div>
        <div>
          <div className="flex items-center justify-between gap-3 mb-1">
            <p className="text-sm font-semibold text-[#0F172A]">Form text</p>
            <div className="flex items-center gap-1 shrink-0">
              {["en", "cs", "he", "ru"].map((lg) => (
                <button key={lg} type="button" data-testid={`child-text-lang-${lg}`} onClick={() => setTextLang(lg)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${textLang === lg ? "bg-[#0F172A] text-white" : "bg-white text-[#64748B] border border-[#0F172A]/10 hover:bg-[#0F172A]/5"}`}>
                  {{ en: "EN", cs: "CS", he: "עב", ru: "RU" }[lg]}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mb-3">Edit the wording for the selected language. Clear a box to restore the default.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CHILD_TEXT_FIELDS.map((f) => {
              const val = texts[f.key]?.[textLang] ?? "";
              const ph = STRINGS[textLang]?.[f.i18n] || STRINGS.en[f.i18n] || "";
              const rtl = textLang === "he";
              return (
                <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                  <Label className="text-xs">{f.label}</Label>
                  {f.multiline ? (
                    <Textarea data-testid={`child-text-${f.key}`} rows={2} value={val} dir={rtl ? "rtl" : "ltr"}
                      placeholder={ph} onChange={(e) => setText(f.key, textLang, e.target.value)} className="mt-1 border-[#0F172A]/10" />
                  ) : (
                    <Input data-testid={`child-text-${f.key}`} value={val} dir={rtl ? "rtl" : "ltr"}
                      placeholder={ph} onChange={(e) => setText(f.key, textLang, e.target.value)} className="mt-1 border-[#0F172A]/10" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom questions builder */}
        <div className="rounded-xl border border-[#0F172A]/8 bg-[#F9F8F6] p-4" data-testid="child-questions-builder">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Custom questions</p>
              <p className="text-xs text-[#94A3B8] mt-0.5">Add your own questions to the registration form. Choose whether each is asked once per family or once per child.</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {["en", "cs", "he", "ru"].map((lg) => (
                <button key={lg} type="button" data-testid={`child-q-lang-${lg}`} onClick={() => setQLang(lg)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${qLang === lg ? "bg-[#0F172A] text-white" : "bg-white text-[#475569] border border-[#0F172A]/10"}`}>
                  {{ en: "EN", cs: "CS", he: "עב", ru: "RU" }[lg]}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 space-y-4">
            {questions.map((q, qi) => (
              <div key={q.id} data-testid={`child-question-${qi}`} className="rounded-xl border border-[#0F172A]/10 bg-white p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Question ({qLang.toUpperCase()})</Label>
                    <Input data-testid={`child-question-label-${qi}`} dir={qLang === "he" ? "rtl" : "ltr"} value={q.label?.[qLang] || ""}
                      onChange={(e) => setQLabel(q.id, qLang, e.target.value)} placeholder="e.g. Which languages does the child speak?" className="mt-1 border-[#0F172A]/10" />
                  </div>
                  <button type="button" data-testid={`child-question-remove-${qi}`} onClick={() => removeQuestion(q.id)} className="mt-6 p-2 rounded-lg text-[#B4573B] hover:bg-[#B4573B]/10"><Trash2 size={16} /></button>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div>
                    <Label className="text-xs">Ask</Label>
                    <select data-testid={`child-question-scope-${qi}`} value={q.scope || "child"} onChange={(e) => updateQuestion(q.id, { scope: e.target.value })}
                      className="mt-1 block rounded-lg border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                      <option value="child">Once per child</option>
                      <option value="family">Once per family</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select data-testid={`child-question-type-${qi}`} value={q.type} onChange={(e) => updateQuestion(q.id, { type: e.target.value })}
                      className="mt-1 block rounded-lg border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
                      <option value="multiselect">Multiple choice (pick several)</option>
                      <option value="dropdown">Single choice (pick one)</option>
                      <option value="text">Short text</option>
                      <option value="yesno">Yes / No</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer mt-6">
                    <input type="checkbox" data-testid={`child-question-required-${qi}`} checked={!!q.required} onChange={(e) => updateQuestion(q.id, { required: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" /> Required
                  </label>
                  {["dropdown", "multiselect"].includes(q.type) && (
                    <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer mt-6">
                      <input type="checkbox" data-testid={`child-question-other-${qi}`} checked={!!q.allow_other} onChange={(e) => updateQuestion(q.id, { allow_other: e.target.checked })} className="w-4 h-4 accent-[#BE923C]" /> Add “Other” (fill-in)
                    </label>
                  )}
                </div>
                {["dropdown", "multiselect"].includes(q.type) && (
                  <div className="space-y-2 pt-1">
                    <Label className="text-xs">Options ({qLang.toUpperCase()})</Label>
                    {(q.options || []).map((o, oi) => (
                      <div key={o.id} className="flex items-center gap-2" data-testid={`child-question-${qi}-option-${oi}`}>
                        <Input dir={qLang === "he" ? "rtl" : "ltr"} data-testid={`child-question-${qi}-option-input-${oi}`} value={o.label?.[qLang] || ""}
                          onChange={(e) => setOptLabel(q.id, o.id, qLang, e.target.value)} placeholder="Option label" className="max-w-xs border-[#0F172A]/10" />
                        <button type="button" data-testid={`child-question-${qi}-option-remove-${oi}`} onClick={() => removeOption(q.id, o.id)} className="p-2 rounded-lg text-[#B4573B] hover:bg-[#B4573B]/10"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <button type="button" data-testid={`child-question-${qi}-add-option`} onClick={() => addOption(q.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 px-3 py-1.5 text-xs font-semibold hover:border-[#BE923C]"><Plus size={13} /> Add option</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button type="button" data-testid="child-add-question" onClick={addQuestion}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 bg-white px-4 py-2 text-sm font-semibold hover:border-[#BE923C]"><Plus size={15} /> Add question</button>
          <p className="text-[11px] text-[#94A3B8] mt-2">Tip: enter the wording in each language using the EN / עב / RU toggle. Empty questions/options are ignored on save.</p>
        </div>

        <button onClick={saveCfg} disabled={savingCfg} data-testid="save-child-program"
          className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1e293b] disabled:opacity-60">
          {savingCfg ? "Saving…" : "Save settings"}
        </button>
      </div>

      {/* Registrations */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button key={k} data-testid={`child-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === k ? "bg-[#0F172A] text-white" : "bg-[#E8E6E1] text-[#475569] hover:bg-[#dcdad4]"}`}>
            {k} <span className="opacity-70">({counts[k] ?? 0})</span>
          </button>
        ))}
        <button data-testid="export-children-btn" onClick={exportCsv}
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-2 text-sm font-semibold transition-colors">
          <Download size={15} /> Export {filter === "all" ? "all" : filter} (CSV)
        </button>
      </div>

      <div className="space-y-3" data-testid="children-list">
        {filtered.map((r) => {
          const status = r.status || "pending";
          const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
          const BadgeIcon = badge.icon;
          return (
            <div key={r.id} data-testid={`admin-child-${r.id}`} className="rounded-2xl bg-white border border-[#0F172A]/5 p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#0F172A]">{r.parent_name}</p>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.cls}`}><BadgeIcon size={12} /> {badge.label}</span>
                    {r.paid && <span className="rounded-full bg-[#BE923C]/10 text-[#9E7829] border border-[#BE923C]/30 px-2.5 py-0.5 text-xs font-semibold">Paid {r.amount}{r.currency ? ` ${r.currency.toUpperCase()}` : ""}</span>}
                  </div>
                  <p className="mt-1 text-sm text-[#475569]">{r.parent_email}{r.parent_phone ? ` · ${r.parent_phone}` : ""}{r.parent_phone2 ? ` · ${r.parent_phone2}` : ""}</p>
                  {r.parent_address && <p className="mt-0.5 text-sm text-[#475569]">{r.parent_address}</p>}
                  {(r.father_name || r.mother_name) && (
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {[["Father", r.father_name, r.father_phone, r.father_jewish, r.father_conversion_doc],
                        ["Mother", r.mother_name, r.mother_phone, r.mother_jewish, r.mother_conversion_doc]].map(([role, nm, ph, jw, doc]) => (
                        <div key={role} className="rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 p-3 text-sm text-[#475569]">
                          <p className="font-semibold text-[#0F172A]">{role}: {nm || "—"}</p>
                          <p className="text-xs">{ph || "—"}{jw ? ` · ${CHILD_JEWISH_LABEL[jw] || jw}` : ""}</p>
                          {jw === "conversion" && doc && (
                            <a href={resolveImg(doc)} target="_blank" rel="noopener noreferrer" data-testid={`child-parent-doc-${r.id}-${role.toLowerCase()}`}
                              className="mt-1 inline-flex items-center gap-1 text-xs text-[#BE923C] underline"><FileText size={12} /> Conversion document</a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {(r.children || []).map((c, i) => (
                      <div key={i} className="rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 p-3 text-sm text-[#475569] flex gap-3">
                        {c.photo_url && <img src={resolveImg(c.photo_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-semibold text-[#0F172A]">{c.name} <span className="font-normal">· {CHILD_PROG_LABEL[c.program] || c.program}</span></p>
                          <p className="text-xs">{[euDate(c.dob), c.gender, c.grade].filter(Boolean).join(" · ") || "—"}</p>
                          {c.age_group && <p className="mt-1 inline-block rounded-full bg-[#0F172A] px-2 py-0.5 text-[10px] font-bold text-[#E9C77E]" data-testid={`admin-child-group-${r.id}-${i}`}>{c.age_group}</p>}
                          {c.jewish && <p className="text-xs mt-0.5">{CHILD_JEWISH_LABEL[c.jewish] || c.jewish}</p>}
                          {c.jewish === "conversion" && c.conversion_doc && (
                            <a href={resolveImg(c.conversion_doc)} target="_blank" rel="noopener noreferrer" data-testid={`child-doc-link-${r.id}-${i}`}
                              className="inline-flex items-center gap-1 text-xs text-[#BE923C] underline"><FileText size={12} /> Conversion document</a>
                          )}
                          {c.allergies && <p className="text-xs italic mt-0.5">{c.allergies}</p>}
                          {(c.answers || []).map((a, ai) => (a.a ? (
                            <p key={ai} className="text-xs mt-0.5"><span className="text-[#94A3B8]">{a.q}:</span> {a.a}</p>
                          ) : null))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(r.answers || []).length > 0 && (
                    <div className="mt-2 rounded-xl bg-[#F9F8F6] border border-[#0F172A]/5 p-3" data-testid={`child-family-answers-${r.id}`}>
                      {r.answers.map((a, ai) => (
                        <p key={ai} className="text-xs text-[#475569]"><span className="text-[#94A3B8]">{a.q}:</span> {a.a}</p>
                      ))}
                    </div>
                  )}
                  {r.is_installment && (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`child-installment-${r.id}`}>
                      <span className="rounded-full bg-[#BE923C]/10 text-[#9E7829] border border-[#BE923C]/30 px-2.5 py-0.5 text-xs font-semibold">
                        Installments · {r.installments_paid || 0}/{r.installments_count || 0} paid
                      </span>
                      {r.plan_cancelled ? (
                        <span className="rounded-full bg-red-50 text-red-600 border border-red-200 px-2.5 py-0.5 text-xs font-semibold">Plan cancelled</span>
                      ) : (
                        <button data-testid={`cancel-plan-${r.id}`} disabled={busy === r.id} onClick={() => cancelPlan(r.id)}
                          className="rounded-full border border-red-200 text-red-600 hover:bg-red-50 px-3 py-0.5 text-xs font-semibold disabled:opacity-50">
                          Cancel plan
                        </button>
                      )}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#94A3B8]">{euDate(r.created_at)}</p>
                  <span data-testid={`child-consent-${r.id}`} className={`mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${r.photo_consent ? "bg-[#8C9B86]/15 text-[#5f6f59]" : "bg-[#0F172A]/5 text-[#94A3B8]"}`}>
                    <Camera size={12} /> {r.photo_consent ? "Photo consent granted" : "No photo consent"}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {status === "pending" && (
                    <div className="flex items-center gap-2">
                      <button data-testid={`approve-child-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "approve")}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#8C9B86] hover:bg-[#788a72] text-white text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"><Check size={15} /> Approve</button>
                      <button data-testid={`reject-child-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "reject")}
                        className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"><Ban size={15} /> Decline</button>
                    </div>
                  )}
                  {status !== "pending" && (
                    <button data-testid={`reset-child-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, status === "approved" ? "reject" : "approve")}
                      className="text-sm text-[#475569] hover:text-[#0F172A] underline underline-offset-2">
                      {status === "approved" ? "Decline instead" : "Approve instead"}
                    </button>
                  )}
                  <button data-testid={`delete-child-${r.id}`} disabled={busy === r.id} onClick={() => del(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60">
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-[#475569] py-8 text-center">No registrations yet.</p>}
      </div>
    </div>
  );
}

const SITETEXT_GROUPS = {
  nav: "Navigation menu",
  hero: "Hero (top of homepage)",
  programs: "Programs section",
  holiday: "Upcoming holiday box",
  stats: "Numbers section",
  events: "Events section",
  donate: "Donate section",
  gallery: "Gallery section",
  contact: "Contact section",
  weekly: "Weekly classes strip",
  footer: "Footer",
  eventspage: "Events page",
  eventdetail: "Event detail page",
  rsvp: "RSVP form",
  program: "Program pages",
  wa: "WhatsApp join button",
  lang: "Language banner",
};

function SiteTextAdmin() {
  const langCfg = [
    { code: "en", label: "English", rtl: false },
    { code: "cs", label: "Čeština · Czech", rtl: false },
    { code: "he", label: "עברית · Hebrew", rtl: true },
    { code: "ru", label: "Русский · Russian", rtl: false },
  ];
  const def = (code, key) => (STRINGS[code] && STRINGS[code][key]) || STRINGS.en[key] || "";
  const allKeys = Object.keys(STRINGS.en);

  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    api.get("/site-text").then((r) => {
      const ov = r.data || {};
      const v = { en: {}, cs: {}, he: {}, ru: {} };
      allKeys.forEach((k) => {
        ["en", "cs", "he", "ru"].forEach((c) => {
          v[c][k] = (ov[c] && ov[c][k] != null) ? ov[c][k] : def(c, k);
        });
      });
      setValues(v);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setVal = (code, key, val) =>
    setValues((prev) => ({ ...prev, [code]: { ...prev[code], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const overrides = { en: {}, cs: {}, he: {}, ru: {} };
      allKeys.forEach((k) => {
        ["en", "cs", "he", "ru"].forEach((c) => {
          const val = values[c][k];
          if (val != null && val !== def(c, k)) overrides[c][k] = val;
        });
      });
      await api.put("/site-text", overrides);
      const count = Object.values(overrides).reduce((n, o) => n + Object.keys(o).length, 0);
      toast.success(count ? `Saved ${count} custom text${count === 1 ? "" : "s"} — reload the site to see changes` : "Reset to default site text");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!values) return <div><SectionTitle>Site Text</SectionTitle><p className="text-[#475569]">Loading…</p></div>;

  const q = query.trim().toLowerCase();
  const groups = {};
  allKeys.forEach((k) => {
    const g = k.split(".")[0];
    const match = !q || k.toLowerCase().includes(q) ||
      ["en", "cs", "he", "ru"].some((c) => (values[c][k] || "").toLowerCase().includes(q));
    if (!match) return;
    (groups[g] = groups[g] || []).push(k);
  });
  const groupKeys = Object.keys(groups).sort((a, b) =>
    (SITETEXT_GROUPS[a] || a).localeCompare(SITETEXT_GROUPS[b] || b));

  return (
    <div data-testid="sitetext-admin">
      <SectionTitle action={
        <button onClick={save} disabled={saving} data-testid="save-sitetext-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-5 py-2.5 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save all changes"}
        </button>
      }>Site Text</SectionTitle>

      <p className="text-sm text-[#475569] mb-4">Edit any fixed text on the site — menu, hero, section titles, buttons, footer — in all four languages (English, Czech, Hebrew, Russian). Leave a field on its default and it stays default. Changes go live after a page reload.</p>

      <Input data-testid="sitetext-search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Search text (e.g. Donate, hero, Contact)…" className="mb-4 border-[#0F172A]/10" />

      <div className="space-y-3">
        {groupKeys.map((g) => {
          const isOpen = openGroups[g] ?? (!!q);
          return (
            <div key={g} data-testid={`sitetext-group-${g}`} className="rounded-2xl bg-white border border-[#0F172A]/5 overflow-hidden">
              <button onClick={() => setOpenGroups((p) => ({ ...p, [g]: !isOpen }))}
                data-testid={`sitetext-toggle-${g}`}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-[#F9F8F6] transition-colors">
                <span className="font-semibold text-[#0F172A]">{SITETEXT_GROUPS[g] || g}</span>
                <span className="text-xs text-[#475569]">{groups[g].length} item{groups[g].length === 1 ? "" : "s"}</span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-[#0F172A]/5 space-y-4">
                  {groups[g].map((k) => (
                    <div key={k} className="pt-3">
                      <p className="text-xs font-mono text-[#94a3b8] mb-1.5">{k}</p>
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
                        {langCfg.map((L) => (
                          <div key={L.code}>
                            <Label className="text-[11px] text-[#475569]">{L.label}</Label>
                            <Textarea data-testid={`sitetext-${L.code}-${k}`} rows={1} dir={L.rtl ? "rtl" : "ltr"}
                              value={values[L.code][k] ?? ""} onChange={(e) => setVal(L.code, k, e.target.value)}
                              className={`mt-1 border-[#0F172A]/10 resize-y min-h-[42px] text-sm ${L.rtl ? "text-right" : ""}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {groupKeys.length === 0 && <p className="text-[#475569] py-6 text-center">No text matches "{query}".</p>}
      </div>

      <div className="mt-6">
        <button onClick={save} disabled={saving} data-testid="save-sitetext-btn-bottom"
          className="rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white font-semibold px-6 py-3 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
}

function SimpleList({ endpoint, title, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <div className="rounded-2xl bg-white border border-[#0F172A]/5 overflow-hidden">
        <table className="w-full text-sm" data-testid={`table-${testid}`}>
          <thead className="bg-[#E8E6E1]/60 text-left">
            <tr>{columns.map(([, l]) => <th key={l} className="px-5 py-3 font-semibold">{l}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-[#0F172A]/5">
                {columns.map(([k]) => <td key={k} className="px-5 py-3 text-[#475569]">{String(row[k] ?? "—")}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="px-5 py-8 text-center text-[#475569]">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Messages() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/contact").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.put(`/contact/${id}/read`); load(); };
  return (
    <div>
      <SectionTitle>Messages</SectionTitle>
      <div className="space-y-3">
        {rows.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className={`rounded-2xl border p-5 ${m.read ? "bg-white border-[#0F172A]/5" : "bg-[#BE923C]/5 border-[#BE923C]/20"}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold">{m.name} <span className="text-sm font-normal text-[#475569]">· {m.email}</span></p>
                {m.subject && <p className="text-sm text-[#8C9B86] font-medium">{m.subject}</p>}
              </div>
              {!m.read && <button onClick={() => markRead(m.id)} data-testid={`mark-read-${m.id}`} className="text-xs rounded-full bg-[#0F172A] text-white px-3 py-1.5">Mark read</button>}
            </div>
            <p className="mt-3 text-sm text-[#475569] leading-relaxed">{m.message}</p>
          </div>
        ))}
        {rows.length === 0 && <p className="text-[#475569]">No messages yet.</p>}
      </div>
    </div>
  );
}

function GalleryAdmin() {
  const [rows, setRows] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [captionCs, setCaptionCs] = useState("");
  const [captionHe, setCaptionHe] = useState("");
  const [captionRu, setCaptionRu] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);
  const load = () => api.get("/gallery").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!url) return;
    const i18n = {};
    if (captionCs) i18n.cs = { caption: captionCs };
    if (captionHe) i18n.he = { caption: captionHe };
    if (captionRu) i18n.ru = { caption: captionRu };
    await api.post("/gallery", { image_url: url, caption, category, i18n });
    setUrl(""); setCaption(""); setCaptionCs(""); setCaptionHe(""); setCaptionRu(""); setCategory(""); toast.success("Image added"); load();
  };
  const remove = async (id) => { await api.delete(`/gallery/${id}`); load(); };

  const [editImg, setEditImg] = useState(null);
  const [eForm, setEForm] = useState({ caption: "", cs: "", he: "", ru: "", categories: [] });
  const openEdit = (img) => {
    const list = (img.categories && img.categories.length) ? img.categories : (img.category ? [img.category] : []);
    setEForm({ caption: img.caption || "", cs: img.i18n?.cs?.caption || "", he: img.i18n?.he?.caption || "", ru: img.i18n?.ru?.caption || "", categories: list });
    setEditImg(img);
  };
  const toggleCat = (title) => setEForm((f) => {
    const set = new Set(f.categories || []);
    if (set.has(title)) set.delete(title); else set.add(title);
    return { ...f, categories: Array.from(set) };
  });
  const saveEdit = async () => {
    const i18n = {};
    if (eForm.cs) i18n.cs = { caption: eForm.cs };
    if (eForm.he) i18n.he = { caption: eForm.he };
    if (eForm.ru) i18n.ru = { caption: eForm.ru };
    await api.put(`/gallery/${editImg.id}`, { image_url: editImg.image_url, caption: eForm.caption, category: eForm.categories[0] || "", categories: eForm.categories, i18n });
    toast.success("Photo updated"); setEditImg(null); load();
  };

  return (
    <div>
      <SectionTitle>Gallery</SectionTitle>
      <form onSubmit={add} className="space-y-3 mb-6 rounded-2xl bg-white border border-[#0F172A]/5 p-4">
        <ImageUploadField testid="gallery-image" value={url} onChange={setUrl} />
        <div className="flex flex-col sm:flex-row gap-3">
          <Input data-testid="gallery-caption" placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="border-[#0F172A]/10" />
          <Input data-testid="gallery-caption-cs" placeholder="Popisek (česky, nepovinné)" value={captionCs} onChange={(e) => setCaptionCs(e.target.value)} className="border-[#0F172A]/10" />
          <Input data-testid="gallery-caption-he" dir="rtl" placeholder="כיתוב בעברית (רשות)" value={captionHe} onChange={(e) => setCaptionHe(e.target.value)} className="border-[#0F172A]/10 text-right" />
          <Input data-testid="gallery-caption-ru" placeholder="Подпись (рус., необяз.)" value={captionRu} onChange={(e) => setCaptionRu(e.target.value)} className="border-[#0F172A]/10" />
          <select data-testid="gallery-category" value={category} onChange={(e) => setCategory(e.target.value)}
            className="rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm sm:max-w-[240px]">
            <option value="">General (no program)</option>
            {cats.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
          </select>
          <button type="submit" data-testid="gallery-add-btn" className="rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-6 py-2.5 transition-colors whitespace-nowrap">Add Photo</button>
        </div>
      </form>
      <p className="text-sm text-[#475569] mb-3">Tip: click any photo to change the program it's tagged with (or edit its caption).</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {rows.map((img) => (
          <div key={img.id} data-testid={`gallery-item-${img.id}`} className="relative group rounded-2xl overflow-hidden">
            <button type="button" onClick={() => openEdit(img)} data-testid={`gallery-edit-${img.id}`} className="block w-full text-left">
              <img src={resolveImg(img.image_url)} alt={img.caption} className="w-full h-40 object-cover" />
              <span className="absolute inset-0 bg-[#0F172A]/0 group-hover:bg-[#0F172A]/30 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 rounded-full bg-white/95 text-[#0F172A] text-xs font-semibold px-3 py-1.5"><Pencil size={13} /> Edit</span>
              </span>
            </button>
            <span data-testid={`gallery-item-cat-${img.id}`} className="absolute bottom-2 left-2 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-[#0F172A] pointer-events-none max-w-[90%] truncate">{(img.categories && img.categories.length ? img.categories.join(", ") : (img.category || "General"))}</span>
            <button onClick={() => remove(img.id)} data-testid={`gallery-delete-${img.id}`} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editImg} onOpenChange={(o) => !o && setEditImg(null)}>
        <DialogContent dir="ltr" className="font-sans sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit photo</DialogTitle>
            <DialogDescription>Change the program this photo is tagged with, or update its caption.</DialogDescription>
          </DialogHeader>
          {editImg && (
            <div className="space-y-4">
              <img src={resolveImg(editImg.image_url)} alt="" className="w-full h-48 object-cover rounded-xl" />
              <div>
                <Label>Programs (tick all that apply)</Label>
                <div data-testid="gallery-edit-categories" className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl border border-[#0F172A]/10 p-3 max-h-44 overflow-y-auto">
                  {cats.map((c) => (
                    <label key={c.id} data-testid={`gallery-edit-cat-${c.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={(eForm.categories || []).includes(c.title)} onChange={() => toggleCat(c.title)} className="w-4 h-4 accent-[#BE923C]" />
                      <span className="truncate">{c.title}</span>
                    </label>
                  ))}
                </div>
                {(eForm.categories || []).length === 0 && <p className="mt-1 text-xs text-[#94a3b8]">No program selected — shows as “General”.</p>}
              </div>
              <div className="grid gap-2">
                <Label>Captions (optional)</Label>
                <Input data-testid="gallery-edit-caption" placeholder="Caption (English)" value={eForm.caption} onChange={(e) => setEForm({ ...eForm, caption: e.target.value })} className="border-[#0F172A]/10" />
                <Input data-testid="gallery-edit-caption-cs" placeholder="Popisek (česky)" value={eForm.cs} onChange={(e) => setEForm({ ...eForm, cs: e.target.value })} className="border-[#0F172A]/10" />
                <Input data-testid="gallery-edit-caption-he" dir="rtl" placeholder="כיתוב בעברית" value={eForm.he} onChange={(e) => setEForm({ ...eForm, he: e.target.value })} className="border-[#0F172A]/10 text-right" />
                <Input data-testid="gallery-edit-caption-ru" placeholder="Подпись (рус.)" value={eForm.ru} onChange={(e) => setEForm({ ...eForm, ru: e.target.value })} className="border-[#0F172A]/10" />
              </div>
              <button type="button" onClick={saveEdit} data-testid="gallery-edit-save" className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3">Save changes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
